import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { downloadHtmlAsDocx } from '../../utils/docxExport';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ═══════════════════════════════════════════════════════════════════════
// Shared UI kit — same palette/components as EventFilings.js, kept local
// to this file so Annual Filing doesn't depend on Event Filing internals.
// ═══════════════════════════════════════════════════════════════════════
const ACCENTS = {
  navy:    { bg: 'bg-blue-50',    strong: 'bg-blue-100',    text: 'text-blue-700',    textLight: 'text-blue-600',    border: 'border-l-blue-400' },
  purple:  { bg: 'bg-purple-50',  strong: 'bg-purple-100',  text: 'text-purple-700',  textLight: 'text-purple-600',  border: 'border-l-purple-400' },
  cyan:    { bg: 'bg-cyan-50',    strong: 'bg-cyan-100',    text: 'text-cyan-700',    textLight: 'text-cyan-600',    border: 'border-l-cyan-400' },
  emerald: { bg: 'bg-emerald-50', strong: 'bg-emerald-100', text: 'text-emerald-700', textLight: 'text-emerald-600', border: 'border-l-emerald-400' },
  amber:   { bg: 'bg-amber-50',   strong: 'bg-amber-100',   text: 'text-amber-700',   textLight: 'text-amber-600',   border: 'border-l-amber-400' },
  orange:  { bg: 'bg-orange-50',  strong: 'bg-orange-100',  text: 'text-orange-700',  textLight: 'text-orange-600',  border: 'border-l-orange-400' },
  red:     { bg: 'bg-red-50',     strong: 'bg-red-100',     text: 'text-red-700',     textLight: 'text-red-600',     border: 'border-l-red-400' },
  slate:   { bg: 'bg-gray-100',   strong: 'bg-gray-100',    text: 'text-gray-600',    textLight: 'text-gray-500',    border: 'border-l-gray-300' },
};

function filingAccent(status) {
  if (status === 'Synced') return ACCENTS.emerald;
  if (status === 'Approved') return ACCENTS.emerald;
  if (status === 'Filed') return ACCENTS.orange;
  if (status === 'Rejected') return ACCENTS.red;
  if (status === 'In Progress') return ACCENTS.navy;
  return ACCENTS.slate;
}
// Same convention as Event Filing: Board Meeting = navy, AGM = amber.
function meetingAccent(meetingType) { return meetingType === 'BM' ? ACCENTS.navy : ACCENTS.amber; }

const Icon = {
  calendar: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  meeting: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  form: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  checklist: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  kebab: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="12" cy="19" r="1.8"/></svg>,
  eye: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  trash: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  back: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  sync: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  alert: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
};

function IconBadge({ accent, children }) {
  return (
    <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${accent.bg} ${accent.text}`}>
      {children}
    </span>
  );
}

function KebabMenu({ actions }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const toggleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, left: Math.max(8, rect.right - 150) });
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <>
      <button ref={btnRef} onClick={(e) => { e.stopPropagation(); toggleOpen(); }}
        style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        {Icon.kebab}
      </button>
      {open && ReactDOM.createPortal(
        <div ref={menuRef} style={{ position: 'fixed', top: pos.top, left: pos.left, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 150, zIndex: 9999, overflow: 'hidden' }}>
          {actions.map((a, i) => (
            <button key={i} onClick={(e) => { e.stopPropagation(); setOpen(false); a.onClick(); }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: 'none', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: a.danger ? '#b91c1c' : '#0f1f45', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = a.danger ? '#fef2f2' : '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              {a.icon}{a.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

const STEP_ACCENTS = [ACCENTS.navy, ACCENTS.purple, ACCENTS.emerald, ACCENTS.amber, ACCENTS.cyan];
function StepBadge({ n }) {
  const accent = STEP_ACCENTS[(n - 1) % STEP_ACCENTS.length];
  return (
    <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${accent.strong} ${accent.text}`}>
      {n}
    </span>
  );
}

function AlertModal({ message, title, variant = 'error', onClose }) {
  const colors = variant === 'error' ? { bg: '#fde8e8', text: '#b91c1c', btn: '#b91c1c' } : { bg: '#e6f4ea', text: '#166534', btn: '#166534' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.35)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="modal-surface" style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 380, padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
          <span style={{ color: colors.text, fontWeight: 700, fontSize: 18 }}>{variant === 'error' ? '!' : '✓'}</span>
        </div>
        {title && <p className="modal-title" style={{ fontWeight: 700, fontSize: 15, color: '#0f1f45', margin: '0 0 6px' }}>{title}</p>}
        <p className="modal-body" style={{ fontSize: 13.5, color: '#475569', margin: 0, lineHeight: 1.5 }}>{message}</p>
        <button onClick={onClose} style={{ marginTop: 18, width: '100%', padding: '10px 0', borderRadius: 9, border: 'none', background: colors.btn, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>OK</button>
      </div>
    </div>
  );
}

function ConfirmModal({ message, title, confirmLabel = 'Confirm', onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.35)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="modal-surface" style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 400, padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
        {title && <p className="modal-title" style={{ fontWeight: 700, fontSize: 15, color: '#0f1f45', margin: '0 0 8px' }}>{title}</p>}
        <p className="modal-body" style={{ fontSize: 13.5, color: '#475569', margin: 0, lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex: 1, padding: '10px 0', borderRadius: 9, border: 'none', background: '#0f1f45', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function RegisterSyncChoiceModal({ message, title, onUpdate, onCreateNew, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.35)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="modal-surface" style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420, padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
        {title && <p className="modal-title" style={{ fontWeight: 700, fontSize: 15, color: '#0f1f45', margin: '0 0 8px' }}>{title}</p>}
        <p className="modal-body" style={{ fontSize: 13.5, color: '#475569', margin: 0, lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}>
          <button onClick={onUpdate} style={{ padding: '10px 0', borderRadius: 9, border: 'none', background: '#0f1f45', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Update Existing Record</button>
          <button onClick={onCreateNew} style={{ padding: '10px 0', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', color: '#0f1f45', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Record New Row</button>
          <button onClick={onCancel} style={{ padding: '8px 0', borderRadius: 9, border: 'none', background: 'transparent', color: '#94a3b8', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

const DARK_STYLE_OVERRIDES = `
  .dark [class~="bg-white"] { background-color: #1e293b !important; }
  .dark [class~="bg-gray-50"] { background-color: #0f172a !important; }
  .dark [class~="bg-gray-50/50"] { background-color: rgba(15,23,42,0.6) !important; }
  .dark [class~="bg-gray-50/60"] { background-color: rgba(15,23,42,0.6) !important; }
  .dark [class~="bg-gray-100"] { background-color: #334155 !important; }
  .dark [class~="hover:bg-gray-50"]:hover { background-color: #334155 !important; }
  .dark [class~="hover:bg-gray-50/60"]:hover { background-color: #334155 !important; }
  .dark [class~="hover:text-gray-600"]:hover { color: #e2e8f0 !important; }

  .dark [class~="text-gray-300"] { color: #475569 !important; }
  .dark [class~="text-gray-400"] { color: #64748b !important; }
  .dark [class~="text-gray-500"] { color: #94a3b8 !important; }
  .dark [class~="text-gray-600"] { color: #cbd5e1 !important; }
  .dark [class~="text-gray-700"] { color: #e2e8f0 !important; }
  .dark [class~="text-gray-800"] { color: #f1f5f9 !important; }
  .dark [class*="text-[#0f1f45]"] { color: #f1f5f9 !important; }

  .dark [class~="border-gray-50"] { border-color: #334155 !important; }
  .dark [class~="border-gray-100"] { border-color: #334155 !important; }
  .dark [class~="border-gray-200"] { border-color: #475569 !important; }
  .dark [class~="divide-gray-50"] > * + * { border-color: #334155 !important; }

  .dark [class~="bg-blue-50"] { background-color: rgba(30,58,138,0.3) !important; }
  .dark [class~="bg-blue-100"] { background-color: rgba(30,64,175,0.4) !important; }
  .dark [class~="text-blue-600"] { color: #60a5fa !important; }
  .dark [class~="text-blue-700"] { color: #93c5fd !important; }
  .dark [class~="border-l-blue-400"] { border-color: #3b82f6 !important; }

  .dark [class~="bg-purple-50"] { background-color: rgba(88,28,135,0.3) !important; }
  .dark [class~="bg-purple-100"] { background-color: rgba(107,33,168,0.4) !important; }
  .dark [class~="text-purple-600"] { color: #c084fc !important; }
  .dark [class~="text-purple-700"] { color: #d8b4fe !important; }
  .dark [class~="border-l-purple-400"] { border-color: #a78bfa !important; }

  .dark [class~="bg-emerald-50"] { background-color: rgba(6,78,59,0.3) !important; }
  .dark [class~="bg-emerald-100"] { background-color: rgba(6,95,70,0.4) !important; }
  .dark [class~="text-emerald-600"] { color: #34d399 !important; }
  .dark [class~="text-emerald-700"] { color: #6ee7b7 !important; }
  .dark [class~="border-l-emerald-400"] { border-color: #34d399 !important; }
  .dark [class~="bg-green-100"] { background-color: rgba(6,78,59,0.4) !important; }
  .dark [class~="text-green-600"] { color: #34d399 !important; }
  .dark [class~="text-green-700"] { color: #6ee7b7 !important; }

  .dark [class~="bg-amber-50"] { background-color: rgba(120,53,15,0.3) !important; }
  .dark [class~="bg-amber-100"] { background-color: rgba(146,64,14,0.4) !important; }
  .dark [class~="text-amber-600"] { color: #fbbf24 !important; }
  .dark [class~="text-amber-700"] { color: #fcd34d !important; }
  .dark [class~="border-l-amber-400"] { border-color: #fbbf24 !important; }

  .dark [class~="bg-orange-50"] { background-color: rgba(124,45,18,0.3) !important; }
  .dark [class~="bg-orange-100"] { background-color: rgba(154,52,18,0.4) !important; }
  .dark [class~="text-orange-600"] { color: #fb923c !important; }
  .dark [class~="text-orange-700"] { color: #fdba74 !important; }
  .dark [class~="border-l-orange-400"] { border-color: #fb923c !important; }

  .dark [class~="bg-red-50"] { background-color: rgba(127,29,29,0.3) !important; }
  .dark [class~="bg-red-100"] { background-color: rgba(153,27,27,0.4) !important; }
  .dark [class~="text-red-500"] { color: #f87171 !important; }
  .dark [class~="text-red-600"] { color: #f87171 !important; }
  .dark [class~="text-red-700"] { color: #fca5a5 !important; }
  .dark [class~="border-l-red-400"] { border-color: #f87171 !important; }
  .dark [class~="border-red-100"] { border-color: rgba(127,29,29,0.5) !important; }
  .dark [class~="border-red-200"] { border-color: rgba(153,27,27,0.5) !important; }

  .dark [class~="border-l-gray-300"] { border-color: #64748b !important; }

  .dark [class~="bg-cyan-50"] { background-color: rgba(22,78,99,0.3) !important; }
  .dark [class~="text-cyan-700"] { color: #67e8f9 !important; }

  .dark input, .dark select, .dark textarea {
    background-color: #0f172a !important;
    color: #f1f5f9 !important;
    border-color: #475569 !important;
  }
  .dark input::placeholder, .dark textarea::placeholder { color: #64748b !important; }
  .dark input:focus, .dark select:focus, .dark textarea:focus { border-color: #60a5fa !important; }
  .dark select option { background-color: #1e293b; color: #f1f5f9; }
  .dark table thead tr { background-color: #0f172a !important; }
  .dark .modal-surface { background-color: #1e293b !important; box-shadow: 0 24px 60px rgba(0,0,0,0.5) !important; }
  .dark .modal-title { color: #f1f5f9 !important; }
  .dark .modal-body { color: #cbd5e1 !important; }
`;

function fetchJson(url, opts) {
  const token = localStorage.getItem('token');
  return fetch(`${BASE}${url}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts?.headers || {}) },
  }).then(async r => {
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.message || 'Request failed');
    return data;
  });
}

// ═══════════════════════════════════════════════════════════════════════
// SHARED DOC-EXPORT + TEMPLATE HELPERS — copied from EventFilings.js
// verbatim (generic, not Event-specific) so both modules produce
// identically-styled Word output and the same resolution-drafting engine.
// ═══════════════════════════════════════════════════════════════════════
function textToHtml(text) {
  return (text || '')
    .split('\n\n')
    .map(para => `<p>${para.split('\n').map(l => l.replace(/&/g, '&amp;').replace(/</g, '&lt;')).join('<br/>')}</p>`)
    .join('');
}
function buildWordDoc(title, bodyHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4; margin: 72pt 72pt 72pt 90pt; }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 12pt; text-align: justify; color: #000; }
  p { margin: 0 0 6pt 0; line-height: 1.35; text-align: justify; }
  h1 { font-size: 14pt; font-weight: bold; text-transform: uppercase; text-align: center; margin: 0 0 4pt 0; }
  h2 { font-size: 12pt; font-weight: bold; text-align: center; margin: 0 0 12pt 0; }
  table { width: 100%; border-collapse: collapse; margin-top: 12pt; }
  td, th { border: 1pt solid #000; padding: 6pt 8pt; font-size: 10pt; vertical-align: top; }
  th { font-weight: bold; text-align: center; }
  tr.sig-row { height: 45pt; }
</style></head>
<body>${bodyHtml}</body></html>`;
}
async function downloadWord(filename, bodyHtml) {
  const html = buildWordDoc(filename, bodyHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}

// ═══════════════════════════════════════════════════════════════════════
// STATUTORY LETTER ENGINE — a second document template, distinct from
// buildWordDoc's minutes/notice/CTC styling, for formal member/company
// letters (Shorter Notice Consent, and future AGM/EGM correspondence).
// Spec: A4 portrait, margins 25/20/20/25mm (T/R/B/L), Plus Jakarta Sans,
// title 14pt bold uppercase centered (line-height 1.3), subject 11pt bold,
// body 11pt justified (line-height 1.5), address/metadata 10.5pt.
// ═══════════════════════════════════════════════════════════════════════
function buildStatutoryDoc(title, bodyHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 25mm 20mm 20mm 25mm; }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 11pt; color: #000; }
  p { margin: 0 0 8pt 0; }
  .doc-title { font-weight: bold; font-size: 14pt; text-transform: uppercase; text-align: center; line-height: 1.3; margin: 0 0 4pt 0; }
  .doc-ref { font-size: 10.5pt; text-align: center; margin: 0 0 3pt 0; }
  .doc-subtitle { font-weight: bold; font-size: 14pt; text-transform: uppercase; text-align: center; line-height: 1.3; margin: 10pt 0 16pt 0; }
  .meta { font-size: 10.5pt; margin: 0 0 2pt 0; }
  .subject { font-weight: bold; font-size: 11pt; margin: 12pt 0; text-align: left; }
  .body-text { font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 10pt 0; }
  .sig-table { width: 100%; border-collapse: collapse; margin-top: 28pt; }
  .sig-table td { border: none; font-size: 11pt; padding: 1pt 0; vertical-align: top; }
  .sig-label { color: #000; width: 42%; }
  .letter-page { page-break-after: always; }
  .letter-page:last-child { page-break-after: auto; }
</style></head>
<body>${bodyHtml}</body></html>`;
}
async function downloadStatutoryWord(filename, bodyHtml) {
  const html = buildStatutoryDoc(filename, bodyHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}

// ═══════════════════════════════════════════════════════════════════════
// NOTICE ENGINE — a third document template, for the Notice of AGM/EGM
// specifically (Board Meeting notices keep the simpler existing style).
// Full typography/hierarchy matrix as specified: company header, header
// metadata, document title, statutory provision reference, section
// headings, item titles, body narrative, bold-lead resolution quotes,
// hanging-indent statutory notes, bordered tables, and a signatory block
// — plus page-break rules (Explanatory Statement starts on a new page
// once there are more than 2 notice items; signatory/resolution blocks
// never split across a page).
// ═══════════════════════════════════════════════════════════════════════
function buildNoticeStatutoryDoc(title, bodyHtml, headerHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 25mm 20mm 20mm 25mm;${headerHtml ? ' mso-header: h1; mso-header-margin: 10mm;' : ''} }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 11pt; color: #000000; }
  p { margin: 0; }
  .co-header { font-weight: bold; font-size: 13.5pt; text-align: center; text-transform: uppercase; line-height: 1.3; margin: 0 0 4pt 0; }
  .co-meta { font-weight: normal; font-size: 9.75pt; text-align: center; line-height: 1.3; margin: 0 0 2pt 0; }
  .co-meta.last { margin-bottom: 12pt; }
  .doc-title { font-weight: bold; font-size: 12.75pt; text-align: center; text-transform: uppercase; text-decoration: underline; line-height: 1.3; margin: 10pt 0 14pt 0; }
  .doc-provision { font-style: italic; font-weight: normal; font-size: 9.25pt; text-align: center; line-height: 1.2; margin: 0 0 10pt 0; }
  .section-heading { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; text-decoration: underline; line-height: 1.4; margin: 14pt 0 6pt 0; }
  .item-title { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; line-height: 1.4; margin: 10pt 0 4pt 0; }
  .body-narrative { font-weight: normal; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 6pt 0; }
  .resolution-quote { font-size: 10.5pt; text-align: justify; line-height: 1.4; margin: 0 0 6pt 15pt; page-break-inside: avoid; }
  .resolution-quote b { font-weight: bold; }
  .agenda-list { margin: 0 0 8pt 0; padding-left: 22pt; }
  .agenda-list li { font-size: 11pt; text-align: justify; line-height: 1.5; margin-bottom: 6pt; }
  .statutory-notes { margin: 6pt 0 0 0; }
  .note-item { font-size: 9.75pt; text-align: justify; line-height: 1.35; margin: 0 0 5pt 18pt; text-indent: -18pt; }
  .note-item b { font-weight: bold; }
  table.doc-table { width: 100%; border-collapse: collapse; margin: 10pt 0; }
  table.doc-table th, table.doc-table td { border: 1pt solid #000000; padding: 5pt 8pt; font-size: 10pt; vertical-align: top; }
  table.doc-table th { font-weight: bold; background: #F2F2F2; text-align: left; }
  table.doc-table td.num, table.doc-table th.num { text-align: center; }
  .explanatory-statement p { font-weight: normal; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 6pt 0; }
  .explanatory-statement.new-page { page-break-before: always; }
  .sig-block { font-weight: bold; font-size: 10.75pt; line-height: 1.4; margin-top: 35pt; page-break-inside: avoid; }
  .sig-block .sig-name { text-transform: uppercase; }
  .sig-right { text-align: right; }
  .docx-header { mso-element: header; }
</style></head>
<body>${headerHtml ? `<div class="docx-header" id="h1">${headerHtml}</div>` : ''}${bodyHtml}</body></html>`;
}
async function downloadNoticeStatutoryWord(filename, bodyHtml, headerHtml) {
  const html = buildNoticeStatutoryDoc(filename, bodyHtml, headerHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}
// A legal Notice/CTC/Minutes quote must show ONLY the operative clauses
// ("RESOLVED THAT ... RESOLVED FURTHER THAT ...") — never any narrative
// a resolution template happens to carry before that point (e.g. "The
// Chairman informed the members that... After discussion, the members
// passed the following resolution:"). That framing belongs in MINUTES
// only, and even there it should come from what the document engine
// itself generates, not from text baked into Resolution Master — a
// template authored as a Minutes narrative must not leak into a Notice,
// which is drafted and sent BEFORE the meeting even happens. This is the
// one place that guarantee is enforced, so every quote — in every engine,
// in both modules — funnels through it.
function extractOperativeClauses(text) {
  const t = (text || '').trim().replace(/^"+/, '').replace(/"+$/, '');
  const idx = t.search(/RESOLVED\s+THAT/i);
  return idx > 0 ? t.slice(idx).trim() : t;
}
// Bolds the "RESOLVED THAT" / "RESOLVED FURTHER THAT" lead phrase, keeps
// the rest of the resolution text regular weight — the spec's "Bold Lead
// / Regular Body" treatment for resolution quotes.
function formatResolutionQuote(text) {
  const t = extractOperativeClauses(text);
  const m = t.match(/^(RESOLVED(?:\s+FURTHER)?\s+THAT)/i);
  const body = m ? `<b>${m[1].toUpperCase()}</b> ${t.slice(m[1].length).trim()}` : t;
  // Every quote block is wrapped in a single <p> when exported, and
  // HTML/Word collapse bare newlines by default — so a carefully
  // (i)/(ii)/(iii)-numbered clause list (e.g. an MOA objects-clause
  // alteration, see NUMBERED_CLAUSE_KEYS above) would otherwise print as
  // one run-on paragraph in Notice/Minutes/CTC no matter how cleanly it
  // was typed. A resolution with no line breaks is untouched.
  return body.replace(/\n/g, '<br/><br/>');
}

// ═══════════════════════════════════════════════════════════════════════
// ATTENDANCE SHEET ENGINE — a fourth document template, for the AGM/EGM
// Attendance Sheet specifically (BM attendance keeps its existing simpler
// style). Company header 13-14pt, metadata 9-9.5pt, title 12pt underlined,
// bordered 10pt meeting-metadata table, 9.5-10pt table headers on
// #F2F2F2, body rows >=28pt tall for a real signature, 10pt signatory block.
// ═══════════════════════════════════════════════════════════════════════
function buildAttendanceStatutoryDoc(title, bodyHtml, headerHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 25mm 20mm 20mm 25mm;${headerHtml ? ' mso-header: h1; mso-header-margin: 10mm;' : ''} }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 10pt; color: #000000; }
  p { margin: 0; }
  .co-header { font-weight: bold; font-size: 13.5pt; text-align: center; text-transform: uppercase; line-height: 1.3; margin: 0 0 4pt 0; }
  .co-meta { font-weight: normal; font-size: 9.25pt; text-align: center; line-height: 1.3; margin: 0 0 2pt 0; }
  .co-meta.last { margin-bottom: 10pt; }
  .doc-title { font-weight: bold; font-size: 12pt; text-align: center; text-transform: uppercase; text-decoration: underline; line-height: 1.3; margin: 8pt 0 12pt 0; }
  .meta-table { width: 100%; border-collapse: collapse; margin: 0 0 14pt 0; }
  .meta-table td { border: 1pt solid #000000; padding: 4pt 6pt; font-size: 10pt; }
  .meta-table td.label { font-weight: bold; width: 30%; background: #F7F7F7; }
  .section-heading { font-weight: bold; font-size: 10.5pt; text-align: left; text-transform: uppercase; margin: 14pt 0 6pt 0; }
  table.att-table { width: 100%; border-collapse: collapse; margin: 0 0 4pt 0; }
  table.att-table th { border: 1pt solid #000000; padding: 4pt 6pt; font-weight: bold; font-size: 9.75pt; text-align: center; background: #F2F2F2; }
  table.att-table td { border: 1pt solid #000000; padding: 4pt 6pt; font-size: 9.75pt; text-align: left; vertical-align: middle; height: 28pt; }
  table.att-table td.num { text-align: center; }
  table.att-table td.sig { width: 16%; }
  table.att-table tr.total-row td { font-weight: bold; background: #FAFAFA; }
  .quorum-block { margin-top: 16pt; border: 1pt solid #000000; padding: 8pt 10pt; page-break-inside: avoid; }
  .quorum-block .qb-title { font-weight: bold; font-size: 10.5pt; text-transform: uppercase; margin-bottom: 6pt; }
  .quorum-block p { font-size: 10pt; line-height: 1.5; margin: 0 0 3pt 0; }
  .sig-block { font-weight: bold; font-size: 10pt; line-height: 1.4; margin-top: 30pt; page-break-inside: avoid; }
  .sig-block .sig-name { text-transform: uppercase; }
  .docx-header { mso-element: header; }
</style></head>
<body>${headerHtml ? `<div class="docx-header" id="h1">${headerHtml}</div>` : ''}${bodyHtml}</body></html>`;
}
async function downloadAttendanceStatutoryWord(filename, bodyHtml, headerHtml) {
  const html = buildAttendanceStatutoryDoc(filename, bodyHtml, headerHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}
const ATTENDEE_CATEGORY_LABELS = { self: 'Self', corp_rep: 'Corp. Rep. (Sec 113)', nominee: 'Nominee (Sec 112)' };

// ═══════════════════════════════════════════════════════════════════════
// MINUTES ENGINE — a fifth document template, for the Minutes of an
// AGM/EGM specifically (BM Minutes keeps its existing simpler style, same
// asymmetric split already used for Notice/Attendance/Consent). Company
// header 13.5pt, metadata 9.75pt, title 12.75pt underlined, two-column
// attendance box (#F2F2F2 headers), numbered procedural headings,
// business-section headings, bold-lead resolution quotes with 15pt
// indent, vote-result lines, and a chairperson signature block.
// ═══════════════════════════════════════════════════════════════════════
function buildMinutesStatutoryDoc(title, bodyHtml, headerHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 25mm 20mm 20mm 25mm;${headerHtml ? ' mso-header: h1; mso-header-margin: 10mm;' : ''} }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 11pt; color: #000000; }
  p { margin: 0; }
  .co-header { font-weight: bold; font-size: 13.5pt; text-align: center; text-transform: uppercase; line-height: 1.3; margin: 0 0 4pt 0; }
  .co-meta { font-weight: normal; font-size: 9.75pt; text-align: center; line-height: 1.3; margin: 0 0 2pt 0; }
  .co-meta.last { margin-bottom: 12pt; }
  .doc-title { font-weight: bold; font-size: 12.75pt; text-align: center; text-transform: uppercase; text-decoration: underline; line-height: 1.3; margin: 10pt 0 14pt 0; }
  .opening-narrative { font-weight: bold; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 10pt 0; }
  table.attendance-box { width: 100%; border-collapse: collapse; margin: 0 0 14pt 0; }
  table.attendance-box th { border: 1pt solid #000000; padding: 5pt 8pt; font-weight: bold; font-size: 10pt; text-align: left; text-transform: uppercase; background: #F2F2F2; width: 50%; }
  table.attendance-box td { border: 1pt solid #000000; padding: 5pt 8pt; font-size: 10pt; text-align: left; vertical-align: top; }
  table.attendance-box .entry-name { font-weight: bold; }
  .proc-heading { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; line-height: 1.4; margin: 12pt 0 4pt 0; }
  .section-heading { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; text-decoration: underline; line-height: 1.4; margin: 14pt 0 6pt 0; }
  .item-title { font-weight: bold; font-size: 11pt; text-align: left; line-height: 1.4; margin: 10pt 0 4pt 0; }
  .procedural-narrative { font-weight: normal; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 6pt 0; }
  .resolution-quote { font-size: 10.5pt; text-align: justify; line-height: 1.4; margin: 0 0 6pt 15pt; page-break-inside: avoid; }
  .resolution-quote b { font-weight: bold; }
  .vote-result { font-weight: bold; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 8pt 0; }
  .minute-entry { font-weight: bold; font-size: 10pt; text-align: left; line-height: 1.35; margin: 20pt 0 15pt 0; }
  .sig-block { font-weight: bold; font-size: 10.75pt; line-height: 1.4; margin-top: 35pt; page-break-inside: avoid; }
  .sig-block .sig-name { text-transform: uppercase; }
  .docx-header { mso-element: header; }
</style></head>
<body>${headerHtml ? `<div class="docx-header" id="h1">${headerHtml}</div>` : ''}${bodyHtml}</body></html>`;
}
async function downloadMinutesStatutoryWord(filename, bodyHtml, headerHtml) {
  const html = buildMinutesStatutoryDoc(filename, bodyHtml, headerHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}

// ═══════════════════════════════════════════════════════════════════════
// CTC ENGINE — a sixth document template, for the Certified True Copy of
// ANY meeting's resolutions (Board, AGM, or EGM). Company header 13.5pt,
// metadata 9.75pt, CTC title banner 12.5pt underlined, justified preamble
// stating serial/company/day/date/commencement time/venue (+ conclusion
// for BM), bold-lead resolution quotes at 15pt indent with "RESOLVED
// THAT"/"RESOLVED FURTHER THAT" as separate paragraphs, a centered
// "// CERTIFIED TRUE COPY //" banner, and a fully borderless signatory
// block (Name/Designation/DIN-or-Membership-No/Date/Place — no HTML
// table, which would otherwise render visible grid borders in Word).
// ═══════════════════════════════════════════════════════════════════════
function buildCtcStatutoryDoc(title, bodyHtml, headerHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 25mm 20mm 20mm 25mm;${headerHtml ? ' mso-header: h1; mso-header-margin: 10mm;' : ''} }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 11pt; color: #000000; }
  p { margin: 0; }
  .co-header { font-weight: bold; font-size: 13.5pt; text-align: center; text-transform: uppercase; line-height: 1.3; margin: 0 0 3pt 0; }
  .co-meta { font-weight: normal; font-size: 9.75pt; text-align: center; line-height: 1.3; margin: 0 0 2pt 0; }
  .co-meta.last { margin-bottom: 12pt; }
  .doc-title { font-weight: bold; font-size: 12.5pt; text-align: center; text-transform: uppercase; text-decoration: underline; line-height: 1.3; margin: 10pt 0; }
  .preamble { font-weight: normal; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 12pt 0; }
  .preamble b { font-weight: bold; }
  .item-title { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; text-decoration: underline; line-height: 1.4; margin: 10pt 0; }
  .resolution-quote { font-size: 10.5pt; text-align: justify; line-height: 1.4; margin: 0 0 6pt 15pt; page-break-inside: avoid; }
  .resolution-quote b { font-weight: bold; }
  .ctc-banner { font-weight: bold; font-size: 11pt; text-align: center; line-height: 1.3; margin: 20pt 0 12pt 0; }
  .sig-block { page-break-inside: avoid; margin-top: 6pt; }
  .sig-for { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; line-height: 1.3; margin: 0 0 35pt 0; }
  .sig-name-block { font-weight: bold; font-size: 10.5pt; text-align: left; line-height: 1.3; }
  .sig-name-block .sig-name { text-transform: uppercase; }
  .sig-meta { font-weight: bold; font-size: 10pt; text-align: left; line-height: 1.3; margin-top: 10pt; }
  .section-heading { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; text-decoration: underline; line-height: 1.4; margin: 14pt 0 6pt 0; }
  .docx-header { mso-element: header; }
</style></head>
<body>${headerHtml ? `<div class="docx-header" id="h1">${headerHtml}</div>` : ''}${bodyHtml}</body></html>`;
}
async function downloadCtcStatutoryWord(filename, bodyHtml, headerHtml) {
  const html = buildCtcStatutoryDoc(filename, bodyHtml, headerHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}
// Strips stray doubled quotation marks a hand-typed resolution might carry
// (""RESOLVED THAT...."") down to a single pair, and applies the bold-lead
// treatment — same normalization the spec calls "Double-Quote Sanitization".
// (extractOperativeClauses inside formatResolutionQuote already discards
// any narrative and surrounding quotes, so this is now just an alias —
// kept as its own name since it reads better at each call site.)
function sanitizeQuotedResolution(text) {
  return formatResolutionQuote(text);
}
// Splits a resolution's full text into separate paragraphs at every
// "RESOLVED FURTHER THAT" — these must render as distinct <p> elements
// (each independently indented/bold-lead), not collapsed into one
// paragraph joined by <br/><br/>. Every Notice/Minutes/CTC quote block —
// single- or multi-clause — should always be built by mapping this over
// formatResolutionQuote(), never by quoting a resolution's raw text directly.
function splitResolutionParagraphs(text) {
  const cleaned = extractOperativeClauses(text);
  const parts = cleaned.split(/(?=RESOLVED\s+FURTHER\s+THAT)/i).map(s => s.trim()).filter(Boolean);
  return parts.length ? parts : [cleaned];
}
// A resolution's Master title sometimes carries an internal disambiguation
// suffix the CS added purely to tell similar entries apart while picking
// one from a list — e.g. "(Special Resolution — EGM, authorizes RD
// process)" or "(Board Resolution — recommends & convenes EGM)". That
// suffix must never print in a legal document heading; the picker already
// shows Type/Category as a separate subtitle, so nothing is lost by
// stripping it here. Only strips a TRAILING parenthetical that explicitly
// names a meeting/resolution-type token — a genuine substantive title
// with an unrelated parenthetical (e.g. a section reference) is left alone.
function cleanLegalTitle(title) {
  return (title || '')
    .replace(/\s*\((?:[^()]*\b(?:BM|AGM|EGM|Board Resolution|Ordinary Resolution|Special Resolution|Ordinary Business|Special Business|authorizes|recommends|convenes)\b[^()]*)\)\s*$/i, '')
    .trim();
}
// The company's letterhead — name, CIN, registered office, registered
// e-mail — rendered ONCE per document as a genuine Word page header (see
// .docx-header / mso-element:header above), so it repeats automatically on
// every page of a multi-page document instead of needing to be reapplied
// by hand. Every Notice/Attendance/Minutes/CTC call site should build this
// once and pass it as the header argument, never inline it into the body.
function buildLetterheadHtml(item) {
  return `<p class="co-header">${item.company_name}</p>
<p class="co-meta">CIN: ${item.cin_llpin || '—'}</p>
<p class="co-meta">Regd. Office: ${item.registered_office || '—'}</p>
<p class="co-meta last">E-Mail: ${item.registered_email || '—'}</p>`;
}

// ═══════════════════════════════════════════════════════════════════════
// BOARD'S REPORT ENGINE — an eighth document template, for the Directors'
// / Board's Report under Sec 134(3)/134(5) of the Companies Act, 2013 and
// Rule 8/8A of the Companies (Accounts) Rules, 2014.
//
// Coverage routes through THREE disclosure profiles, exactly as scoped:
//   Small Companies & OPCs   → Rule 8A  (Abridged, ~10 matters)
//   Non-Small Private Cos.   → Sec 134(3) + Rule 8(5) (Standard, ~25-29 clauses)
//   Public / Large Companies → Sec 134(3) + Rule 8 + Sec 178/204 (Comprehensive)
// A clause's `profiles` array is the single choke point deciding whether it
// prints at all; automation flags (loss-making, no website, no orders) only
// ever change a clause's WORDING within a profile that already includes it.
// ═══════════════════════════════════════════════════════════════════════
function isPublicCompanyClient(companyType) { return /public/i.test(companyType || ''); }
function isSmallOrOpcClient(companyType) {
  const t = (companyType || '').toLowerCase();
  return t.includes('one person') || t.includes('opc') || t.includes('small');
}
// profileOverride: 'auto' | 'abridged' | 'standard' | 'comprehensive'
function computeBoardReportProfile(companyType, profileOverride) {
  if (profileOverride && profileOverride !== 'auto') return profileOverride;
  if (isPublicCompanyClient(companyType)) return 'comprehensive';
  if (isSmallOrOpcClient(companyType)) return 'abridged';
  return 'standard';
}
const BOARD_REPORT_PROFILE_LABELS = { abridged: 'Abridged (Rule 8A)', standard: 'Standard (Rule 8)', comprehensive: 'Comprehensive (Public/Listed)' };
// Sec 135 CSR thresholds — net worth >= Rs.500 Cr, OR turnover >= Rs.1000
// Cr, OR net profit (immediately preceding FY) >= Rs.5 Cr.
function computeCsrApplicable(companyData, netProfitPy) {
  const netWorth = Number(companyData.net_worth) || 0;
  const turnover = Number(companyData.turnover) || 0;
  const netProfit = Number(netProfitPy) || 0;
  return netWorth >= 5000000000 || turnover >= 10000000000 || netProfit >= 50000000;
}
// Sec 204 Secretarial Audit — mirrors the backend's computeApplicability
// (kept as its own local copy, same convention as every other duplicated
// helper in this file): listed, OR public co. over the Rule 8 capital/
// turnover thresholds, OR borrowings >= Rs.100 Cr from banks/PFIs.
function computeSecretarialAuditApplicable(companyType, companyData) {
  const isListed = !!companyData.is_listed;
  const isPublic = isPublicCompanyClient(companyType);
  const capital = Number(companyData.paid_up_capital) || 0;
  const turnover = Number(companyData.turnover) || 0;
  const borrowings = Number(companyData.borrowings) || 0;
  return isListed || (isPublic && (capital >= 500000000 || turnover >= 2500000000)) || borrowings >= 1000000000;
}
function formatINR(num) {
  const n = Number(num) || 0;
  const sign = n < 0 ? '(' : '';
  const close = n < 0 ? ')' : '';
  return `${sign}₹${Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${close}`;
}
function fyLabelToYears(fy) {
  const m = (fy || '').match(/(\d{4})[^\d]+(\d{2,4})/);
  if (!m) return { cy: fy || '', py: '' };
  const startY = Number(m[1]);
  const prevStart = startY - 1;
  const prevEnd = String(startY).slice(-2);
  return { cy: fy, py: `${prevStart}-${prevEnd}` };
}
// ── Directors & KMP table SCOPE for the Board's Report ──────────────────
// Companies Master's director list is every director ever on file
// (active or ceased) — the Report needs only the ones actually relevant
// to THIS Report: anyone who served any part of the financial year under
// review, PLUS anyone appointed after the FY ended but still sitting as
// of the date this Report is being signed (very common — Reports are
// often drafted months after the FY closes, so the Board composition can
// already look different by then). A director who served the whole FY
// but has SINCE resigned (before the Report was even drafted) still
// needs to read as "Resigned", not silently as still-serving — otherwise
// the Report contradicts itself the day it's read.
function relevantBoardReportDirectors(directors, fyStart, fyEnd, reportDate) {
  return (directors || [])
    .filter(d => d.appointment_date) // can't place an incomplete record in time at all
    .filter(d => {
      const appt = d.appointment_date, cess = d.cessation_date;
      // (a) served any part of the FY — appointed on/before FY end, and
      // either never ceased or ceased on/after FY start.
      const servedDuringFY = (!fyEnd || appt <= fyEnd) && (!cess || !fyStart || cess >= fyStart);
      // (b) appointed strictly after FY end, on/before the Report date,
      // and not already ceased again by the Report date either.
      const appointedInGapStillSitting = !!fyEnd && appt > fyEnd
        && (!reportDate || appt <= reportDate)
        && (!cess || !reportDate || cess > reportDate);
      return servedDuringFY || appointedInGapStillSitting;
    })
    .map(d => {
      const cess = d.cessation_date;
      // Status is just Appointed/Ceased; the date column carries whichever
      // one of appointment_date/cessation_date corresponds to that status
      // — a Ceased director's row shows their resignation date there, not
      // their (now less relevant) original appointment date.
      const ceased = !!cess && (!reportDate || cess <= reportDate);
      return {
        ...d,
        board_report_status: ceased ? 'Ceased' : 'Appointed',
        board_report_status_date: ceased ? cess : d.appointment_date,
      };
    })
    .sort((a, b) => (a.appointment_date || '').localeCompare(b.appointment_date || ''));
}
// Auto-drafts the "Changes / KMP note" paragraph from the SAME two
// windows above — checked against the full (unfiltered) director list,
// so even a director who was appointed AND already resigned again
// entirely within the post-FY gap (and so never appears as a row in the
// table itself, having neither served the FY nor still sitting today)
// still gets disclosed in the narrative. Always overridable — this is
// only ever the *default*, shown until the CS types their own text.
function computeDirectorsKmpNote(directors, fyStart, fyEnd, reportDate, isPublic) {
  const fmt = (d) => formatDateForDoc(d) || d;
  const inFY = (d) => !!fyStart && !!fyEnd && d >= fyStart && d <= fyEnd;
  const inGap = (d) => !!fyEnd && d > fyEnd && (!reportDate || d <= reportDate);
  const fyEvents = [], gapEvents = [];
  (directors || []).forEach(d => {
    const role = d.designation || 'Director';
    if (d.appointment_date && inFY(d.appointment_date)) fyEvents.push(`${d.director_name} was appointed as ${role} of the Company with effect from ${fmt(d.appointment_date)}`);
    if (d.cessation_date && inFY(d.cessation_date)) fyEvents.push(`${d.director_name} resigned/ceased to be a ${role} of the Company with effect from ${fmt(d.cessation_date)}`);
    if (d.appointment_date && inGap(d.appointment_date)) gapEvents.push(`${d.director_name} was appointed as ${role} of the Company with effect from ${fmt(d.appointment_date)}`);
    if (d.cessation_date && inGap(d.cessation_date)) gapEvents.push(`${d.director_name} resigned as ${role} of the Company with effect from ${fmt(d.cessation_date)}`);
  });
  const sentences = [
    fyEvents.length
      ? `During the year under review, ${fyEvents.join('; ')}.`
      : 'During the year under review, there was no change in the composition of the Board of Directors.',
  ];
  if (gapEvents.length) sentences.push(`Subsequent to the end of the financial year and up to the date of this Report, ${gapEvents.join('; ')}.`);
  if (!isPublic) sentences.push('The Company, being a private limited company, is not required to appoint Key Managerial Personnel under Section 203 of the Companies Act, 2013 unless separately triggered.');
  return sentences.join(' ');
}
// Builds the full clause-rendering context once per render — every default
// clause text and every automation flag reads off this single object.
// Picks which Statutory Auditor Master row this report should talk about:
// the currently active one if there is one, else whoever ceased most
// recently (a report drafted in the gap between a removal/resignation and
// the replacement being appointed should still name the outgoing auditor,
// not go blank).
function activeAuditorFor(auditors) {
  const list = auditors || [];
  const active = list.find(a => a.is_active);
  if (active) return active;
  return [...list].sort((a, b) => (b.cessation_date || '').localeCompare(a.cessation_date || ''))[0] || null;
}
// "Take Data" half of the Auditors-tab wiring — pre-fills a newly-added
// auditor resolution's field_values from the Statutory Auditor Master
// instead of a blank form. RES-AGM-006 is the only auditor-lifecycle
// resolution that normally lives inside an Annual Filing case (the AGM
// itself); the others (First Auditor/casual vacancy/removal) belong to
// Event Filing and are handled by its own identical copy of this function.
function auditorResolutionPrefill(resolutionCode, auditors) {
  const a = activeAuditorFor(auditors);
  if (!a || resolutionCode !== 'RES-AGM-006') return {};
  return { auditor_firm_name: a.firm_name || '', auditor_frn: a.frn || '' };
}
function buildBoardReportCtx(item, companyData, boardReport, directors, auditors) {
  const br = boardReport || {};
  const fin = br.financials || {};
  const profile = computeBoardReportProfile(item.company_type, br.profile_override);
  const revenueCy = Number(fin.revenue_cy) || 0, revenuePy = Number(fin.revenue_py) || 0;
  const otherIncCy = Number(fin.other_income_cy) || 0, otherIncPy = Number(fin.other_income_py) || 0;
  const totalIncCy = revenueCy + otherIncCy, totalIncPy = revenuePy + otherIncPy;
  const expCy = Number(fin.total_expenditure_cy) || 0, expPy = Number(fin.total_expenditure_py) || 0;
  const pbtCy = totalIncCy - expCy, pbtPy = totalIncPy - expPy;
  const taxCy = Number(fin.tax_cy) || 0, taxPy = Number(fin.tax_py) || 0;
  const patCy = pbtCy - taxCy, patPy = pbtPy - taxPy;
  const isLossMaking = patCy < 0;
  const csrApplicable = br.csr?.applicable_override === 'yes' ? true : br.csr?.applicable_override === 'no' ? false : computeCsrApplicable(companyData, patPy);
  const secretarialAuditApplicable = computeSecretarialAuditApplicable(item.company_type, companyData);
  const hasWebsite = !!br.has_website;
  const orders = br.significant_orders || {};
  const ibc = br.ibc || {};
  const hasRdOrIbcOrders = !!orders.has_orders || !!ibc.has_proceedings;
  const { cy: fyCy, py: fyPy } = fyLabelToYears(item.financial_year);
  const isPublic = isPublicCompanyClient(item.company_type);
  // Report date (the "Date" field under Report Header — when the Board
  // actually signs this report) is the cutoff for "who's still sitting
  // as of today" — falls back to the Board Meeting (Pre-AGM) date, then
  // today, if the CS hasn't filled it in yet, so the table isn't just
  // empty/wrong before that field gets set.
  const reportDate = br.date || item.board?.meeting_date || new Date().toISOString().slice(0, 10);
  const reportDirectors = relevantBoardReportDirectors(directors, item.fy_start_date, item.fy_end_date, reportDate);
  const directorsKmpNoteDefault = computeDirectorsKmpNote(directors, item.fy_start_date, item.fy_end_date, reportDate, isPublic);
  return {
    item, companyData, br, directors: reportDirectors, allDirectors: directors || [], profile,
    activeAuditor: activeAuditorFor(auditors), allAuditors: auditors || [],
    directorsKmpNoteDefault, reportDate,
    companyName: item.company_name || '', fyCy, fyPy,
    fyEndLabel: item.fy_end_date ? formatDateForDoc(item.fy_end_date) : '',
    reportOrdinal: br.report_ordinal || '',
    revenueCy, revenuePy, otherIncCy, otherIncPy, totalIncCy, totalIncPy, expCy, expPy, pbtCy, pbtPy, taxCy, taxPy, patCy, patPy,
    isLossMaking, csrApplicable, secretarialAuditApplicable, hasWebsite, hasRdOrIbcOrders,
    isSmallOrOpc: isSmallOrOpcClient(item.company_type), isPublic,
    isListed: !!companyData.is_listed, hasSubsidiaries: !!companyData.has_subsidiaries,
  };
}
// Each clause: `profiles` is the routing gate; `text(ctx)` is the default
// narrative (always overridable per-clause via boardReport.clauses[key]).
// `special` marks clauses rendered by dedicated structured UI/doc blocks
// (financial summary table, meetings table, directors table, DRS,
// acknowledgements) rather than a plain textarea.
const BOARD_REPORT_CLAUSES = [
  { key: 'state_of_affairs', heading: "Financial Summary / Highlights and State of the Company's Affairs", profiles: ['abridged', 'standard', 'comprehensive'], special: 'financials',
    text: (ctx) => `During the year under review, the Company recorded total income of ${formatINR(ctx.totalIncCy)} as against ${formatINR(ctx.totalIncPy)} in the previous year, and a profit/(loss) after tax of ${formatINR(ctx.patCy)} as against ${formatINR(ctx.patPy)} in the previous year. The Directors continue to explore opportunities to improve the operational and financial performance of the Company.` },
  { key: 'change_in_nature_of_business', heading: 'Change in the Nature of Business', profiles: ['standard', 'comprehensive'],
    text: () => 'There was no change in the nature of the business of the Company during the financial year under review.' },
  { key: 'dividend', heading: 'Dividend', profiles: ['standard', 'comprehensive'],
    text: (ctx) => (ctx.isLossMaking || !ctx.br.dividend?.recommended)
      ? 'With a view to conserving the resources of the Company for its future operations, your Directors do not recommend any dividend for the financial year under review.'
      : `Your Directors are pleased to recommend a dividend of ${ctx.br.dividend?.rate || '[rate]'} for the financial year under review, subject to the approval of the members at the ensuing Annual General Meeting.` },
  { key: 'reserves', heading: 'Amounts Carried to Reserves', profiles: ['standard', 'comprehensive'],
    text: (ctx) => (ctx.isLossMaking || !ctx.br.reserves?.transferred)
      ? 'The Board of Directors has decided not to transfer any amount to the reserves of the Company for the financial year under review.'
      : `The Board of Directors has transferred ${formatINR(ctx.br.reserves?.amount)} to the reserves of the Company for the financial year under review.` },
  { key: 'material_changes', heading: 'Material Changes and Commitments Affecting the Financial Position', profiles: ['abridged', 'standard', 'comprehensive'],
    text: (ctx) => `No material changes or commitments affecting the financial position of the Company have occurred between the end of the financial year to which the financial statements relate${ctx.fyEndLabel ? `, i.e. ${ctx.fyEndLabel},` : ''} and the date of this report.` },
  { key: 'board_meetings', heading: 'Number of Meetings of the Board', profiles: ['abridged', 'standard', 'comprehensive'], special: 'meetings' },
  { key: 'directors_kmp', heading: 'Directors and Key Managerial Personnel', profiles: ['abridged', 'standard', 'comprehensive'], special: 'directors' },
  { key: 'independent_directors', heading: 'Declaration by Independent Directors', profiles: ['standard', 'comprehensive'],
    text: (ctx) => ctx.isPublic
      ? 'Every Independent Director has, at the first meeting of the Board in which they participated as a Director and thereafter at the first meeting of the Board in every financial year, given a declaration under Section 149(6) and (7) of the Companies Act, 2013 that they meet the criteria of independence.'
      : 'The provisions of Section 149 of the Companies Act, 2013 relating to the appointment of Independent Directors do not apply to the Company. Accordingly, no declaration under Section 149(6) has been received.' },
  { key: 'board_committees', heading: 'Board Committees and Annual Evaluation', profiles: ['standard', 'comprehensive'],
    text: (ctx) => ctx.isPublic
      ? 'Pursuant to Section 134(3)(p) of the Companies Act, 2013, the Board has carried out an annual evaluation of its own performance, of the individual directors, as well as of its Committees. The manner in which the evaluation was carried out has been detailed in the Corporate Governance Report / applicable annexure.'
      : 'The Company, being a private limited company to which the relevant provisions do not apply, is not required to constitute an Audit Committee under Section 177 or a Nomination and Remuneration Committee under Section 178 of the Companies Act, 2013, and is not required to carry out a formal annual evaluation of the performance of the Board, its committees and individual directors. Accordingly, no disclosure in this regard is required.' },
  { key: 'remuneration_policy', heading: "Company's Policy on Directors' Appointment and Remuneration", profiles: ['standard', 'comprehensive'],
    text: (ctx) => ctx.isPublic
      ? "The Company has, in terms of Section 178(1) and 178(3) of the Companies Act, 2013, formulated a policy on the appointment and remuneration of Directors, Key Managerial Personnel and Senior Management, including criteria for determining qualifications, positive attributes and independence of a Director."
      : "The provisions of Section 178(1) and 178(3) of the Companies Act, 2013 relating to the formulation of a policy on directors' appointment and remuneration do not apply to the Company." },
  { key: 'statutory_auditors', heading: "Statutory Auditors and Auditors' Report", profiles: ['abridged', 'standard', 'comprehensive'],
    // Branches on the active Statutory Auditor Master row's
    // appointment_category/confirmation_status so the tense and Companies
    // Act citation match the actual situation — First Auditor still
    // pending AGM confirmation (Sec 139(6), prospective wording, since a
    // report signed at the pre-AGM Board Meeting cannot speak of an AGM
    // appointment that hasn't happened yet), a regular AGM appointment/
    // re-appointment already confirmed (Sec 139(1), retrospective), or a
    // casual vacancy — either still awaiting EGM confirmation (Sec 139(8)
    // proviso) or already confirmed/Board-filled. Falls back to the old
    // flat boardReport.auditor object (pre-master cases) if no master row
    // has synced yet, so nothing already on file goes blank.
    text: (ctx) => {
      const a = ctx.activeAuditor;
      const legacy = ctx.br.auditor || {};
      const remark = (legacy.report_clean === false && legacy.qualification_text)
        ? legacy.qualification_text
        : "The Auditors' Report for the financial year under review does not contain any qualification, reservation, adverse remark or disclaimer. The observations, if any, made by the Auditors in their report are self-explanatory and do not call for any further comments from the Board.";
      if (!a) {
        const appt = legacy.appointed_agm_date ? `At the Company's Annual General Meeting held on ${formatDateForDoc(legacy.appointed_agm_date)}, ` : 'The members have appointed ';
        const name = legacy.firm_name || '[Statutory Auditor firm name]', frn = legacy.frn ? ` (Firm Registration No. ${legacy.frn})` : '';
        const term = (legacy.term_to_year) ? ` for a term of 5 years to hold office from the conclusion of that meeting until the conclusion of the Annual General Meeting to be held in ${legacy.term_to_year}.` : '.';
        return `${appt}${name}, Chartered Accountants${frn}, has been appointed as the Statutory Auditors of the Company${term} ${remark}`;
      }
      const name = a.firm_name, frn = a.frn ? ` (Firm Registration No. ${a.frn})` : '';
      let lead;
      if (a.appointment_category === 'First Auditor') {
        lead = `Pursuant to Section 139(6) of the Companies Act, 2013, M/s. ${name}, Chartered Accountants${frn}, were appointed as the First Auditors of the Company by the Board of Directors${a.board_resolution_date ? ` on ${formatDateForDoc(a.board_resolution_date)}` : ''}, to hold office until the conclusion of the ensuing Annual General Meeting. Being eligible, they have offered themselves for appointment as the Statutory Auditors of the Company, and the Board recommends their appointment for a term of five (5) consecutive years, as set out in the Notice convening the Meeting.`;
      } else if (a.appointment_category === 'Casual Vacancy' && a.confirmation_status === 'Pending EGM Confirmation') {
        lead = `Pursuant to Section 139(8) of the Companies Act, 2013, M/s. ${name}, Chartered Accountants${frn}, were appointed by the Board of Directors${a.board_resolution_date ? ` on ${formatDateForDoc(a.board_resolution_date)}` : ''} to fill the casual vacancy in the office of Statutory Auditors, subject to confirmation by the members at an Extraordinary General Meeting to be convened within three months of the Board's recommendation.`;
      } else if (a.appointment_category === 'Casual Vacancy') {
        const confirmedBy = a.appointed_by === 'EGM'
          ? `confirmed by the members at the Extraordinary General Meeting held on ${a.agm_resolution_date ? formatDateForDoc(a.agm_resolution_date) : '[EGM date]'}`
          : `approved by the Board of Directors${a.board_resolution_date ? ` on ${formatDateForDoc(a.board_resolution_date)}` : ''}`;
        lead = `Pursuant to Section 139(8) of the Companies Act, 2013, M/s. ${name}, Chartered Accountants${frn}, were appointed to fill the casual vacancy in the office of Statutory Auditors and ${confirmedBy}. M/s. ${name} hold office until the conclusion of the next Annual General Meeting of the Company.`;
      } else {
        const action = a.appointment_category === 'Re-appointment' ? 're-appointed' : 'appointed';
        const atMeeting = a.agm_resolution_date ? `at the Annual General Meeting held on ${formatDateForDoc(a.agm_resolution_date)}` : 'at the Annual General Meeting of the Company';
        const term = a.term_to_year ? ` for a term of five (5) consecutive years, to hold office until the conclusion of the Annual General Meeting to be held in ${a.term_to_year}` : '';
        lead = `Pursuant to Section 139(1) of the Companies Act, 2013, M/s. ${name}, Chartered Accountants${frn}, were ${action} as the Statutory Auditors of the Company ${atMeeting}${term}.`;
      }
      return `${lead} ${remark}`;
    } },
  { key: 'fraud_reporting', heading: 'Reporting of Fraud by Auditors', profiles: ['abridged', 'standard', 'comprehensive'],
    text: () => 'During the year under review, the Statutory Auditors have not reported to the Board, under Section 143(12) of the Companies Act, 2013, any instance of fraud committed in the Company by its officers or employees.' },
  { key: 'secretarial_audit', heading: 'Secretarial Audit', profiles: ['standard', 'comprehensive'],
    text: (ctx) => ctx.secretarialAuditApplicable
      ? 'Pursuant to Section 204 of the Companies Act, 2013, the Secretarial Audit Report given by a Company Secretary in Practice, in Form MR-3, is annexed to this report.'
      : 'The provisions of Section 204 of the Companies Act, 2013 relating to secretarial audit do not apply to the Company. Accordingly, a Secretarial Audit Report is not required to be annexed to this report.' },
  { key: 'cost_records', heading: 'Maintenance of Cost Records', profiles: ['standard', 'comprehensive'],
    text: () => 'The maintenance of cost records as specified by the Central Government under Section 148(1) of the Companies Act, 2013 is not applicable to the Company, and accordingly such accounts and records are not required to be maintained.' },
  { key: 'annual_return_weblink', heading: 'Extract of Annual Return', profiles: ['abridged', 'standard', 'comprehensive'],
    text: (ctx) => ctx.hasWebsite
      ? `Pursuant to Section 134(3)(a) and Section 92(3) of the Companies Act, 2013, the Annual Return of the Company for the financial year under review is available on the Company's website at ${ctx.br.website_url || '[website URL]'}.`
      : "Pursuant to Section 134(3)(a) and Section 92(3) of the Companies Act, 2013, the Company does not maintain any official website. Accordingly, the disclosure of the web address for the Annual Return for the financial year under review is not applicable." },
  { key: 'loans_investments', heading: 'Particulars of Loans, Guarantees or Investments', profiles: ['standard', 'comprehensive'],
    text: () => 'The particulars of loans given, guarantees provided and investments made under Section 186 of the Companies Act, 2013 during the year under review are disclosed in the notes to the financial statements.' },
  { key: 'related_party', heading: 'Particulars of Contracts or Arrangements with Related Parties', profiles: ['standard', 'comprehensive'],
    text: () => "All contracts, arrangements and transactions entered into by the Company with related parties during the financial year under review were in the ordinary course of business and on an arm's length basis. There were no materially significant related party transactions requiring disclosure in Form AOC-2 under Section 188(1) of the Companies Act, 2013." },
  { key: 'deposits', heading: 'Deposits', profiles: ['standard', 'comprehensive'],
    text: () => 'The Company has neither accepted nor renewed any deposits within the meaning of Chapter V of the Companies Act, 2013 read with the Companies (Acceptance of Deposits) Rules, 2014 during the year under review. Accordingly, there are no unpaid or unclaimed deposits, and no default in the repayment of deposits or in the payment of interest thereon, as at the end of the financial year.' },
  { key: 'energy_tech_forex', heading: 'Conservation of Energy, Technology Absorption and Foreign Exchange Earnings and Outgo', profiles: ['standard', 'comprehensive'],
    text: () => "The information required under Section 134(3)(m) of the Companies Act, 2013 read with Rule 8(3) of the Companies (Accounts) Rules, 2014 is furnished below:\n(a) Conservation of energy: Having regard to the nature of the Company's business, the particulars relating to the conservation of energy are not material. The Company nevertheless takes reasonable steps to conserve energy in its day-to-day operations.\n(b) Technology absorption: The Company has not imported any technology and there is no information to report under this head.\n(c) Foreign exchange earnings and outgo: There were no foreign exchange earnings or outgo during the year under review." },
  { key: 'risk_management', heading: 'Risk Management', profiles: ['standard', 'comprehensive'],
    text: () => 'The Board of Directors periodically reviews the risks associated with the business of the Company and takes appropriate steps to monitor and mitigate the same. In the opinion of the Board, there are no risks which may threaten the existence of the Company.' },
  { key: 'ifc', heading: 'Internal Financial Controls', profiles: ['standard', 'comprehensive'],
    text: (ctx) => ctx.isPublic
      ? "The Company has in place adequate internal financial controls with reference to the financial statements, commensurate with the size, scale and complexity of its operations. The Statutory Auditors have, in their report, expressed their opinion on the adequacy and operating effectiveness of the Company's internal financial controls over financial reporting."
      : 'The Company has, having regard to the size and nature of its business, put in place internal financial controls which are commensurate with the size and scale of its operations, and such controls were operating effectively during the year under review. The Board is of the opinion that the internal financial controls with reference to the financial statements were adequate during the financial year under review.' },
  { key: 'csr', heading: 'Corporate Social Responsibility', profiles: ['abridged', 'standard', 'comprehensive'],
    text: (ctx) => ctx.csrApplicable
      ? `Pursuant to Section 135 of the Companies Act, 2013, the Company has constituted a Corporate Social Responsibility (CSR) Committee. The Annual Report on CSR activities, including composition of the CSR Committee and details of amount spent, is annexed to this report.${ctx.br.csr?.committee_details ? ` ${ctx.br.csr.committee_details}` : ''}${ctx.br.csr?.spend_details ? ` ${ctx.br.csr.spend_details}` : ''}`
      : 'The provisions of Section 135 of the Companies Act, 2013 relating to Corporate Social Responsibility do not apply to the Company, as it does not meet any of the thresholds of net worth, turnover or net profit specified therein. Accordingly, the Company is not required to constitute a Corporate Social Responsibility Committee or to incur expenditure on Corporate Social Responsibility.' },
  { key: 'subsidiaries', heading: 'Subsidiaries, Joint Ventures and Associate Companies', profiles: ['standard', 'comprehensive'],
    text: (ctx) => ctx.hasSubsidiaries
      ? 'A statement containing salient features of the financial statements of the subsidiaries/joint ventures/associate companies, in Form AOC-1, is annexed to this report.'
      : 'The Company does not have any subsidiary, joint venture or associate company within the meaning of the Companies Act, 2013. No company became or ceased to be a subsidiary, joint venture or associate of the Company during the year under review.' },
  { key: 'significant_orders', heading: 'Significant and Material Orders Passed by Regulators or Courts', profiles: ['abridged', 'standard', 'comprehensive'],
    text: (ctx) => (ctx.br.significant_orders?.has_orders && ctx.br.significant_orders?.note)
      ? ctx.br.significant_orders.note
      : 'No significant or material orders were passed during the year under review by any regulator, court or tribunal which would impact the going concern status of the Company or its future operations.' },
  { key: 'ibc_proceedings', heading: 'Proceedings under the Insolvency and Bankruptcy Code, 2016', profiles: ['standard', 'comprehensive'],
    text: (ctx) => (ctx.br.ibc?.has_proceedings && ctx.br.ibc?.note)
      ? ctx.br.ibc.note
      : 'No application was made, and no proceeding is pending, against the Company under the Insolvency and Bankruptcy Code, 2016 during the year under review.' },
  { key: 'one_time_settlement', heading: 'Difference in Valuation on One-Time Settlement', profiles: ['standard', 'comprehensive'],
    text: () => 'There was no instance of a one-time settlement of any loan taken from the Banks or Financial Institutions during the year under review. Accordingly, the disclosure of any difference between the amount of the valuation done at the time of one-time settlement and the valuation done while taking a loan from the Banks or Financial Institutions is not applicable.' },
  { key: 'sexual_harassment', heading: 'Disclosure under the Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013', profiles: ['standard', 'comprehensive'],
    text: (ctx) => {
      const n = Number(ctx.br.sexual_harassment?.complaints) || 0;
      return `The Company has complied with the applicable provisions of the Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013 and the rules made thereunder. During the year under review, ${n > 0 ? `${n} complaint(s) of sexual harassment were received by the Company and dealt with in accordance with the Act.` : 'no complaint of sexual harassment was received by the Company.'}`;
    } },
  { key: 'vigil_mechanism', heading: 'Vigil Mechanism / Whistle Blower Policy', profiles: ['comprehensive'],
    text: () => 'Pursuant to Section 177(9) and (10) of the Companies Act, 2013, the Company has established a Vigil Mechanism for directors and employees to report genuine concerns, providing for adequate safeguards against victimisation and direct access to the Chairperson of the Audit Committee.' },
  { key: 'corporate_governance', heading: 'Corporate Governance Report', profiles: ['comprehensive'],
    text: (ctx) => ctx.isListed
      ? "A separate report on Corporate Governance, along with the Statutory Auditors' certificate on its compliance, is annexed to this report in terms of the SEBI (Listing Obligations and Disclosure Requirements) Regulations, 2015."
      : 'The provisions relating to Corporate Governance Report under the SEBI (Listing Obligations and Disclosure Requirements) Regulations, 2015 are not applicable to the Company, as its securities are not listed on any recognised stock exchange.' },
  { key: 'drs', heading: "Directors' Responsibility Statement", profiles: ['abridged', 'standard', 'comprehensive'], special: 'drs' },
  { key: 'acknowledgements', heading: 'Acknowledgements', profiles: ['abridged', 'standard', 'comprehensive'], special: 'acknowledgements',
    text: () => 'Your Directors place on record their sincere appreciation for the co-operation and assistance received from the bankers, government authorities, customers, vendors and members of the Company during the year under review, and for the continued dedication and contribution of the employees at all levels.' },
];
function boardReportClausesForProfile(profile) {
  return BOARD_REPORT_CLAUSES.filter(c => c.profiles.includes(profile));
}
function boardReportClauseText(clause, ctx) {
  const override = ctx.br.clauses?.[clause.key];
  return (override !== undefined && override !== null && override !== '') ? override : clause.text ? clause.text(ctx) : '';
}
const DRS_ITEMS = [
  'in the preparation of the annual accounts for the financial year, the applicable accounting standards have been followed, along with proper explanation relating to material departures, if any;',
  'the Directors have selected such accounting policies and applied them consistently, and made judgements and estimates that are reasonable and prudent, so as to give a true and fair view of the state of affairs of the Company at the end of the financial year and of the profit or loss of the Company for that period;',
  'the Directors have taken proper and sufficient care for the maintenance of adequate accounting records in accordance with the provisions of the Companies Act, 2013 for safeguarding the assets of the Company and for preventing and detecting fraud and other irregularities;',
  'the Directors have prepared the annual accounts on a going concern basis;',
  'the Directors have devised proper systems to ensure compliance with the provisions of all applicable laws, and that such systems were adequate and operating effectively.',
];
// A public company additionally confirms internal financial controls were
// laid down and operating effectively (Sec 134(5)(e)) — private companies'
// DRS stops at five items (a)-(e) above (Sec 134(5) as applicable to them).
const DRS_ITEM_PUBLIC = 'the Directors have devised proper systems to ensure compliance with the provisions of all applicable laws, and that such systems were adequate and operating effectively, and had laid down internal financial controls to be followed by the Company and that such internal financial controls are adequate and were operating effectively.';

function buildBoardReportDoc(title, bodyHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 25mm 20mm 20mm 25mm; }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 11pt; color: #000000; }
  p { margin: 0; }
  .rpt-title { font-weight: bold; font-size: 13.5pt; text-align: center; text-transform: uppercase; line-height: 1.3; margin: 0 0 6pt 0; }
  .rpt-salutation { font-weight: normal; font-size: 11pt; text-align: left; margin: 0 0 8pt 0; }
  .rpt-salutation.b { font-weight: bold; }
  .rpt-heading { font-weight: bold; font-size: 11pt; text-align: left; text-transform: uppercase; margin: 12pt 0 4pt 0; }
  .rpt-body { font-weight: normal; font-size: 11pt; text-align: justify; line-height: 1.5; margin: 0 0 6pt 0; }
  table.rpt-table { width: 100%; border-collapse: collapse; margin: 6pt 0 10pt 0; }
  table.rpt-table th, table.rpt-table td { border: 1pt solid #000000; padding: 4pt 6pt; font-size: 9.5pt; vertical-align: top; }
  table.rpt-table th { font-weight: bold; background: #F2F2F2; text-align: center; }
  table.rpt-table td.num { text-align: right; }
  table.rpt-table tr.total-row td { font-weight: bold; }
  .rpt-drs-item { font-weight: normal; font-size: 10.5pt; text-align: justify; margin: 0 0 4pt 15pt; }
  .rpt-sig-block { margin-top: 30pt; page-break-inside: avoid; }
  table.rpt-sig-table { width: 100%; border-collapse: collapse; margin-top: 30pt; }
  table.rpt-sig-table td { border: none; font-weight: bold; font-size: 10.5pt; padding: 1pt 0; vertical-align: top; }
  .rpt-sig-meta { font-weight: normal; font-size: 10.5pt; margin-top: 16pt; }
</style></head>
<body>${bodyHtml}</body></html>`;
}
async function downloadBoardReportWord(filename, bodyHtml) {
  const html = buildBoardReportDoc(filename, bodyHtml);
  await downloadHtmlAsDocx(filename.replace(/\s+/g, '_'), html);
}
// Assembles the full Board's Report HTML body — the single place that
// walks the routed clause list, in order, and renders each one either as
// its default/overridden narrative or via its dedicated `special` block.
function renderBoardReportHtml(ctx, boardMeetingsRows, signatories) {
  const clauses = boardReportClausesForProfile(ctx.profile);
  let num = 0;
  const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const paras = (text) => textToHtml(text).replace(/<p>/g, '<p class="rpt-body">');
  const parts = [];
  parts.push(`<p class="rpt-title">${esc(ctx.reportOrdinal ? `${ctx.reportOrdinal} ` : '')}BOARD'S REPORT</p>`);
  parts.push(`<p class="rpt-salutation">To the Members,</p>`);
  parts.push(`<p class="rpt-salutation b">${esc(ctx.companyName.toUpperCase())}</p>`);
  parts.push(`<p class="rpt-body">Your Directors have pleasure in presenting the ${esc(ctx.reportOrdinal || '')} Annual Report of the Company together with the audited financial statements for the financial year ended ${esc(ctx.fyEndLabel || `31st March ${ctx.fyCy?.split('-')[1] ? '20' + ctx.fyCy.split('-')[1] : ''}`)}.</p>`);

  clauses.forEach((clause) => {
    num += 1;
    parts.push(`<p class="rpt-heading">${num}. ${esc(clause.heading)}</p>`);
    if (clause.special === 'financials') {
      parts.push(`<table class="rpt-table"><tr><th>Particulars (Amount in ₹)</th><th>FY ${esc(ctx.fyCy)}</th><th>FY ${esc(ctx.fyPy)}</th></tr>
        <tr><td>Revenue from operations</td><td class="num">${formatINR(ctx.revenueCy)}</td><td class="num">${formatINR(ctx.revenuePy)}</td></tr>
        <tr><td>Other income</td><td class="num">${formatINR(ctx.otherIncCy)}</td><td class="num">${formatINR(ctx.otherIncPy)}</td></tr>
        <tr class="total-row"><td>Total income</td><td class="num">${formatINR(ctx.totalIncCy)}</td><td class="num">${formatINR(ctx.totalIncPy)}</td></tr>
        <tr><td>Total expenditure</td><td class="num">${formatINR(ctx.expCy)}</td><td class="num">${formatINR(ctx.expPy)}</td></tr>
        <tr><td>Profit / (Loss) before tax</td><td class="num">${formatINR(ctx.pbtCy)}</td><td class="num">${formatINR(ctx.pbtPy)}</td></tr>
        <tr><td>Less: Current and deferred tax</td><td class="num">${formatINR(ctx.taxCy)}</td><td class="num">${formatINR(ctx.taxPy)}</td></tr>
        <tr class="total-row"><td>Profit / (Loss) after tax</td><td class="num">${formatINR(ctx.patCy)}</td><td class="num">${formatINR(ctx.patPy)}</td></tr>
      </table>`);
      parts.push(paras(boardReportClauseText(clause, ctx)));
    } else if (clause.special === 'meetings') {
      const rows = boardMeetingsRows || [];
      parts.push(`<p class="rpt-body">During the financial year under review, ${rows.length ? `${numberToWordsIndian(rows.length).toLowerCase()} (${rows.length})` : 'no'} meeting${rows.length === 1 ? '' : 's'} of the Board of Directors ${rows.length ? 'were' : 'was'} held. The intervening gap between any two consecutive meetings was within the period prescribed under the Companies Act, 2013.</p>`);
      if (rows.length) {
        parts.push(`<table class="rpt-table"><tr><th>S. No.</th><th>Date of Meeting</th><th>No. of Directors Entitled</th><th>No. of Directors Present</th></tr>
          ${rows.map((r, i) => `<tr><td class="num">${i + 1}</td><td>${esc(formatDateForDoc(r.date) || r.date || '')}</td><td class="num">${esc(r.entitled || '')}</td><td class="num">${esc(r.present || '')}</td></tr>`).join('')}
        </table>`);
      }
    } else if (clause.special === 'directors') {
      const rows = ctx.directors || [];
      parts.push(`<table class="rpt-table"><tr><th>S. No.</th><th>Name</th><th>DIN</th><th>Designation</th><th>Date of Appointment or Resignation</th><th>Status</th></tr>
        ${rows.map((d, i) => `<tr><td class="num">${i + 1}</td><td>${esc(d.director_name || '')}</td><td>${esc(d.din || '')}</td><td>${esc(d.designation || 'Director')}</td><td>${esc(formatDateForDoc(d.board_report_status_date) || '')}</td><td>${esc(d.board_report_status || '')}</td></tr>`).join('')}
      </table>`);
      parts.push(paras(ctx.br.clauses?.directors_kmp_note || ctx.directorsKmpNoteDefault));
    } else if (clause.special === 'drs') {
      parts.push(`<p class="rpt-body">Pursuant to Section 134(3)(c) read with Section 134(5) of the Companies Act, 2013, the Directors, to the best of their knowledge and belief and according to the information and explanations obtained by them, hereby state that:</p>`);
      const items = ctx.profile === 'comprehensive' ? [...DRS_ITEMS.slice(0, 3), DRS_ITEM_PUBLIC] : DRS_ITEMS;
      items.forEach((t, i) => parts.push(`<p class="rpt-drs-item">(${String.fromCharCode(97 + i)}) ${esc(t)}</p>`));
    } else if (clause.special === 'acknowledgements') {
      parts.push(paras(boardReportClauseText(clause, ctx)));
      parts.push(`<div class="rpt-sig-block"><p class="rpt-body">By Order of the Board</p><p class="rpt-body">For and on behalf of the Board of Directors of</p><p class="rpt-salutation b">${esc(ctx.companyName.toUpperCase())}</p></div>`);
      parts.push(`<table class="rpt-sig-table"><tr>${(signatories || []).map(() => `<td style="width:50%">______________________</td>`).join('')}</tr>
        <tr>${(signatories || []).map(s => `<td>${esc(s.name || '')}<br/>Director<br/>DIN: ${esc(s.din || '')}</td>`).join('')}</tr>
      </table>`);
      parts.push(`<p class="rpt-sig-meta">Place: ${esc(ctx.br.place || '')}<br/>Date: ${esc(formatDateForDoc(ctx.br.date) || ctx.br.date || '')}</p>`);
    } else {
      parts.push(paras(boardReportClauseText(clause, ctx)));
    }
  });
  return parts.join('');
}

// ── Number → Indian-style words (Crore/Lakh/Thousand), used for share
// counts and face values in statutory letters. ──
const NUM_ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const NUM_TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
function twoDigitWords(n) { return n < 20 ? NUM_ONES[n] : NUM_TENS[Math.floor(n / 10)] + (n % 10 ? ' ' + NUM_ONES[n % 10] : ''); }
function threeDigitWords(n) {
  const h = Math.floor(n / 100), r = n % 100;
  return (h ? NUM_ONES[h] + ' Hundred' + (r ? ' ' : '') : '') + (r ? twoDigitWords(r) : '');
}
function numberToWordsIndian(num) {
  num = Math.round(Number(num) || 0);
  if (num <= 0) return 'Zero';
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  const rest = num;
  const parts = [];
  if (crore) parts.push(threeDigitWords(crore) + ' Crore');
  if (lakh) parts.push(threeDigitWords(lakh) + ' Lakh');
  if (thousand) parts.push(threeDigitWords(thousand) + ' Thousand');
  if (rest) parts.push(threeDigitWords(rest));
  return parts.join(' ').trim() || 'Zero';
}

function extractChoiceFields(templateBody) {
  const regex = /\{\{#(?:if|elif)\s+(\w+)\s*==\s*"([^"]+)"\}\}/g;
  const fields = {};
  let m;
  while ((m = regex.exec(templateBody || '')) !== null) {
    const [, field, value] = m;
    if (!fields[field]) fields[field] = [];
    if (!fields[field].includes(value)) fields[field].push(value);
  }
  return fields;
}
function resolveConditionals(templateBody, values) {
  return (templateBody || '').replace(/\{\{#if\b[\s\S]*?\{\{\/if\}\}/g, (block) => {
    const parts = block.split(/\{\{#(?:if|elif)\s+(\w+)\s*==\s*"([^"]+)"\}\}|\{\{\/if\}\}/g);
    for (let i = 1; i < parts.length; i += 3) {
      const field = parts[i], val = parts[i + 1], branch = parts[i + 2] || '';
      if (field && values?.[field] === val) return branch;
    }
    return '';
  });
}
function extractPlaceholders(templateBody) {
  const matches = (templateBody || '').match(/\{\{(\w+)\}\}/g) || [];
  const plain = matches.map(m => m.replace(/[{}]/g, ''));
  const choiceFieldNames = Object.keys(extractChoiceFields(templateBody));
  return [...new Set([...plain, ...choiceFieldNames])];
}
function humanizeKey(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
function isDateKey(key) { return /date/i.test(key); }
// A placeholder key listed here (e.g. {{altered_objects_clause_text}},
// for an MOA objects-clause alteration) renders as a repeating
// "(i) / (ii) / (iii) …" clause list instead of a single flat text box,
// auto-numbered on every edit — legal drafting for a multi-clause MOA
// amendment needs each clause on its own line, not typed by hand into
// one run-on paragraph. Raw rows live at companyData[`${key}__clauses`]
// (an array); the placeholder itself (companyData[key], what
// fillTemplate actually reads) is kept in sync with the formatted join
// on every change — same computed-field trick EventFilings.js uses for
// its own {{transfers_table}} etc., just applied inline on edit here.
const NUMBERED_CLAUSE_KEYS = ['altered_objects_clause_text'];
const LOWER_ROMAN = ['', 'i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x',
  'xi', 'xii', 'xiii', 'xiv', 'xv', 'xvi', 'xvii', 'xviii', 'xix', 'xx', 'xxi', 'xxii', 'xxiii', 'xxiv', 'xxv'];
function toLowerRoman(n) { return LOWER_ROMAN[n] || String(n); }
function formatNumberedClauses(clauses) {
  return (clauses || []).map(c => (c || '').trim()).filter(Boolean).map((c, i) => `(${toLowerRoman(i + 1)}) ${c}`).join('\n');
}
function NumberedClauseEditor({ clauses, onChange }) {
  const list = clauses && clauses.length ? clauses : [''];
  const update = (i, val) => onChange(list.map((c, idx) => idx === i ? val : c));
  const addClause = () => onChange([...list, '']);
  const removeClause = (i) => onChange(list.length > 1 ? list.filter((_, idx) => idx !== i) : ['']);
  return (
    <div className="space-y-2">
      {list.map((c, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="text-xs font-semibold text-gray-400 pt-2.5 w-9 flex-shrink-0 text-right">({toLowerRoman(i + 1)})</span>
          <textarea rows={2} value={c} onChange={e => update(i, e.target.value)} placeholder={`Clause (${toLowerRoman(i + 1)})…`}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300" />
          {list.length > 1 && (
            <button type="button" onClick={() => removeClause(i)} title="Remove clause" className="text-gray-300 hover:text-red-500 pt-2.5">{Icon.trash}</button>
          )}
        </div>
      ))}
      <button type="button" onClick={addClause} className="text-xs font-semibold text-blue-600 hover:underline">+ Add Clause</button>
    </div>
  );
}
function formatDateForDoc(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
function formatTime12h(hhmm) {
  if (!hhmm) return '—';
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}
function fillTemplate(templateBody, values) {
  const resolved = resolveConditionals(templateBody, values);
  return resolved.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const v = values?.[key];
    if (!v || !String(v).trim()) return match;
    return isDateKey(key) ? formatDateForDoc(v) : v;
  });
}
function findUnresolvedTokens(text) {
  return [...new Set((text.match(/\{\{(\w+)\}\}/g) || []).map(m => m.replace(/[{}]/g, '')))];
}
// Resolution Master stores each explanatory statement with its own leading
// "ITEM NO. [•]: <TITLE>" heading line (for standalone reference/printing),
// but the Notice document already renders "Item No. X: <Title>" above this
// block — so that stored heading line is dropped here to avoid a duplicate,
// unfilled-placeholder heading. {{tokens}} in the body are filled the same
// way resolution text is, and any leftover "Item No. [•]" self-reference in
// the body is stitched to the item's real serial number.
function renderExplanatoryStatement(rawText, itemNo, context) {
  if (!rawText) return '';
  const withoutHeading = rawText.replace(/^\s*ITEM NO\.\s*\[[^\]]*\]\s*:[^\n]*\n+/i, '');
  return fillTemplate(withoutHeading, context).replace(/\[•\]/g, String(itemNo));
}
function boardQuorum(totalDirectors) {
  if (!totalDirectors) return 2;
  return Math.max(2, Math.ceil(totalDirectors / 3));
}
function generalMeetingQuorum(companyType, totalMembers) {
  const isPublicCo = /public/i.test(companyType || '');
  if (!isPublicCo) return 2;
  if (totalMembers <= 1000) return 5;
  if (totalMembers <= 5000) return 15;
  return 30;
}
function voteWeight(isBM, personName, shareholders) {
  if (isBM) return 1;
  const sh = shareholders.find(s => s.member_name === personName);
  return sh ? Number(sh.no_of_shares) || 0 : 0;
}
function tallyVotes(votes, presentRoster, isBM, shareholders) {
  let forWeight = 0, againstWeight = 0, abstainWeight = 0;
  presentRoster.forEach(name => {
    const w = voteWeight(isBM, name, shareholders);
    const v = votes[name];
    if (v === 'for') forWeight += w;
    else if (v === 'against') againstWeight += w;
    else if (v === 'abstain') abstainWeight += w;
  });
  return { forWeight, againstWeight, abstainWeight };
}
function evaluateResolution(resolutionType, forWeight, againstWeight) {
  if (forWeight + againstWeight <= 0) return null;
  if (resolutionType === 'SPECIAL') return forWeight >= 3 * againstWeight;
  return forWeight > againstWeight;
}
// GM (Sec 101(1)) is "21 CLEAR days" — excludes both the day notice was
// sent and the day of the meeting itself, hence the extra -1. Board
// Meeting notice (Sec 173(3)) has no "clear days" concept — it's just
// "not less than seven days' notice in writing" — so BM must NOT get
// the -1 adjustment. (Matches EventFilings.js's noticeGapDays exactly;
// this used to subtract 1 unconditionally, which understated a Board
// Meeting's actual notice gap by a day.)
function clearDaysBetween(noticeDate, meetingDate, type) {
  if (!noticeDate || !meetingDate) return null;
  const raw = Math.round((new Date(meetingDate) - new Date(noticeDate)) / 86400000);
  return type === 'BM' ? raw : raw - 1;
}
function requiredNoticeDays(type) { return type === 'BM' ? 7 : 21; }

// ═══════════════════════════════════════════════════════════════════════
// FORM CATALOGUE — mirrors FORM_RULES in annualFilingRoutes.js.
// `basis` tells the wizard/case-detail where each form's due date comes
// from — 'agm' counts forward from the AGM date; 'fy'/'msme' are fixed
// dates off the financial year, independent of the AGM entirely.
// Only things actually FILED with the ROC as their own e-form live here —
// AOC-1/AOC-2/MGT-8/MGT-11/DIR-8/MBP-1 are attachments, not separate
// filings, and are generated into the case's checklist by the backend.
// ═══════════════════════════════════════════════════════════════════════
const FORM_DEFINITIONS = [
  { key: 'AOC-4',      label: 'AOC-4 — Financial Statements (Sec 137)',   basis: 'agm', offsetDays: 30, defaultOn: true },
  { key: 'AOC-4 XBRL', label: 'AOC-4 XBRL — Financial Statements (XBRL)', basis: 'agm', offsetDays: 30, defaultOn: false },
  { key: 'AOC-4 CFS',  label: 'AOC-4 CFS — Consolidated Financial Statements', basis: 'agm', offsetDays: 30, defaultOn: false },
  { key: 'MGT-7',      label: 'MGT-7 — Annual Return (Sec 92)',           basis: 'agm', offsetDays: 60, defaultOn: true },
  { key: 'MGT-7A',     label: 'MGT-7A — Annual Return (OPC/Small Company)', basis: 'agm', offsetDays: 60, defaultOn: false },
  { key: 'ADT-1',      label: 'ADT-1 — Auditor Appointment/Ratification', basis: 'agm', offsetDays: 15, defaultOn: true },
  { key: 'DPT-3',      label: 'DPT-3 — Return of Deposits (Sec 73)',      basis: 'fy', defaultOn: false },
  { key: 'MSME-1',     label: 'MSME-1 — Half-Yearly Return (Sec 405)',    basis: 'msme', defaultOn: false },
  { key: 'MSME-1-H1',  label: 'MSME-1 — Half Year Apr–Sep (Sec 405)',     basis: 'fy', defaultOn: false },
  { key: 'MSME-1-H2',  label: 'MSME-1 — Half Year Oct–Mar (Sec 405)',     basis: 'fy', defaultOn: false },
  // ── LLP (LLP Act, 2008) — fixed calendar due dates, no AGM ──
  { key: 'Form 11',    label: 'Form 11 — Annual Return (LLP) · due 30 May',                 basis: 'llp', defaultOn: false, llp: true },
  { key: 'Form 8',     label: 'Form 8 — Statement of Account & Solvency (LLP) · due 30 Oct', basis: 'llp', defaultOn: false, llp: true },
];
const FORM_GROUPS = [
  { key: 'agm', title: 'AGM-Linked Forms', note: 'Due date counts forward from the AGM date.' },
  { key: 'fy', title: 'Independent Forms — Financial Year Based', note: "Not tied to the AGM at all — due date is fixed once the financial year ends (or, for MSME-1, fixed calendar half-year dates), whether or not the AGM has happened yet." },
];
function addDaysISO(dateStr, days) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function isLLPEntity(companyType) { return /\b(llp|limited liability partnership)\b/i.test(String(companyType || '')); }
function previewDueDate(formKey, agmDate, fyEndDate) {
  const def = FORM_DEFINITIONS.find(f => f.key === formKey);
  if (!def) return null;
  if (def.basis === 'llp') {
    if (!fyEndDate) return null;
    const fyEnd = new Date(fyEndDate);
    const dueYear = fyEnd.getMonth() >= 3 ? fyEnd.getFullYear() + 1 : fyEnd.getFullYear();
    return formKey === 'Form 11' ? `${dueYear}-05-30` : `${dueYear}-10-30`;
  }
  if (def.basis === 'fy') {
    if (!fyEndDate) return null;
    const fyEnd = new Date(fyEndDate);
    const juneYear = fyEnd.getMonth() >= 3 ? fyEnd.getFullYear() + 1 : fyEnd.getFullYear();
    return `${juneYear}-06-30`;
  }
  if (def.basis === 'msme') {
    if (!fyEndDate) return null;
    const fyEnd = new Date(fyEndDate);
    const fyStartYear = fyEnd.getMonth() >= 3 ? fyEnd.getFullYear() : fyEnd.getFullYear() - 1;
    return `${fyStartYear}-10-31 & ${fyStartYear + 1}-04-30`;
  }
  return addDaysISO(agmDate, def.offsetDays);
}
function isOpcOrSmall(companyType) {
  const t = (companyType || '').toLowerCase();
  return t.includes('one person') || t.includes('opc') || t.includes('small');
}
function isPublicCompany(companyType) { return /public/i.test(companyType || ''); }

const THRESHOLDS = {
  XBRL_CAPITAL: 50000000, XBRL_TURNOVER: 1000000000,
  MGT8_CAPITAL: 100000000, MGT8_TURNOVER: 500000000,
  SEC_AUDIT_CAPITAL: 500000000, SEC_AUDIT_TURNOVER: 2500000000, SEC_AUDIT_BORROWINGS: 1000000000,
};
function computeApplicability(companyType, company, companyData) {
  const isListed = !!company?.is_listed;
  const hasSubs = !!company?.has_subsidiaries;
  const capital = Number(companyData.paid_up_capital) || 0;
  const turnover = Number(companyData.turnover) || 0;
  const borrowings = Number(companyData.borrowings) || 0;
  const isPublic = isPublicCompany(companyType);
  return {
    aoc4Xbrl: isListed || (isPublic && (capital >= THRESHOLDS.XBRL_CAPITAL || turnover >= THRESHOLDS.XBRL_TURNOVER)),
    aoc4Cfs: hasSubs,
    mgt8: isListed || capital >= THRESHOLDS.MGT8_CAPITAL || turnover >= THRESHOLDS.MGT8_TURNOVER,
    secretarialAudit: isListed
      || (isPublic && (capital >= THRESHOLDS.SEC_AUDIT_CAPITAL || turnover >= THRESHOLDS.SEC_AUDIT_TURNOVER))
      || borrowings >= THRESHOLDS.SEC_AUDIT_BORROWINGS,
  };
}
function suggestForms(companyType, company, companyData) {
  if (isLLPEntity(companyType)) return new Set(['Form 11', 'Form 8']);
  const small = isOpcOrSmall(companyType);
  const applicability = computeApplicability(companyType, company, companyData || {});
  const set = new Set(['AOC-4', 'ADT-1', small ? 'MGT-7A' : 'MGT-7']);
  if (applicability.aoc4Xbrl) set.add('AOC-4 XBRL');
  if (applicability.aoc4Cfs) set.add('AOC-4 CFS');
  return set;
}
function currentFYOptions() {
  const now = new Date();
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const out = [];
  for (let offset = 1; offset >= -3; offset--) {
    const startYear = y - offset;
    out.push(`${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`);
  }
  return out;
}
function fyToDates(fy) {
  const startYear = Number((fy || '').split('-')[0]);
  if (!startYear) return { fy_start_date: null, fy_end_date: null };
  return { fy_start_date: `${startYear}-04-01`, fy_end_date: `${startYear + 1}-03-31` };
}

// ── ORDINARY BUSINESS SLOTS — the 4 matters every AGM transacts (Sec
// 96/102). These are NOT authored resolutions — every one of them must be
// picked from Resolution Master, same as any Special Business item. This
// array only labels the 4 guided slots in the wizard so a CS can't
// forget one; the actual text always comes from whichever master
// resolution they select for that slot.
// eventTag matches the standard seeded resolution for that matter
// (RES-AGM-003/004/005/006's event_tag) — lets the wizard resolve straight
// to the one canonical resolution for a plain checkbox, no picking needed.
const ORDINARY_BUSINESS_SLOTS = [
  { key: 'adoption_accounts', label: 'Adoption of Financial Statements', eventTag: 'agm_adopt_financials' },
  { key: 'dividend', label: 'Declaration of Dividend', eventTag: 'agm_declare_dividend' },
  { key: 'director_reappointment', label: 'Re-appointment of Director Retiring by Rotation', eventTag: 'agm_director_rotation' },
  { key: 'auditor_appointment', label: 'Appointment of Statutory Auditor', eventTag: 'agm_auditor_appointment' },
];

const STAGE_ORDER = ['Draft', 'In Progress', 'Filed', 'Approved', 'Synced'];
const STATUS_COLORS = {
  'Draft':       'bg-gray-100 text-gray-600',
  'In Progress': 'bg-blue-100 text-blue-700',
  'Filed':       'bg-orange-100 text-orange-700',
  'Approved':    'bg-green-100 text-green-700',
  'Synced':      'bg-emerald-100 text-emerald-700',
  'Rejected':    'bg-red-100 text-red-700',
};
const FILING_STATUS_OPTIONS = ['Pending', 'Filed', 'Approved', 'Rejected'];
const CHECKLIST_STATUS_COLORS = {
  'Pending':        'bg-amber-100 text-amber-700',
  'Collected':      'bg-blue-100 text-blue-700',
  'Completed':      'bg-green-100 text-green-700',
  'Not Applicable': 'bg-gray-100 text-gray-500',
};
const CHECKLIST_GROUP_ORDER = ['AOC-4 Attachments', 'MGT-7 Attachments', 'AGM Requirements', 'First Board Meeting of FY', 'Other'];

function computeCaseStatus(filings, syncedAt) {
  const applicable = filings.filter(f => f.status !== 'Not Applicable');
  if (syncedAt) return 'Synced';
  if (applicable.length > 0 && applicable.every(f => f.status === 'Approved')) return 'Approved';
  if (applicable.some(f => f.status === 'Rejected')) return 'Rejected';
  if (applicable.length > 0 && applicable.every(f => ['Filed', 'Approved'].includes(f.status))) return 'Filed';
  if (applicable.some(f => ['Filed', 'Approved'].includes(f.status))) return 'In Progress';
  return 'Draft';
}
function isOverdue(filings) {
  const today = new Date().toISOString().slice(0, 10);
  return (filings || []).some(f => f.due_date && f.due_date < today && !['Filed', 'Approved'].includes(f.status));
}
function fmtDate(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}
function meetingLabel(type) { return type === 'BM' ? 'Board Meeting (Pre-AGM)' : 'Annual General Meeting'; }

// ═══════════════════════════════════════════════════════════════════════
// NEW ANNUAL FILING WIZARD
// Company & FY → Board Meeting (pre-AGM) → AGM → Financials & Forms →
// Resolutions (Ordinary Business + Board agenda + AGM Special Business,
// unrestricted) → create case.
// ═══════════════════════════════════════════════════════════════════════
function NewAnnualFilingWizard({ onClose, onCreated, entityScope }) {
  const llpScoped = entityScope === 'LLP';
  const [step, setStep] = useState(1);
  const [companies, setCompanies] = useState([]);
  const [boardMeetings, setBoardMeetings] = useState([]);
  const [agmMeetings, setAgmMeetings] = useState([]);
  const [boardResolutionOptions, setBoardResolutionOptions] = useState([]);
  const [agmResolutionOptions, setAgmResolutionOptions] = useState([]);
  const [boardResSearch, setBoardResSearch] = useState('');
  const [agmResSearch, setAgmResSearch] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [companyId, setCompanyId] = useState('');
  const [financialYear, setFinancialYear] = useState(currentFYOptions()[0]);
  const [boardMode, setBoardMode] = useState('new');
  const [boardMeetingId, setBoardMeetingId] = useState('');
  const [board, setBoard] = useState({ meeting_date: '', meeting_time: '', venue: '', notice_date: '', venue_mode: 'physical' });
  const [agmMode, setAgmMode] = useState('new');
  const [agmMeetingId, setAgmMeetingId] = useState('');
  const [agm, setAgm] = useState({ meeting_date: '', meeting_time: '', venue: '', notice_date: '', venue_mode: 'physical' });
  const [companyData, setCompanyData] = useState({ paid_up_capital: '', turnover: '', net_worth: '', borrowings: '', contribution: '', has_msme_delayed_payments: false });
  const [selectedForms, setSelectedForms] = useState(new Set(['AOC-4', 'ADT-1', 'MGT-7']));
  // One Resolution Master id per Ordinary Business slot — single-select,
  // since each slot is exactly one matter. Empty string = skipped this year.
  const [ordinarySlotIds, setOrdinarySlotIds] = useState({});
  const [boardResolutionIds, setBoardResolutionIds] = useState(new Set());
  const [agmResolutionIds, setAgmResolutionIds] = useState(new Set());

  const selectedCompany = companies.find(c => String(c.id) === String(companyId));
  // LLPs file Form 11 + Form 8 only — no Board Meeting / AGM / resolution steps.
  const isLLPWiz = isLLPEntity(selectedCompany?.company_type);
  const agmDateForPreview = agmMode === 'new' ? agm.meeting_date
    : agmMode === 'existing' ? (agmMeetings.find(m => String(m.id) === String(agmMeetingId))?.meeting_date || null)
    : null;

  useEffect(() => {
    fetchJson('/api/mca/companies')
      .then(list => setCompanies(llpScoped ? (list || []).filter(c => isLLPEntity(c.company_type)) : list))
      .catch(() => {});
    // Board Meeting agenda: only BM-tagged resolutions — a resolution can't
    // span both a Board Meeting and a general meeting (Resolution Master
    // enforces this at the source).
    fetchJson('/api/mca/resolutions?meeting=BM&active=true').then(setBoardResolutionOptions).catch(() => {});
    // AGM Special Business: AGM-tagged AND EGM-tagged resolutions — an AGM
    // can transact anything a general meeting can (Sec 102), it's just
    // never restricted to a fixed "AGM-only" preset. BM-only resolutions
    // are excluded since those can't be discussed at a general meeting.
    Promise.all([
      fetchJson('/api/mca/resolutions?meeting=AGM&active=true'),
      fetchJson('/api/mca/resolutions?meeting=EGM&active=true'),
    ]).then(([agmList, egmList]) => {
      const byId = new Map();
      [...(agmList || []), ...(egmList || [])].forEach(r => byId.set(r.id, r));
      setAgmResolutionOptions([...byId.values()]);
    }).catch(() => {});
  }, []);

  // "Use Existing Meeting" must offer meetings actually recorded in the
  // Statutory Register — not every row in mca_meetings, which is the raw
  // internal log every Event Filing case creation adds to (drafts, test
  // runs, meetings nobody ever confirmed/recorded). Register rows created
  // via Event Filings' "Sync to Register" carry event_filing_meeting_id,
  // which IS the mca_meetings.id this form's backend expects — so this
  // still submits the same shape, just filtered to what's real.
  useEffect(() => {
    if (!companyId) { setBoardMeetings([]); setAgmMeetings([]); return; }
    Promise.all([
      fetchJson(`/api/mca/companies/${companyId}/board-meetings`),
      fetchJson(`/api/mca/companies/${companyId}/general-meetings`),
    ]).then(([bmRows, gmRows]) => {
      const toOption = m => ({ id: m.event_filing_meeting_id, meeting_date: m.meeting_date, venue: m.venue });
      setBoardMeetings((bmRows || []).filter(m => m.event_filing_meeting_id).map(toOption));
      setAgmMeetings((gmRows || []).filter(m => m.event_filing_meeting_id && m.meeting_type === 'AGM').map(toOption));
    }).catch(() => { setBoardMeetings([]); setAgmMeetings([]); });
  }, [companyId]);

  useEffect(() => {
    if (!selectedCompany) return;
    setSelectedForms(suggestForms(selectedCompany.company_type, selectedCompany, companyData));
    setBoard(a => ({ ...a, venue: a.venue || selectedCompany.registered_office || '' }));
    setAgm(a => ({ ...a, venue: a.venue || selectedCompany.registered_office || '' }));
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleForm = (key) => setSelectedForms(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
  const setOrdinarySlot = (slotKey, resolutionId) => {
    setOrdinarySlotIds(prev => ({ ...prev, [slotKey]: resolutionId }));
    // If that resolution was also ticked in the free Special Business
    // list, drop it there — a slot assignment supersedes it.
    if (resolutionId) setAgmResolutionIds(prev => { const next = new Set(prev); next.delete(Number(resolutionId)); return next; });
  };
  const toggleBoardRes = (id) => setBoardResolutionIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const toggleAgmRes = (id) => setAgmResolutionIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  // Ordinary Business slots only offer resolutions Resolution Master has
  // explicitly tagged business_classification === 'ORDINARY_BUSINESS'
  // (Sec 102) — NOT resolution_type === 'ORDINARY' (Sec 114 voting
  // threshold, an entirely different axis: e.g. appointing an additional
  // director is an Ordinary RESOLUTION but Special BUSINESS). The same
  // list feeds all 4 slots; the CS assigns each one manually.
  const ordinaryResolutionOptions = agmResolutionOptions.filter(r => r.business_classification === 'ORDINARY_BUSINESS');
  const selectedOrdinaryIds = new Set(Object.values(ordinarySlotIds).filter(Boolean).map(Number));

  const filteredBoardRes = boardResolutionOptions.filter(r => {
    const q = boardResSearch.toLowerCase();
    return !q || r.resolution_title?.toLowerCase().includes(q) || r.category?.toLowerCase().includes(q);
  });
  // Special Business list only shows business_classification ===
  // 'SPECIAL_BUSINESS' items — Ordinary Business ones belong only in the
  // 4 slots above, never pickable twice.
  const filteredAgmRes = agmResolutionOptions.filter(r => {
    if (r.business_classification !== 'SPECIAL_BUSINESS') return false;
    const q = agmResSearch.toLowerCase();
    return !q || r.resolution_title?.toLowerCase().includes(q) || r.category?.toLowerCase().includes(q);
  });

  const canNextFromStep1 = companyId && financialYear;
  // A negative notice gap means the meeting date is before the notice was
  // sent — not a valid "shorter notice" case, just a wrong date — so it
  // blocks Next the same way a missing date does.
  const boardGapDays = clearDaysBetween(board.notice_date, board.meeting_date, 'BM');
  const agmGapDays = clearDaysBetween(agm.notice_date, agm.meeting_date, 'AGM');
  const canNextFromStep2 = boardMode === 'none' || (boardMode === 'existing' ? boardMeetingId : (board.meeting_date && (boardGapDays === null || boardGapDays >= 0)));
  const canNextFromStep3 = agmMode === 'none' || (agmMode === 'existing' ? agmMeetingId : (agm.meeting_date && (agmGapDays === null || agmGapDays >= 0)));

  const submit = async () => {
    setSaving(true); setError('');
    try {
      const { fy_start_date, fy_end_date } = fyToDates(financialYear);
      const body = {
        company_id: companyId,
        financial_year: financialYear,
        fy_start_date, fy_end_date,
        board: isLLPWiz ? null : boardMode === 'existing' ? { meeting_id: boardMeetingId } : boardMode === 'new' ? board : null,
        agm: isLLPWiz ? null : agmMode === 'existing' ? { meeting_id: agmMeetingId } : agmMode === 'new' ? agm : null,
        company_data: isLLPWiz
          ? { ...companyData, contribution: companyData.contribution || selectedCompany?.llp_contribution || '' }
          : companyData,
        applicable_forms: [...selectedForms],
        board_resolution_ids: isLLPWiz ? [] : [...boardResolutionIds],
        // Ordinary Business slot picks + free Special Business picks —
        // both are just Resolution Master ids; which section they render
        // under is decided later by each resolution's own resolution_type.
        agm_resolution_ids: isLLPWiz ? [] : [...new Set([...selectedOrdinaryIds, ...agmResolutionIds])],
      };
      const created = await fetchJson('/api/mca/annual-filings', { method: 'POST', body: JSON.stringify(body) });
      onCreated(created);
    } catch (e) {
      setError(e.message || 'Failed to create annual filing case');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300";
  const labelCls = "block text-xs font-semibold text-gray-500 mb-1";

  // Notice-of-meeting data only — same as Event Filing's wizard: the
  // meeting hasn't happened yet at case-creation time, so chairperson and
  // attendance aren't asked here at all. Those get recorded on the case's
  // Resolutions tab once the meeting is actually held.
  const meetingStep = (label, mode, setMode, meetingId, setMeetingId, meetingsList, spec, setSpec, requiredNote) => {
    const isVC = spec.venue_mode === 'vc';
    const isGM = label !== 'Board Meeting';
    // "Clear days" is Section 101(1)'s term for General Meetings only —
    // Section 173(3) for Board Meetings just requires "not less than
    // seven days' notice in writing", with no "clear days" concept.
    const requiredDays = requiredNoticeDays(isGM ? 'AGM' : 'BM');
    const gapDays = clearDaysBetween(spec.notice_date, spec.meeting_date, isGM ? 'AGM' : 'BM');
    const isShort = gapDays !== null && gapDays < requiredDays;
    return (
      <div className="space-y-4">
        <div className="flex gap-2">
          {['existing', 'new', 'none'].map(m => (
            <button key={m} onClick={() => setMode(m)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${mode === m ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'border-gray-200 text-gray-600'}`}>
              {m === 'existing' ? `Use Existing ${label}` : m === 'new' ? `Create New ${label}` : `No ${label} Yet`}
            </button>
          ))}
        </div>
        {mode === 'existing' && (
          <div>
            <label className={labelCls}>Select {label}</label>
            <select className={inputCls} value={meetingId} onChange={e => setMeetingId(e.target.value)}>
              <option value="">Select meeting…</option>
              {meetingsList.map(m => <option key={m.id} value={m.id}>{fmtDate(m.meeting_date)} — {m.venue || 'Venue TBD'}</option>)}
            </select>
            {meetingsList.length === 0 && <p className="text-xs text-gray-400 mt-1">No {label} found for this company yet — try "Create New {label}".</p>}
          </div>
        )}
        {mode === 'new' && (
          <div>
            <p className="text-[11px] font-semibold text-gray-500 mb-1.5">Notice of Meeting <span className="font-normal text-gray-400">— the meeting hasn't happened yet, so only what goes into the notice is needed here</span></p>
            <div className="grid grid-cols-3 gap-2 mb-1">
              <div><label className="block text-[10px] text-gray-400 mb-0.5">Notice Sent</label><input type="date" className={inputCls} value={spec.notice_date || ''} onChange={e => setSpec(a => ({ ...a, notice_date: e.target.value }))} /></div>
              <div><label className="block text-[10px] text-gray-400 mb-0.5">Meeting Date</label><input type="date" className={inputCls} min={spec.notice_date || undefined} value={spec.meeting_date} onChange={e => setSpec(a => ({ ...a, meeting_date: e.target.value }))} /></div>
              <div><label className="block text-[10px] text-gray-400 mb-0.5">Time</label><input type="time" className={inputCls} value={spec.meeting_time} onChange={e => setSpec(a => ({ ...a, meeting_time: e.target.value }))} /></div>
            </div>
            {/* A negative gap means the meeting date falls BEFORE the
                notice was sent — not a "shorter notice" scenario at all
                (there's no provision letting notice go out after the
                meeting already happened), so it gets its own hard error
                instead of the shorter-notice-consent suggestion, which
                only makes sense when notice WAS given, just late. */}
            {gapDays !== null && gapDays < 0 && (
              <p className="text-[11px] mb-2 text-red-600 font-semibold">
                ⚠ Meeting Date can't be before Notice Sent date — check the dates above.
              </p>
            )}
            {gapDays !== null && gapDays >= 0 && (
              <p className={`text-[11px] mb-2 ${isShort ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
                {gapDays} day{gapDays !== 1 ? 's' : ''}{isGM ? ' clear' : ''} notice {isShort ? `— short of the ${requiredDays}-day minimum. Shorter notice consent will be required (available on the Documents tab once the case is created).` : `— meets the ${requiredDays}-day minimum.`}
              </p>
            )}
            <div className="flex items-center gap-1.5 mb-2">
              <button type="button" onClick={() => setSpec(a => ({ ...a, venue_mode: 'physical', venue: a.venue || selectedCompany?.registered_office || '' }))}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${!isVC ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>Registered Office / Physical</button>
              <button type="button" onClick={() => setSpec(a => ({ ...a, venue_mode: 'vc', venue: 'Through Video Conferencing (VC) / Other Audio-Visual Means' }))}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${isVC ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>Audio/Video Mode</button>
            </div>
            <input className={inputCls} value={spec.venue} onChange={e => setSpec(a => ({ ...a, venue: e.target.value }))} placeholder="Venue" />
            {!isVC && spec.venue && spec.venue === selectedCompany?.registered_office && <p className="text-[11px] text-gray-400 mt-1">Defaulted to registered office — edit if this meeting is happening elsewhere.</p>}
          </div>
        )}
        {mode === 'none' && <p className="text-xs text-gray-400">{requiredNote}</p>}
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <style>{`.af-wizard-scroll::-webkit-scrollbar { display: none; }`}</style>
      <div className="bg-white af-wizard-scroll" style={{ borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 30px 80px rgba(0,0,0,0.25)', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <IconBadge accent={ACCENTS.navy}>{Icon.calendar}</IconBadge>
            <p className="font-bold text-[15px] text-[#0f1f45]">New Annual Filing</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" style={{ fontSize: 20, lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button>
        </div>

        <div className="px-6 py-4 flex items-center gap-2 text-xs text-gray-400 font-semibold flex-wrap">
          {(isLLPWiz
            ? [['Company & FY', 1], ['Forms (Form 11 & Form 8)', 4]]
            : [['Company & FY', 1], ['Board Meeting', 2], ['AGM', 3], ['Financials & Forms', 4], ['Resolutions', 5]]
          ).map(([label, n], i, arr) => (
            <React.Fragment key={label}>
              <span className={`flex items-center gap-1.5 ${step === n ? 'text-[#0f1f45]' : ''}`}><StepBadge n={i + 1} />{label}</span>
              {i < arr.length - 1 && <span style={{ width: 16, height: 1, background: '#e2e8f0' }} />}
            </React.Fragment>
          ))}
        </div>

        <div className="px-6 pb-6">
          {error && <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs font-semibold">{error}</div>}

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Company</label>
                <select className={inputCls} value={companyId} onChange={e => setCompanyId(e.target.value)}>
                  <option value="">Select company…</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.company_name} ({c.company_type})</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Financial Year</label>
                <select className={inputCls} value={financialYear} onChange={e => setFinancialYear(e.target.value)}>
                  {currentFYOptions().map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
                </select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-[11px] text-gray-400 mb-3">Most AGMs are preceded by a Board Meeting that approves the financial statements and fixes the AGM's date/notice — track it here so its own Notice/Minutes/Attendance/CTC are one click away, same as the AGM's.</p>
              {meetingStep('Board Meeting', boardMode, setBoardMode, boardMeetingId, setBoardMeetingId, boardMeetings, board, setBoard,
                'You can link the pre-AGM Board Meeting later from the case\'s Resolutions tab.')}
            </div>
          )}

          {step === 3 && meetingStep('AGM', agmMode, setAgmMode, agmMeetingId, setAgmMeetingId, agmMeetings, agm, setAgm,
            'Due dates for AOC-4/MGT-7/ADT-1 will be left blank until you link an AGM later from the case\'s Overview tab.')}

          {step === 4 && (
            <div className="space-y-5">
              {isLLPWiz ? (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Turnover for the FY (₹)</label><input type="number" className={inputCls} value={companyData.turnover} onChange={e => setCompanyData(d => ({ ...d, turnover: e.target.value }))} placeholder="Drives the Rule 24(8) audit test" /></div>
                  <div><label className={labelCls}>Total Contribution (₹)</label><input type="number" className={inputCls} value={companyData.contribution} onChange={e => setCompanyData(d => ({ ...d, contribution: e.target.value }))} placeholder={selectedCompany?.llp_contribution ? `On file: ₹${Number(selectedCompany.llp_contribution).toLocaleString('en-IN')}` : 'As per the LLP Agreement'} /></div>
                </div>
              ) : (
                <>
                  {selectedCompany && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className={`px-2 py-1 rounded-full font-semibold ${selectedCompany.is_listed ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{selectedCompany.is_listed ? 'Listed Company' : 'Not Listed'}</span>
                      <span className={`px-2 py-1 rounded-full font-semibold ${selectedCompany.has_subsidiaries ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{selectedCompany.has_subsidiaries ? 'Has Subsidiaries/JV/Associates' : 'No Subsidiaries on file'}</span>
                      <span className="text-gray-400">— edit these in Company Master</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={labelCls}>Paid-up Capital (₹)</label><input type="number" className={inputCls} value={companyData.paid_up_capital} onChange={e => setCompanyData(d => ({ ...d, paid_up_capital: e.target.value }))} /></div>
                    <div><label className={labelCls}>Turnover (₹)</label><input type="number" className={inputCls} value={companyData.turnover} onChange={e => setCompanyData(d => ({ ...d, turnover: e.target.value }))} /></div>
                    <div><label className={labelCls}>Net Worth (₹)</label><input type="number" className={inputCls} value={companyData.net_worth} onChange={e => setCompanyData(d => ({ ...d, net_worth: e.target.value }))} /></div>
                    <div><label className={labelCls}>Borrowings (₹)</label><input type="number" className={inputCls} value={companyData.borrowings} onChange={e => setCompanyData(d => ({ ...d, borrowings: e.target.value }))} /></div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-gray-600 border border-gray-200 rounded-lg px-3 py-2 cursor-pointer">
                    <input type="checkbox" checked={!!companyData.has_msme_delayed_payments} onChange={e => {
                      const checked = e.target.checked;
                      setCompanyData(d => ({ ...d, has_msme_delayed_payments: checked }));
                      setSelectedForms(prev => { const next = new Set(prev); checked ? next.add('MSME-1') : next.delete('MSME-1'); return next; });
                    }} />
                    Company sources from Micro/Small enterprises and has payments delayed beyond 45 days (triggers MSME-1)
                  </label>
                </>
              )}

              <div className="flex items-center justify-between">
                <label className={labelCls}>Applicable Forms</label>
                {!isLLPWiz && (
                  <button type="button" onClick={() => setSelectedForms(suggestForms(selectedCompany?.company_type, selectedCompany, companyData))}
                    className="text-[11px] font-semibold text-blue-600 hover:underline">Recalculate from financials above</button>
                )}
              </div>
              {isLLPWiz && (
                <div className="mb-3">
                  <p className="text-[11px] font-bold text-gray-500 mb-0.5">LLP Annual Filing — LLP Act, 2008</p>
                  <p className="text-[11px] text-gray-400 mb-1.5">No AGM. Form 11 due 30 May, Form 8 due 30 October. Accounts audit (Rule 24(8)) required if turnover &gt; ₹40 L or contribution &gt; ₹25 L — the case checklist flags this from the turnover above.</p>
                  <div className="flex flex-col gap-1.5">
                    {FORM_DEFINITIONS.filter(f => f.basis === 'llp').map(f => {
                      const dueDate = previewDueDate(f.key, null, fyToDates(financialYear).fy_end_date);
                      return (
                        <label key={f.key} className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-medium cursor-pointer ${selectedForms.has(f.key) ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-200 text-gray-600'}`}>
                          <span className="flex items-center gap-1.5"><input type="checkbox" checked={selectedForms.has(f.key)} onChange={() => toggleForm(f.key)} />{f.label}</span>
                          <span className="text-[11px] text-gray-400">{dueDate ? `Due ${fmtDate(dueDate)}` : '—'}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {!isLLPWiz && FORM_GROUPS.map(({ key: basis, title, note }) => (
                <div key={basis} className="mb-3">
                  <p className="text-[11px] font-bold text-gray-500 mb-0.5">{title}</p>
                  <p className="text-[11px] text-gray-400 mb-1.5">{note}</p>
                  <div className="flex flex-col gap-1.5">
                    {FORM_DEFINITIONS.filter(f => f.basis === basis && !f.key.startsWith('MSME-1-')).map(f => {
                      const dueDate = previewDueDate(f.key, agmDateForPreview, fyToDates(financialYear).fy_end_date);
                      return (
                        <label key={f.key} className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-medium cursor-pointer ${selectedForms.has(f.key) ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-200 text-gray-600'}`}>
                          <span className="flex items-center gap-1.5"><input type="checkbox" checked={selectedForms.has(f.key)} onChange={() => toggleForm(f.key)} />{f.label}</span>
                          <span className="text-[11px] text-gray-400">{dueDate ? `Due ${f.basis === 'msme' ? dueDate : fmtDate(dueDate)}` : 'Due date: link AGM'}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <div>
                <p className="text-[11px] font-bold text-gray-500 mb-0.5">Ordinary Business — every AGM</p>
                <p className="text-[11px] text-gray-400 mb-1.5">Tick whichever standard matters apply this year — leave one unchecked if it doesn't (e.g. no dividend recommended).</p>
                {ordinaryResolutionOptions.length === 0 && (
                  <p className="text-[11px] text-amber-600 mb-2">No ORDINARY-type AGM/EGM resolutions found in Resolution Master yet — create one (or more) there first, tagged AGM and resolution type Ordinary.</p>
                )}
                <div className="space-y-1.5">
                  {ORDINARY_BUSINESS_SLOTS.map(slot => {
                    // Normally there's exactly one seeded resolution per
                    // matter (matched by event_tag) — a firm that's
                    // customized Resolution Master and ended up with more
                    // than one candidate just gets the first; this is a
                    // plain checkbox, not a picker.
                    const resolution = ordinaryResolutionOptions.find(r => r.event_tag === slot.eventTag) || null;
                    const checked = !!ordinarySlotIds[slot.key];
                    return (
                      <label key={slot.key}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-sm ${!resolution ? 'opacity-50 cursor-not-allowed' : `cursor-pointer ${checked ? 'bg-blue-50 border-blue-200' : 'border-gray-200'}`}`}>
                        <input type="checkbox" checked={checked} disabled={!resolution}
                          onChange={() => setOrdinarySlot(slot.key, checked ? '' : String(resolution.id))} />
                        <span className="font-semibold text-[#0f1f45]">{slot.label}</span>
                        {!resolution && <span className="text-[11px] text-amber-600 ml-auto">No matching resolution in Resolution Master</span>}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-0.5">
                  <p className="text-[11px] font-bold text-gray-500">Board Meeting Agenda</p>
                  {boardResolutionIds.size > 0 && <span className="text-[11px] font-semibold text-blue-600">{boardResolutionIds.size} selected</span>}
                </div>
                <p className="text-[11px] text-gray-400 mb-1.5">From Resolution Master, tagged for Board Meetings — e.g. approving accounts for the Board, fixing the AGM's notice.</p>
                <input value={boardResSearch} onChange={e => setBoardResSearch(e.target.value)} placeholder="Search resolutions…" className={`${inputCls} mb-2`} />
                {boardResolutionOptions.length === 0 && <p className="text-xs text-gray-400">No BM-tagged resolutions found in Resolution Master.</p>}
                <div className="space-y-1.5 max-h-52 overflow-y-auto">
                  {filteredBoardRes.map(r => (
                    <label key={r.id} className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs cursor-pointer ${boardResolutionIds.has(r.id) ? 'bg-blue-50 border-blue-200' : 'border-gray-200'}`}>
                      <input type="checkbox" checked={boardResolutionIds.has(r.id)} onChange={() => toggleBoardRes(r.id)} style={{ marginTop: 2 }} />
                      <span><span className="font-semibold text-[#0f1f45]">{r.resolution_code} — {r.resolution_title}</span><span className="block text-gray-400">{r.resolution_type} · {r.category}</span></span>
                    </label>
                  ))}
                  {boardResolutionOptions.length > 0 && filteredBoardRes.length === 0 && <p className="px-3 py-2 text-center text-xs text-gray-400">No resolutions match your search</p>}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-0.5">
                  <p className="text-[11px] font-bold text-gray-500">AGM Special Business</p>
                  {agmResolutionIds.size > 0 && <span className="text-[11px] font-semibold text-purple-600">{agmResolutionIds.size} selected</span>}
                </div>
                <p className="text-[11px] text-gray-400 mb-1.5">Any AGM- or EGM-tagged resolution from Resolution Master can be moved at the AGM as Special Business (Sec 102) — not restricted to a fixed preset. BM-only resolutions aren't eligible for a general meeting, so they're excluded here.</p>
                <input value={agmResSearch} onChange={e => setAgmResSearch(e.target.value)} placeholder="Search resolutions…" className={`${inputCls} mb-2`} />
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {filteredAgmRes.map(r => (
                    <label key={r.id} className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs cursor-pointer ${agmResolutionIds.has(r.id) ? 'bg-purple-50 border-purple-200' : 'border-gray-200'}`}>
                      <input type="checkbox" checked={agmResolutionIds.has(r.id)} onChange={() => toggleAgmRes(r.id)} style={{ marginTop: 2 }} />
                      <span><span className="font-semibold text-[#0f1f45]">{r.resolution_code} — {r.resolution_title}</span><span className="block text-gray-400">{r.resolution_type} · {r.category}</span></span>
                    </label>
                  ))}
                  {agmResolutionOptions.length > 0 && filteredAgmRes.length === 0 && <p className="px-3 py-2 text-center text-xs text-gray-400">No resolutions match your search</p>}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-6">
            <button onClick={() => step > 1 ? setStep(isLLPWiz && step === 4 ? 1 : step - 1) : onClose()} className="px-4 py-2 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600">
              {step > 1 ? 'Back' : 'Cancel'}
            </button>
            {(isLLPWiz ? step < 4 : step < 5) ? (
              <button disabled={step === 1 ? !canNextFromStep1 : step === 2 ? !canNextFromStep2 : step === 3 ? !canNextFromStep3 : false}
                onClick={() => setStep(isLLPWiz && step === 1 ? 4 : step + 1)}
                className="px-5 py-2 rounded-lg text-xs font-semibold text-white bg-[#0f1f45] disabled:opacity-40">
                Next
              </button>
            ) : (
              <button disabled={saving} onClick={submit} className="px-5 py-2 rounded-lg text-xs font-semibold text-white bg-emerald-600 disabled:opacity-40">
                {saving ? 'Creating…' : 'Create Annual Filing'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CASE DETAIL
// ═══════════════════════════════════════════════════════════════════════
function AnnualFilingCaseDetail({ item: initial, onBack, onNavigate }) {
  const [item, setItem] = useState(initial);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [companyData, setCompanyData] = useState(initial.company_data || {});
  const [filings, setFilings] = useState(initial.filings || []);
  const [checklist, setChecklist] = useState(initial.checklist || []);
  const [resolutions, setResolutions] = useState(initial.resolutions || []);
  const [notes, setNotes] = useState(initial.notes || '');
  const [directors, setDirectors] = useState([]);
  const [shareholders, setShareholders] = useState([]);
  const [auditors, setAuditors] = useState([]);
  // Board's Report tab (Sec 134(3)/(5), Rule 8/8A) — structured inputs +
  // per-clause text overrides, round-tripped via the same details PATCH
  // as company_data/resolutions/filings/checklist above.
  const [boardReport, setBoardReport] = useState(initial.board_report || {});
  const [companyBoardMeetings, setCompanyBoardMeetings] = useState([]);
  // Per-meeting particulars (date/time/venue/chairperson/attendees) live
  // once per meeting here — every resolution sharing that meeting_id reads
  // the SAME entry, rather than each resolution owning its own drifting
  // copy. `meetingState[id].extras` holds notice_date/venue_mode/shorter
  // notice+consent/minutes/CTC extras — round-tripped via mca_meetings'
  // document_extras column.
  const [meetingState, setMeetingState] = useState({});
  const [proxyDraft, setProxyDraft] = useState({});
  // "Add Resolution" escape hatch — lets a resolution be linked to a
  // meeting after the case already exists (forgotten at creation, or
  // added to Resolution Master since), without deleting/recreating the case.
  const [addResBoardOptions, setAddResBoardOptions] = useState([]);
  const [addResAgmOptions, setAddResAgmOptions] = useState([]);
  const [addResOpenFor, setAddResOpenFor] = useState(null); // meetingId currently showing the picker
  const [addResSearch, setAddResSearch] = useState('');
  const [saveStatus, setSaveStatus] = useState('saved');
  const [meetingSaveStatus, setMeetingSaveStatus] = useState({});
  const [syncingWhich, setSyncingWhich] = useState(null); // 'board' | 'agm' | null — which meeting's Sync button is mid-request
  const [registerSyncChoice, setRegisterSyncChoice] = useState(null); // { which, meetingId, type, existing } while awaiting Update/New choice
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [alert, setAlert] = useState(null);
  const saveTimer = useRef(null);
  const meetingSaveTimers = useRef({});
  // meeting_id -> [{id, title, event_name, status, resolutions, module}] —
  // same cross-module sibling mechanism EventFilings.js uses: the Board
  // Meeting/AGM this case links may ALSO be carrying resolutions from an
  // Event Filing case (e.g. an objects-clause alteration folded into the
  // same meeting). Powers the "Meeting Round" panel and lets
  // getMeetingAgendaItems below show one merged agenda either way.
  const [meetingSiblings, setMeetingSiblings] = useState({});

  useEffect(() => {
    fetchJson(`/api/mca/annual-filings/${initial.id}`).then(full => {
      setItem(full);
      setCompanyData(full.company_data || {});
      setFilings(full.filings || []);
      setChecklist(full.checklist || []);
      setResolutions(full.resolutions || []);
      setNotes(full.notes || '');
      setBoardReport(full.board_report || {});
      const ms = {};
      if (full.board) ms[full.board.id] = { ...full.board, ...(full.board.document_extras || {}) };
      if (full.agm) ms[full.agm.id] = { ...full.agm, ...(full.agm.document_extras || {}) };
      setMeetingState(ms);
      if (full.company_id) {
        fetchJson(`/api/mca/companies/${full.company_id}/directors`).then(setDirectors).catch(() => {});
        fetchJson(`/api/mca/companies/${full.company_id}/shareholders`).then(setShareholders).catch(() => {});
        fetchJson(`/api/mca/companies/${full.company_id}/auditors`).then(setAuditors).catch(() => {});
        // Board's Report meetings table pre-fills from the Statutory
        // Register's own board-meeting log (not the single BM this case
        // links for resolutions) — a Board's Report must list EVERY board
        // meeting held in the FY, and only date range tells them apart.
        fetchJson(`/api/mca/companies/${full.company_id}/board-meetings`).then(rows => {
          const inFy = (rows || []).filter(m => (!full.fy_start_date || m.meeting_date >= full.fy_start_date) && (!full.fy_end_date || m.meeting_date <= full.fy_end_date));
          setCompanyBoardMeetings(inFy.sort((a, b) => (a.meeting_date || '').localeCompare(b.meeting_date || '')));
        }).catch(() => {});
      }
      // For every meeting this case links (board/agm, plus anything a
      // resolution points at), check which other cases — in EITHER
      // module — share it. Same endpoint EventFilings.js uses.
      const meetingIds = [...new Set([
        full.board?.id, full.agm?.id, ...(full.resolutions || []).map(r => r.meeting_id),
      ].filter(Boolean))];
      Promise.all(meetingIds.map(id =>
        fetchJson(`/api/mca/meetings/${id}/usage`).then(list => [id, list]).catch(() => [id, []])
      )).then(pairs => setMeetingSiblings(Object.fromEntries(pairs)));
      setLoading(false);
    }).catch(() => setLoading(false));
    // Available Resolution Master options for the "Add Resolution" escape
    // hatch below — same BM-only / AGM-or-EGM split the wizard uses, so a
    // resolution forgotten (or added to Master) after case creation can
    // still be linked in without deleting and recreating the whole case.
    fetchJson('/api/mca/resolutions?meeting=BM&active=true').then(setAddResBoardOptions).catch(() => {});
    Promise.all([
      fetchJson('/api/mca/resolutions?meeting=AGM&active=true'),
      fetchJson('/api/mca/resolutions?meeting=EGM&active=true'),
    ]).then(([agmList, egmList]) => {
      const byId = new Map();
      [...(agmList || []), ...(egmList || [])].forEach(r => byId.set(r.id, r));
      setAddResAgmOptions([...byId.values()]);
    }).catch(() => {});
  }, [initial.id]);

  const status = computeCaseStatus(filings, item.synced_at);
  const overdue = isOverdue(filings);
  const companyType = item.company_type;
  const totalMemberWeight = shareholders.reduce((sum, s) => sum + (Number(s.no_of_shares) || 0), 0);

  const scheduleSave = (patch) => {
    setSaveStatus('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const updated = await fetchJson(`/api/mca/annual-filings/${item.id}/details`, { method: 'PATCH', body: JSON.stringify(patch) });
        setItem(prev => ({ ...prev, ...updated }));
        setSaveStatus('saved');
      } catch (e) {
        setSaveStatus('error');
      }
    }, 600);
  };
  const scheduleSaveMeeting = (meetingId) => {
    setMeetingSaveStatus(prev => ({ ...prev, [meetingId]: 'saving' }));
    if (meetingSaveTimers.current[meetingId]) clearTimeout(meetingSaveTimers.current[meetingId]);
    meetingSaveTimers.current[meetingId] = setTimeout(async () => {
      const m = meetingState[meetingId];
      if (!m) return;
      try {
        await fetchJson(`/api/mca/meetings/${meetingId}`, {
          method: 'PUT',
          body: JSON.stringify({
            meeting_date: m.meeting_date, meeting_time: m.meeting_time, venue: m.venue, chairperson: m.chairperson, attendees: m.attendees || [],
            document_extras: {
              notice_date: m.notice_date || null, venue_mode: m.venue_mode || 'physical',
              shorter_notice: !!m.shorter_notice, shorter_consent: m.shorter_consent || null,
              chairman_remarks: m.chairman_remarks || '', scrutinizer_name: m.scrutinizer_name || '',
              interest_disclosure: m.interest_disclosure || '', signatory_name: m.signatory_name || '',
              signatory_role: m.signatory_role || 'Director', ctc_selection: m.ctc_selection || {},
              // Attendance Sheet (AGM/EGM) specifics — member category
              // (Sec 103/112/113 all count toward quorum; Sec 105 proxies
              // don't, so they're tracked in a wholly separate list),
              // directors/KMP present as invitees, and auditor/CS/
              // scrutinizer particulars for Sections B & C of the sheet.
              attendee_categories: m.attendee_categories || {},
              proxies: m.proxies || [],
              gm_directors_present: m.gm_directors_present || [],
              cs_name: m.cs_name || '', cs_membership_no: m.cs_membership_no || '',
              auditor_name: m.auditor_name || '', auditor_firm_name: m.auditor_firm_name || '', auditor_frn: m.auditor_frn || '',
              scrutinizer_mem_no: m.scrutinizer_mem_no || '',
              // Commencement/conclusion time — Minutes (AGM/EGM) and CTC
              // (Board Meeting) preambles both state when the meeting
              // actually ended, not just when it started.
              meeting_end_time: m.meeting_end_time || '',
              // CTC signatory — Director/MD/WTD carry a DIN, Company
              // Secretary carries a membership number instead.
              signatory_din_or_membership: m.signatory_din_or_membership || '',
            },
          }),
        });
        setMeetingSaveStatus(prev => ({ ...prev, [meetingId]: 'saved' }));
      } catch (e) {
        setMeetingSaveStatus(prev => ({ ...prev, [meetingId]: 'error' }));
      }
    }, 600);
  };
  const updateMeeting = (meetingId, patch) => {
    setMeetingState(prev => ({ ...prev, [meetingId]: { ...prev[meetingId], ...patch } }));
    scheduleSaveMeeting(meetingId);
  };

  const updateCompanyData = (patch) => { const next = { ...companyData, ...patch }; setCompanyData(next); scheduleSave({ company_data: next }); };
  const updateFiling = (idx, patch) => { const next = filings.map((f, i) => i === idx ? { ...f, ...patch } : f); setFilings(next); scheduleSave({ filings: next, status: computeCaseStatus(next, item.synced_at) }); };
  const updateChecklistItem = (id, patch) => { const next = checklist.map(c => c.id === id ? { ...c, ...patch } : c); setChecklist(next); scheduleSave({ checklist: next }); };
  const updateNotes = (val) => { setNotes(val); scheduleSave({ notes: val }); };
  const updateBoardReport = (patch) => { const next = { ...boardReport, ...patch }; setBoardReport(next); scheduleSave({ board_report: next }); };
  const updateBoardReportNested = (section, patch) => updateBoardReport({ [section]: { ...(boardReport[section] || {}), ...patch } });
  const updateBoardReportClause = (key, text) => updateBoardReport({ clauses: { ...(boardReport.clauses || {}), [key]: text } });
  const updateResolution = (idx, patch) => { const next = resolutions.map((r, i) => i === idx ? { ...r, ...patch } : r); setResolutions(next); scheduleSave({ resolutions: next }); };

  // Links a Resolution Master template into this meeting — same shape
  // buildResolutionsFromMaster() produces server-side, built client-side
  // here since this only ever touches the case's own `resolutions` array
  // (already fully editable via PATCH /:id/details, no new endpoint needed).
  // Linking the SAME resolution to a meeting twice is legitimate — e.g. a
  // separate "Regularization of Additional Director" resolution is needed
  // per director, all off the one Resolution Master template. So this
  // asks for confirmation instead of refusing outright; each instance
  // gets its own placeholder values via field_values (see fieldContextFor/
  // resolutionFieldValue above), so a second copy never overwrites the
  // first's Director Name/DIN/etc.
  const addResolutionToMeeting = (meetingId, meetingType, template) => {
    if (resolutions.some(r => r.resolution_id === template.id && r.meeting_id === meetingId)) {
      setConfirm({
        title: 'Already linked to this meeting',
        message: `"${template.resolution_title}" is already linked here. Add another copy anyway? Each copy gets its own fields to fill in separately (e.g. a different director's name/DIN) on the Event Details tab.`,
        confirmLabel: 'Add Anyway',
        onConfirm: () => { setConfirm(null); insertResolutionInstance(meetingId, meetingType, template); },
        onCancel: () => setConfirm(null),
      });
      return;
    }
    insertResolutionInstance(meetingId, meetingType, template);
  };
  const insertResolutionInstance = (meetingId, meetingType, template) => {
    const m = meetingState[meetingId] || {};
    const next = [...resolutions, {
      resolution_id: template.id, resolution_code: template.resolution_code || null,
      meeting_type: meetingType, meeting_id: meetingId,
      resolution_title: template.resolution_title, resolution_type: template.resolution_type,
      business_classification: template.business_classification || null,
      template_body: template.template_body || '', explanatory_statement: template.explanatory_statement || '',
      status: 'Pending', rendered_resolution: '', votes: {}, manually_edited: false,
      field_values: auditorResolutionPrefill(template.resolution_code, auditors),
      meeting_data: { meeting_date: m.meeting_date, meeting_time: m.meeting_time, venue: m.venue, chairperson: m.chairperson },
    }];
    setResolutions(next);
    scheduleSave({ resolutions: next });
    setAddResOpenFor(null);
    setAddResSearch('');
  };
  const removeResolution = (idx) => {
    const r = resolutions[idx];
    if (r.status !== 'Pending') {
      setAlert({ message: `This resolution is ${r.status} — revise it back to Pending first if it really needs to be removed.`, title: 'Cannot remove' });
      return;
    }
    setConfirm({
      title: 'Remove this resolution?', message: `"${r.resolution_title}" will be unlinked from this meeting.`, confirmLabel: 'Remove',
      onConfirm: () => { const next = resolutions.filter((_, i) => i !== idx); setResolutions(next); scheduleSave({ resolutions: next }); setConfirm(null); },
      onCancel: () => setConfirm(null),
    });
  };

  const placeholderContext = { company_name: item.company_name, fy_end_date: item.fy_end_date, ...companyData };
  // The SAME resolution can now be linked more than once to one meeting
  // (e.g. a separate "Regularization of Additional Director" resolution
  // per director) — each instance needs its OWN placeholder values, not
  // one shared companyData[key] every copy would fight over. Instance
  // values live on the resolution itself (r.field_values); a key not yet
  // set there falls back to companyData, so a case created before this
  // existed (single instance, value in companyData) keeps working
  // unchanged until edited.
  const fieldContextFor = (r) => ({ ...placeholderContext, ...(r.field_values || {}) });
  // Grouped per-resolution INSTANCE — one block per resolution actually
  // linked to this case, each showing only that resolution's own
  // placeholders. A field appearing here always traces back to exactly
  // one visible resolution, instead of one dumped, unattributed list.
  // The same resolution can be linked more than once (e.g. one
  // "Regularization of Additional Director" per director) — instanceLabel
  // disambiguates those (" #1", " #2", …), left blank when there's only
  // one copy so the common case reads exactly as before.
  const resolutionsWithPlaceholders = (() => {
    const idCounts = {};
    resolutions.forEach(r => { idCounts[r.resolution_id] = (idCounts[r.resolution_id] || 0) + 1; });
    const seen = {};
    return resolutions
      .map((r, resIdx) => {
        seen[r.resolution_id] = (seen[r.resolution_id] || 0) + 1;
        return {
          resolution: r, resIdx,
          instanceLabel: idCounts[r.resolution_id] > 1 ? ` #${seen[r.resolution_id]}` : '',
          keys: extractPlaceholders(r.template_body).filter(k => !['company_name', 'fy_end_date'].includes(k)),
        };
      })
      .filter(({ keys }) => keys.length > 0);
  })();
  // Writes into THIS resolution instance's own field_values, not the
  // case-level companyData — so two instances of the same resolution
  // never collide on the same key.
  const updateResolutionField = (resIdx, patch) => {
    const r = resolutions[resIdx];
    updateResolution(resIdx, { field_values: { ...(r?.field_values || {}), ...patch } });
  };
  // Reads an instance's own value if it's been set there; otherwise falls
  // back to the case-level companyData value — the pre-existing storage,
  // so a case created before per-instance fields existed (single copy,
  // value in companyData) keeps showing its data unchanged until edited.
  const resolutionFieldValue = (r, key) => (r.field_values && key in r.field_values) ? r.field_values[key] : (companyData[key] || '');
  // Total field-SLOTS across every resolution instance (not deduped by
  // key name) — two instances of the same resolution each need their own
  // value filled in, so the completeness count must reflect both.
  const allPlaceholders = resolutionsWithPlaceholders.flatMap(({ keys }) => keys);
  const filledCount = resolutionsWithPlaceholders.reduce((sum, { resolution: r, keys }) =>
    sum + keys.filter(k => String(resolutionFieldValue(r, k) || '').trim()).length, 0);

  const regenerateResolution = (idx) => {
    const r = resolutions[idx];
    const next = fillTemplate(r.template_body, fieldContextFor(r));
    if (r.manually_edited && r.rendered_resolution !== next) {
      setConfirm({
        message: 'This will overwrite the hand-edited resolution text. Continue?', title: 'Overwrite draft?', confirmLabel: 'Overwrite',
        onConfirm: () => { updateResolution(idx, { rendered_resolution: next, manually_edited: false }); setConfirm(null); },
        onCancel: () => setConfirm(null),
      });
    } else {
      updateResolution(idx, { rendered_resolution: next, manually_edited: false });
    }
  };
  const reviseResolution = (idx) => {
    setConfirm({
      message: `This resolution is marked ${resolutions[idx]?.status}. Revising it will unlock the draft and votes for editing, resetting status back to Pending until every present voter casts a vote again.`,
      title: 'Revise this resolution?', confirmLabel: 'Revise',
      onConfirm: () => { updateResolution(idx, { status: 'Pending', votes: {} }); setConfirm(null); },
      onCancel: () => setConfirm(null),
    });
  };
  const resolutionReadiness = (r) => {
    const m = meetingState[r.meeting_id] || {};
    const effectiveText = (r.manually_edited || r.status === 'Passed' || r.status === 'Rejected')
      ? (r.rendered_resolution || '').trim() : fillTemplate(r.template_body, fieldContextFor(r)).trim();
    if (!effectiveText) return { ready: false, reason: 'The resolution draft is empty.' };
    const unresolved = findUnresolvedTokens(effectiveText);
    if (unresolved.length > 0) return { ready: false, reason: `Still empty: ${unresolved.map(humanizeKey).join(', ')}.` };
    if (!m.meeting_date) return { ready: false, reason: 'Set the meeting date in Meeting Particulars first.' };
    if (!m.chairperson) return { ready: false, reason: 'Set the chairperson in Meeting Particulars first.' };
    const isBM = r.meeting_type === 'BM';
    const attendees = m.attendees || [];
    const quorumNeeded = isBM ? boardQuorum(directors.length) : generalMeetingQuorum(companyType, shareholders.length);
    if (attendees.length < quorumNeeded) return { ready: false, reason: `Quorum not met (${attendees.length}/${quorumNeeded}).` };
    return { ready: true, effectiveText };
  };
  const castVote = (idx, personName, choice) => {
    const r = resolutions[idx];
    if (r.status === 'Passed' || r.status === 'Rejected') return;
    const readiness = resolutionReadiness(r);
    if (!readiness.ready) { setAlert({ message: readiness.reason, title: 'Incomplete' }); return; }
    const isBM = r.meeting_type === 'BM';
    const m = meetingState[r.meeting_id] || {};
    const presentRoster = m.attendees || [];
    const nextVotes = { ...(r.votes || {}) };
    if (nextVotes[personName] === choice) delete nextVotes[personName]; else nextVotes[personName] = choice;
    const allVoted = presentRoster.length > 0 && presentRoster.every(name => nextVotes[name]);
    if (!allVoted) { updateResolution(idx, { votes: nextVotes }); return; }
    const { forWeight, againstWeight } = tallyVotes(nextVotes, presentRoster, isBM, shareholders);
    const passed = evaluateResolution(r.resolution_type, forWeight, againstWeight);
    updateResolution(idx, { votes: nextVotes, status: passed ? 'Passed' : 'Rejected', rendered_resolution: readiness.effectiveText });
  };

  const getMeetingSerialNumber = async (type, companyId) => {
    try {
      const url = type === 'BM' ? `/api/mca/companies/${companyId}/board-meetings` : `/api/mca/companies/${companyId}/general-meetings`;
      const list = await fetchJson(url);
      const rows = Array.isArray(list) ? list : [];
      // The general-meetings register holds BOTH AGM and EGM rows — count
      // only rows of the SAME type, otherwise an EGM held earlier in the
      // year would incorrectly inflate the AGM's serial number (and vice versa).
      const sameType = type === 'BM' ? rows : rows.filter(m => m.meeting_type === type);
      return sameType.length + 1;
    } catch { return null; }
  };
  const ordinal = (n) => n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';

  // Ordinary Business always lists before Special Business — same order
  // for the Resolutions tab AND every generated document (Notice, CTC,
  // etc.), since they all read agenda order from this one function.
  // Stable sort preserves selection order within each classification.
  //
  // Also merges in any sibling case — Event Filing or another Annual
  // Filing — sharing this meeting_id (see meetingSiblings above), so a
  // resolution folded into this same physical meeting from the other
  // module still shows up in the agenda and in Notice/Minutes/CTC. Only
  // THIS case's own resolutions are editable inline (resIdx into local
  // `resolutions` state); sibling resolutions render read-only, same
  // convention EventFilings.js uses for its own siblings.
  const getMeetingAgendaItems = (meetingId) => {
    const own = resolutions.map((r, idx) => ({ r, resIdx: idx, isOwn: true }));
    const siblingCases = (meetingSiblings[meetingId] || []).filter(c => !((c.module || 'event') === 'annual' && c.id === item.id));
    const foreign = siblingCases.flatMap(c =>
      (c.resolutions || []).filter(r => r.meeting_id === meetingId).map(r => ({ r, resIdx: -1, isOwn: false, caseTitle: c.event_name || c.title }))
    );
    return [...own, ...foreign]
      .filter(({ r }) => r.meeting_id === meetingId)
      .sort((a, b) => (a.r.business_classification === 'ORDINARY_BUSINESS' ? 0 : 1) - (b.r.business_classification === 'ORDINARY_BUSINESS' ? 0 : 1))
      .map(({ r, resIdx, isOwn, caseTitle }, i) => {
        const isFinal = r.manually_edited || r.status === 'Passed' || r.status === 'Rejected';
        const text = isFinal
          ? (r.rendered_resolution || '').trim()
          : (isOwn ? fillTemplate(r.template_body, fieldContextFor(r)).trim() : '');
        return { resolution: r, resIdx, itemNo: i + 1, isFinal, isOwn, caseTitle, text };
      });
  };

  const downloadMeetingNoticeDoc = async (meetingId, type) => {
    const items = getMeetingAgendaItems(meetingId);
    const md = meetingState[meetingId] || {};
    const shorter = !!md.shorter_notice;

    // ── Board Meeting Notice — Sec 173(2)/(3) + SS-1. Same statutory
    // engine as AGM/EGM (header/title/notes hierarchy, page-break rules),
    // with BM-specific content: no Ordinary/Special Business split, "Notes
    // on Agenda" (draft resolution text) under each item per SS-1, and the
    // Sec 173(2) video-conferencing intimation note instead of GM's proxy
    // notes. ──
    if (type === 'BM') {
      // Board Meetings are not serially numbered — only AGMs are (they run
      // year-wise, one per financial year). EGMs and Board Meetings have no
      // "Nth Meeting" counting per the user's explicit instruction.
      const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
      const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

      const bmNotes = [];
      bmNotes.push('A Director who intends to participate through Video Conferencing or Other Audio-Visual Means shall intimate the Company/Chairman of his intention sufficiently in advance, in accordance with Section 173(2) of the Companies Act, 2013 read with the applicable Rules, so that the Company can make necessary arrangements.');
      bmNotes.push('Any item(s) of business not included in this Agenda may be taken up with the consent of a majority of the Directors present at the Meeting, at least one of whom shall be an Independent Director, if the Company is required to have one.');
      if (shorter) bmNotes.push('This Meeting is being held at shorter notice, consent for which has been obtained from the requisite majority of Directors in terms of the proviso to Section 173(3) of the Companies Act, 2013.');

      // A Board Meeting Notice states only WHAT will be discussed — the
      // operative "RESOLVED THAT" text belongs in the Minutes, recorded
      // AFTER the Board actually resolves it, never drafted in advance
      // into the Notice calling the meeting. Each agenda item is just a
      // short descriptive line naming the matter.
      const renderBmItem = (i) => `<li>To consider and, if thought fit, approve ${cleanLegalTitle(i.resolution.resolution_title)}.</li>`;
      const headerHtml = buildLetterheadHtml(item);

      const html = `
        <p class="doc-title">Notice of Board Meeting</p>
        <p class="doc-provision">[Pursuant to Section 173 of the Companies Act, 2013 and Secretarial Standard-1]</p>

        <p class="body-narrative">NOTICE is hereby given that the Meeting of the Board of Directors of ${item.company_name} will be held on ${meetingDay}, the ${meetingDateFmt} at ${formatTime12h(md.meeting_time)} at ${md.venue || '__________'}${shorter ? ', on shorter notice,' : ''}, to transact the following business:</p>

        <p class="section-heading">Agenda</p>
        ${items.length ? `<ol class="agenda-list">${items.map(renderBmItem).join('')}</ol>` : '<p class="body-narrative">No agenda items linked to this meeting yet.</p>'}

        <p class="section-heading">Notes</p>
        <div class="statutory-notes">
          ${bmNotes.map((n, i) => `<p class="note-item"><b>${i + 1}.</b> ${n}</p>`).join('')}
        </div>

        <div class="sig-block">
          By Order of the Board<br/>
          For ${item.company_name}<br/><br/><br/>
          _____________________<br/>
          <span class="sig-name">${md.chairperson || 'Director'}</span><br/>
          Director<br/><br/>
          Place: <br/>
          Date: ${md.notice_date ? new Date(md.notice_date).toLocaleDateString('en-IN') : ''}
        </div>
      `;
      downloadNoticeStatutoryWord(`Notice ${type} - ${item.company_name}`, html, headerHtml);
      return;
    }

    const isAGM = type === 'AGM';
    const serial = isAGM ? await getMeetingSerialNumber('AGM', item.company_id) : null;
    const serialLabel = serial ? `${serial}${ordinal(serial)} ` : '';
    const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
    const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

    const ordinaryItems = items.filter(i => i.resolution.business_classification === 'ORDINARY_BUSINESS');
    const specialItems = items.filter(i => i.resolution.business_classification !== 'ORDINARY_BUSINESS');
    const explanatoryItems = specialItems.filter(i => i.resolution.explanatory_statement?.trim());
    const needsNewPage = items.length > 2;

    const renderItem = (i) => `
      <p class="item-title">Item No. ${i.itemNo}: ${cleanLegalTitle(i.resolution.resolution_title)}</p>
      ${splitResolutionParagraphs(i.text).map(p => `<p class="resolution-quote">"${formatResolutionQuote(p)}"</p>`).join('')}
    `;

    const notes = [];
    notes.push('A Member entitled to attend and vote at the Meeting is entitled to appoint a proxy to attend and vote instead of himself/herself, and the proxy need not be a Member of the Company.');
    notes.push('Proxies, in order to be effective, must be deposited at the Registered Office of the Company, duly completed and signed, not less than forty-eight (48) hours before the commencement of the Meeting (Form MGT-11).');
    notes.push('Corporate Members intending to send their authorised representatives to attend the Meeting are requested to send a duly certified copy of the Board Resolution / Power of Attorney authorising their representative to attend and vote on their behalf.');
    notes.push('Voting on the resolution(s) set out in this Notice shall be conducted by poll.');
    if (explanatoryItems.length) notes.push('The Explanatory Statement pursuant to Section 102 of the Companies Act, 2013 in respect of the Special Business set out above is annexed hereto.');
    if (shorter) notes.push('This Meeting is being held at shorter notice, consent for which has been obtained from the requisite majority of Members in terms of the proviso to Section 101(1) of the Companies Act, 2013.');

    const headerHtml = buildLetterheadHtml(item);
    const html = `
      <p class="doc-title">Notice of ${isAGM ? 'Annual' : 'Extraordinary'} General Meeting</p>
      <p class="doc-provision">[Pursuant to Section ${isAGM ? '96' : '100'} of the Companies Act, 2013 and applicable Secretarial Standards]</p>

      <p class="body-narrative">NOTICE is hereby given that the ${serialLabel}${isAGM ? 'Annual' : 'Extraordinary'} General Meeting of the Members of ${item.company_name} will be held on ${meetingDay}, the ${meetingDateFmt} at ${formatTime12h(md.meeting_time)} at ${md.venue || '__________'}${shorter ? ', on shorter notice,' : ''} to transact the following business:</p>

      ${ordinaryItems.length ? `<p class="section-heading">Ordinary Business</p>${ordinaryItems.map(renderItem).join('')}` : ''}
      ${specialItems.length ? `<p class="section-heading">Special Business</p>${specialItems.map(renderItem).join('')}` : ''}

      <p class="section-heading">Notes</p>
      <div class="statutory-notes">
        ${notes.map((n, i) => `<p class="note-item"><b>${i + 1}.</b> ${n}</p>`).join('')}
      </div>

      <div class="sig-block">
        By Order of the Board<br/>
        For ${item.company_name}<br/><br/><br/>
        _____________________<br/>
        <span class="sig-name">${md.chairperson || 'Director'}</span><br/>
        Director<br/><br/>
        Place: <br/>
        Date: ${md.notice_date ? new Date(md.notice_date).toLocaleDateString('en-IN') : ''}
      </div>

      ${explanatoryItems.length ? `
        <div class="explanatory-statement${needsNewPage ? ' new-page' : ''}">
          <p class="doc-title">Explanatory Statement</p>
          <p class="doc-provision">[Pursuant to Section 102 of the Companies Act, 2013]</p>
          ${explanatoryItems.map(i => `
            <p class="item-title">Item No. ${i.itemNo}: ${cleanLegalTitle(i.resolution.resolution_title)}</p>
            ${textToHtml(renderExplanatoryStatement(i.resolution.explanatory_statement, i.itemNo, fieldContextFor(i.resolution)))}
          `).join('')}
          <p>None of the Directors or Key Managerial Personnel of the Company, or their relatives, is concerned or interested, financially or otherwise, in the resolution(s) set out above, except to the extent of their shareholding, if any.</p>
        </div>
      ` : ''}
    `;
    downloadNoticeStatutoryWord(`Notice ${type} - ${item.company_name}`, html, headerHtml);
  };

  const downloadMeetingMinutesDoc = async (meetingId, type) => {
    const items = getMeetingAgendaItems(meetingId);
    const md = meetingState[meetingId] || {};

    // ── Board Meeting Minutes — Sec 118 + SS-1, same statutory engine as
    // AGM/EGM Minutes (typography, page-break rules), with BM-specific
    // content: Quorum under Sec 174(1) (1/3rd of total directors or 2,
    // whichever is higher — not Sec 103's GM quorum), Sec 184 interest
    // disclosure, confirmation of previous Minutes, and a single Agenda
    // (no Ordinary/Special Business split — that's a GM-only concept). ──
    if (type === 'BM') {
      const attendees = md.attendees || [];
      const roster = directors.map(d => d.director_name);
      const absentees = roster.filter(n => !attendees.includes(n));
      const quorumNeeded = boardQuorum(directors.length);
      const quorumMet = attendees.length >= quorumNeeded;
      const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
      const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
      const mode = md.venue_mode === 'vc' ? 'VC' : 'Physical';

      const presentEntries = attendees.map(name => {
        const d = directors.find(x => x.director_name === name);
        return `<span class="entry-name">${name}</span> — ${d?.designation || 'Director'} (DIN: ${d?.din || '—'}), ${mode}`;
      });
      const otherEntries = absentees.map(name => `<span class="entry-name">${name}</span> — Leave of Absence granted`);
      if (md.cs_name) otherEntries.push(`<span class="entry-name">${md.cs_name}</span> — Company Secretary${md.cs_membership_no ? ` (Mem. No: ${md.cs_membership_no})` : ''}`);
      if (md.auditor_name) otherEntries.push(`<span class="entry-name">${md.auditor_name}</span> — Invitee${md.auditor_firm_name ? `, M/s ${md.auditor_firm_name}` : ''}`);

      const renderBmMinutesItem = (i) => {
        const votes = i.resolution.votes || {};
        const { forWeight, againstWeight, abstainWeight } = tallyVotes(votes, attendees, true, shareholders);
        const outcome = i.resolution.status === 'Passed'
          ? (againstWeight === 0 ? 'CARRIED UNANIMOUSLY' : 'CARRIED by requisite majority')
          : i.resolution.status === 'Rejected' ? 'NOT CARRIED' : 'Result pending';
        return `
          <p class="item-title">Item No. ${i.itemNo}: ${cleanLegalTitle(i.resolution.resolution_title)}</p>
          <p class="procedural-narrative">The Chairman placed the matter before the Board for its consideration and, upon deliberation, the Board resolved as under:</p>
          ${i.isFinal
            ? splitResolutionParagraphs(i.text).map(p => `<p class="resolution-quote">"${formatResolutionQuote(p)}"</p>`).join('')
            : '<p class="resolution-quote"><em>Draft — resolution not yet marked Passed.</em></p>'}
          ${(forWeight + againstWeight) > 0 ? `<p class="vote-result">Voting — For: ${forWeight}, Against: ${againstWeight}, Abstained: ${abstainWeight}. ${outcome}.</p>` : ''}
        `;
      };

      const headerHtml = buildLetterheadHtml(item);
      const html = `
        <p class="doc-title">Minutes of the Meeting of the Board of Directors</p>
        <p class="opening-narrative">The Meeting was held on ${meetingDay}, the ${meetingDateFmt}, at ${md.venue || '__________'}. The Meeting commenced at ${formatTime12h(md.meeting_time)}${md.meeting_end_time ? ` and concluded at ${formatTime12h(md.meeting_end_time)}` : ''}.</p>

        <table class="attendance-box"><thead><tr><th>Directors Present</th><th>Leave of Absence / Invitees Present</th></tr></thead>
          <tbody><tr>
            <td>${presentEntries.length ? presentEntries.map(e => `<p style="margin-bottom:4pt;">${e}</p>`).join('') : 'None recorded.'}</td>
            <td>${otherEntries.length ? otherEntries.map(e => `<p style="margin-bottom:4pt;">${e}</p>`).join('') : 'None recorded.'}</td>
          </tr></tbody>
        </table>

        <p class="proc-heading">1. Quorum</p>
        <p class="procedural-narrative">The Chairman noted that ${attendees.length} Director(s) were present, against a minimum quorum of ${quorumNeeded} required under Section 174(1) of the Companies Act, 2013 (one-third of the total strength of the Board, or two Directors, whichever is higher), and confirmed that ${quorumMet ? 'quorum was present and the Meeting was validly constituted' : 'quorum was NOT present'}.</p>

        <p class="proc-heading">2. Chairperson</p>
        <p class="procedural-narrative">${md.chairperson || '_____________________'} chaired the Meeting in accordance with the Articles of Association of the Company.</p>

        <p class="proc-heading">3. Confirmation of Previous Minutes</p>
        <p class="procedural-narrative">The Minutes of the preceding Meeting of the Board of Directors were tabled before the Board, confirmed as a true and correct record of the proceedings, and signed by the Chairman.</p>

        <p class="proc-heading">4. Disclosure of Interest (Section 184)</p>
        <p class="procedural-narrative">${md.interest_disclosure?.trim() || 'None of the Directors were concerned or interested, financially or otherwise, in any of the resolutions considered at the Meeting.'}</p>

        <p class="section-heading">Agenda</p>
        ${items.map(renderBmMinutesItem).join('') || '<p class="procedural-narrative">No agenda items linked to this meeting.</p>'}

        <p class="procedural-narrative" style="margin-top:10pt;">There being no further business, the Meeting concluded with a vote of thanks to the Chair.</p>

        <p class="minute-entry">Entered in the Minute Book. Place: ${md.signatory_place || item.registered_office || '__________'} &nbsp; Date: ${meetingDateFmt}</p>

        <div class="sig-block">
          <span class="sig-name">${md.chairperson || '_____________________'}</span><br/>
          Chairperson of the Meeting<br/>
          <span style="font-weight:normal;font-size:9.5pt;">Each page of these Minutes has been initialed, and this page has been signed, by the Chairperson, in accordance with Section 118 of the Companies Act, 2013 and Secretarial Standard-1.</span>
        </div>
      `;
      downloadMinutesStatutoryWord(`Minutes ${type} - ${item.company_name}`, html, headerHtml);
      return;
    }

    const isAGM = type === 'AGM';
    const attendees = md.attendees || [];
    const categories = md.attendee_categories || {};
    const proxies = md.proxies || [];
    const gmDirectorNames = md.gm_directors_present || [];

    const serial = isAGM ? await getMeetingSerialNumber('AGM', item.company_id) : null;
    const serialLabel = serial ? `${serial}${ordinal(serial)} ` : '';
    const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
    const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

    const quorumCount = attendees.length;
    const quorumRequired = generalMeetingQuorum(companyType, shareholders.length);
    const quorumMet = quorumCount >= quorumRequired;

    // Column 1: members/corp reps/proxies present. Column 2: directors/CS/
    // auditors present, or on leave of absence — same data the Attendance
    // Sheet already collects, just re-shaped into the Minutes' 2-column box.
    const memberEntries = attendees.map(name => {
      const sh = shareholders.find(s => s.member_name === name);
      const cat = ATTENDEE_CATEGORY_LABELS[categories[name] || 'self'];
      return `<span class="entry-name">${name}</span> — Folio ${sh?.folio_number || '—'}, ${sh?.no_of_shares || 0} shares (${cat})`;
    }).concat(proxies.map(p => `<span class="entry-name">${p.name}</span> — Proxy for Folio ${p.represents_folio || '—'} (${p.shares || 0} shares, Form MGT-11)`));

    const directorEntries = gmDirectorNames.map(name => {
      const d = directors.find(x => x.director_name === name);
      return `<span class="entry-name">${name}</span> — ${d?.designation || 'Director'} (DIN: ${d?.din || '—'})`;
    });
    if (md.cs_name) directorEntries.push(`<span class="entry-name">${md.cs_name}</span> — Company Secretary (Mem. No: ${md.cs_membership_no || '—'})`);
    if (md.auditor_name) directorEntries.push(`<span class="entry-name">${md.auditor_name}</span> — Statutory Auditor${md.auditor_firm_name ? `, M/s ${md.auditor_firm_name}` : ''} (FRN: ${md.auditor_frn || '—'})`);
    const absentDirectors = directors.filter(d => !gmDirectorNames.includes(d.director_name)).map(d => `<span class="entry-name">${d.director_name}</span> — Leave of Absence granted`);

    const ordinaryItems = items.filter(i => i.resolution.business_classification === 'ORDINARY_BUSINESS');
    const specialItems = items.filter(i => i.resolution.business_classification !== 'ORDINARY_BUSINESS');
    const renderMinutesItem = (i) => {
      const votes = i.resolution.votes || {};
      const { forWeight, againstWeight, abstainWeight } = tallyVotes(votes, attendees, false, shareholders);
      const outcome = i.resolution.status === 'Passed'
        ? (againstWeight === 0 ? 'CARRIED UNANIMOUSLY' : (i.resolution.resolution_type === 'SPECIAL' ? 'CARRIED by the requisite supermajority' : 'CARRIED by requisite majority'))
        : i.resolution.status === 'Rejected' ? 'NOT CARRIED' : 'Result pending';
      return `
        <p class="item-title">Item No. ${i.itemNo}: ${cleanLegalTitle(i.resolution.resolution_title)}</p>
        <p class="procedural-narrative">The following resolution was placed before the Meeting and, upon deliberation, the Members present resolved as under:</p>
        ${i.isFinal
          ? splitResolutionParagraphs(i.text).map(p => `<p class="resolution-quote">"${formatResolutionQuote(p)}"</p>`).join('')
          : '<p class="resolution-quote"><em>Draft — resolution not yet marked Passed.</em></p>'}
        ${(forWeight + againstWeight) > 0 ? `<p class="vote-result">Voting by poll — For: ${forWeight}, Against: ${againstWeight}, Abstained: ${abstainWeight}. ${outcome}.</p>` : ''}
      `;
    };

    const headerHtml = buildLetterheadHtml(item);
    const html = `
      <p class="doc-title">Minutes of the ${serialLabel}${isAGM ? 'Annual General Meeting' : 'Extraordinary General Meeting'}</p>
      <p class="opening-narrative">The Meeting was held on ${meetingDay}, the ${meetingDateFmt}, at ${md.venue || '__________'}. The Meeting commenced at ${formatTime12h(md.meeting_time)}${md.meeting_end_time ? ` and concluded at ${formatTime12h(md.meeting_end_time)}` : ''}.</p>

      <table class="attendance-box"><thead><tr><th>Members / Corporate Representatives / Proxies Present</th><th>Directors, KMP &amp; Auditors Present</th></tr></thead>
        <tbody><tr>
          <td>${memberEntries.length ? memberEntries.map(e => `<p style="margin-bottom:4pt;">${e}</p>`).join('') : 'None recorded.'}</td>
          <td>${directorEntries.length ? directorEntries.map(e => `<p style="margin-bottom:4pt;">${e}</p>`).join('') : 'None recorded.'}${absentDirectors.length ? absentDirectors.map(e => `<p style="margin-bottom:4pt;">${e}</p>`).join('') : ''}</td>
        </tr></tbody>
      </table>

      <p class="proc-heading">1. Quorum</p>
      <p class="procedural-narrative">The Chairperson noted that ${quorumCount} Member(s)/Authorised Representative(s) were present in person, against a minimum quorum of ${quorumRequired} required under Section 103(1) of the Companies Act, 2013, and confirmed that ${quorumMet ? 'quorum was present and the Meeting was validly constituted' : 'quorum was NOT present'}.</p>

      <p class="proc-heading">2. Chairperson</p>
      <p class="procedural-narrative">${md.chairperson || '_____________________'} chaired the Meeting in accordance with the Articles of Association of the Company.</p>

      ${ordinaryItems.length ? `<p class="section-heading">Ordinary Business</p>${ordinaryItems.map(renderMinutesItem).join('')}` : ''}
      ${specialItems.length ? `<p class="section-heading">Special Business</p>${specialItems.map(renderMinutesItem).join('')}` : ''}

      <p class="procedural-narrative" style="margin-top:10pt;">There being no further business, the Meeting concluded with a vote of thanks to the Chair.</p>

      <p class="minute-entry">Entered in the Minute Book. Place: ${md.signatory_place || item.registered_office || '__________'} &nbsp; Date: ${meetingDateFmt}</p>

      <div class="sig-block">
        <span class="sig-name">${md.chairperson || '_____________________'}</span><br/>
        Chairperson of the Meeting
      </div>
    `;
    downloadMinutesStatutoryWord(`Minutes ${type} - ${item.company_name}`, html, headerHtml);
  };

  const downloadAttendanceSheetDoc = async (meetingId, type) => {
    const md = meetingState[meetingId] || {};

    // ── Board Meeting Attendance Sheet — Sec 174(1) quorum, same statutory
    // engine as AGM/EGM (header/title/table typography), with BM-specific
    // sections: A. Directors (no members/proxies/corporate-rep concept —
    // that's Sec 105/112/113, general-meeting-only), B. Company Secretary
    // & Invitees/Auditors. ──
    if (type === 'BM') {
      const attendees = md.attendees || [];
      const mode = md.venue_mode === 'vc' ? 'VC' : 'Physical';
      const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
      const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
      const quorumRequired = boardQuorum(directors.length);
      const quorumMet = attendees.length >= quorumRequired;

      const directorRows = directors.map((d, i) => ({
        i, name: d.director_name, din: d.din || '—', designation: d.designation || 'Director',
        present: attendees.includes(d.director_name),
      }));
      const inviteeRows = [];
      if (md.cs_name) inviteeRows.push({ name: md.cs_name, rep: 'Company Secretary', ref: md.cs_membership_no ? `Mem. No: ${md.cs_membership_no}` : '—' });
      if (md.auditor_name) inviteeRows.push({ name: md.auditor_name, rep: md.auditor_firm_name ? `Invitee, M/s ${md.auditor_firm_name}` : 'Invitee', ref: md.auditor_frn ? `FRN: ${md.auditor_frn}` : '—' });

      const headerHtml = buildLetterheadHtml(item);
      const html = `
        <p class="doc-title">Attendance Sheet of the Meeting of the Board of Directors</p>

        <table class="meta-table"><tbody>
          <tr><td class="label">Day &amp; Date</td><td>${meetingDay}, ${meetingDateFmt}</td><td class="label">Time of Meeting</td><td>${formatTime12h(md.meeting_time)}</td></tr>
          <tr><td class="label">Venue</td><td colspan="3">${md.venue || '—'}</td></tr>
        </tbody></table>

        <p class="section-heading">A. Attendance of Directors</p>
        <table class="att-table">
          <thead><tr><th style="width:5%;">S. No.</th><th>Name of Director</th><th style="width:16%;">DIN</th><th style="width:22%;">Designation</th><th style="width:14%;">Mode of Presence</th><th class="sig">Signature</th></tr></thead>
          <tbody>
            ${directorRows.map((d, i) => `<tr><td class="num">${i + 1}.</td><td>${d.name}</td><td>${d.din}</td><td>${d.designation}</td><td class="num">${d.present ? mode : 'Absent'}</td><td></td></tr>`).join('') || '<tr><td colspan="6">No directors on record.</td></tr>'}
          </tbody>
        </table>

        <p class="section-heading">B. Company Secretary &amp; Invitees / Auditors Present</p>
        <table class="att-table">
          <thead><tr><th style="width:5%;">S. No.</th><th>Name</th><th>Representation / Capacity</th><th style="width:20%;">Membership / FRN</th><th class="sig">Signature</th></tr></thead>
          <tbody>
            ${inviteeRows.map((r, i) => `<tr><td class="num">${i + 1}.</td><td>${r.name}</td><td>${r.rep}</td><td>${r.ref}</td><td></td></tr>`).join('') || '<tr><td colspan="5">None recorded.</td></tr>'}
          </tbody>
        </table>

        <div class="quorum-block">
          <p class="qb-title">Quorum Verification &amp; Chairman Confirmation</p>
          <p><b>Quorum Determination (Section 174(1)):</b></p>
          <p>Total Directors Present: <b>${attendees.length}</b></p>
          <p>Minimum Quorum Required (one-third of total strength, or 2, whichever is higher): <b>${quorumRequired}</b></p>
          <p>Status: <b>${quorumMet ? 'Quorum Present and Meeting Validly Constituted' : 'Quorum NOT Present'}</b></p>
        </div>

        <div class="sig-block">
          <span class="sig-name">${md.chairperson || '_____________________'}</span><br/>
          Chairperson of the Meeting<br/>
          Date: ${meetingDateFmt}
        </div>
      `;
      downloadAttendanceStatutoryWord(`Attendance Sheet ${type} - ${item.company_name}`, html, headerHtml);
      return;
    }

    const attendees = md.attendees || [];
    const categories = md.attendee_categories || {};
    const proxies = md.proxies || [];
    const memberRows = attendees.map(name => {
      const sh = shareholders.find(s => s.member_name === name);
      const shares = Number(sh?.no_of_shares) || 0;
      return {
        name, folio: sh?.folio_number || sh?.dp_id || '—',
        category: ATTENDEE_CATEGORY_LABELS[categories[name] || 'self'],
        shares, pct: totalMemberWeight > 0 ? (shares / totalMemberWeight) * 100 : 0,
      };
    });
    const proxyRows = proxies.map(p => ({
      name: p.name, folio: p.represents_folio || '—', category: 'Proxy (Form MGT-11)',
      shares: Number(p.shares) || 0, pct: totalMemberWeight > 0 ? ((Number(p.shares) || 0) / totalMemberWeight) * 100 : 0,
    }));
    const allSectionARows = [...memberRows, ...proxyRows];
    const totalShares = allSectionARows.reduce((sum, r) => sum + r.shares, 0);
    const totalPct = totalMemberWeight > 0 ? (totalShares / totalMemberWeight) * 100 : 0;

    const gmDirectorNames = md.gm_directors_present || [];
    const directorRows = gmDirectorNames.map(name => {
      const d = directors.find(x => x.director_name === name);
      return { name, din: d?.din || '—', designation: d?.designation || 'Director' };
    });
    const mode = md.venue_mode === 'vc' ? 'VC' : 'Physical';

    const serial = type === 'AGM' ? await getMeetingSerialNumber('AGM', item.company_id) : null;
    const serialLabel = serial ? `${serial}${ordinal(serial)}` : '';
    const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
    const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

    const quorumCount = attendees.length; // Sec 103/112/113 — self/corp-rep/nominee all count; proxies (Sec 105) never do
    const quorumRequired = generalMeetingQuorum(companyType, shareholders.length);
    const quorumMet = quorumCount >= quorumRequired;
    const chairpersonDin = directors.find(d => d.director_name === md.chairperson)?.din || '—';

    const headerHtml = buildLetterheadHtml(item);
    const html = `
      <p class="doc-title">Attendance Sheet of the ${serialLabel ? `${serialLabel} ` : ''}${type === 'AGM' ? 'Annual General Meeting' : 'Extraordinary General Meeting'}</p>

      <table class="meta-table"><tbody>
        <tr><td class="label">Day &amp; Date</td><td>${meetingDay}, ${meetingDateFmt}</td><td class="label">Time of Meeting</td><td>${formatTime12h(md.meeting_time)}</td></tr>
        <tr><td class="label">Venue</td><td colspan="3">${md.venue || '—'}</td></tr>
        <tr><td class="label">Financial Year</td><td colspan="3">${item.financial_year || '—'}</td></tr>
      </tbody></table>

      <p class="section-heading">A. Attendance of Members / Corporate Representatives / Proxies</p>
      <table class="att-table">
        <thead><tr>
          <th style="width:5%;">S. No.</th><th>Name of Member / Authorised Rep / Proxy</th><th>Registered Folio No. / DP ID &amp; Client ID</th>
          <th style="width:16%;">Category</th><th style="width:10%;">No. of Equity Shares Held</th><th style="width:9%;">% of Total Capital</th><th class="sig">Signature</th>
        </tr></thead>
        <tbody>
          ${allSectionARows.map((r, i) => `<tr><td class="num">${i + 1}.</td><td>${r.name}</td><td>${r.folio}</td><td>${r.category}</td><td class="num">${r.shares}</td><td class="num">${r.pct.toFixed(2)}%</td><td></td></tr>`).join('') || '<tr><td colspan="7">No members marked present yet.</td></tr>'}
          ${allSectionARows.length > 0 ? `<tr class="total-row"><td colspan="4" class="num">TOTAL</td><td class="num">${totalShares}</td><td class="num">${totalPct.toFixed(2)}%</td><td></td></tr>` : ''}
        </tbody>
      </table>

      <p class="section-heading">B. Attendance of Directors &amp; Key Managerial Personnel (KMP)</p>
      <table class="att-table">
        <thead><tr><th style="width:5%;">S. No.</th><th>Name of Director / Official</th><th style="width:16%;">DIN / PAN</th><th style="width:22%;">Designation</th><th style="width:14%;">Mode of Presence</th><th class="sig">Signature</th></tr></thead>
        <tbody>
          ${directorRows.map((d, i) => `<tr><td class="num">${i + 1}.</td><td>${d.name}</td><td>${d.din}</td><td>${d.designation}</td><td class="num">${mode}</td><td></td></tr>`).join('')}
          ${md.cs_name ? `<tr><td class="num">${directorRows.length + 1}.</td><td>${md.cs_name}</td><td>${md.cs_membership_no || '—'}</td><td>Company Secretary</td><td class="num">${mode}</td><td></td></tr>` : ''}
          ${directorRows.length === 0 && !md.cs_name ? '<tr><td colspan="6">No directors/KMP marked present yet.</td></tr>' : ''}
        </tbody>
      </table>

      <p class="section-heading">C. Attendance of Auditors &amp; Special Invitees</p>
      <table class="att-table">
        <thead><tr><th style="width:5%;">S. No.</th><th>Name of Invitee / Auditor</th><th>Representation / Firm Name</th><th style="width:20%;">Membership / FRN</th><th class="sig">Signature</th></tr></thead>
        <tbody>
          ${md.auditor_name ? `<tr><td class="num">1.</td><td>${md.auditor_name}</td><td>${md.auditor_firm_name ? `M/s ${md.auditor_firm_name}, Statutory Auditors` : 'Statutory Auditors'}</td><td>${md.auditor_frn ? `FRN: ${md.auditor_frn}` : '—'}</td><td></td></tr>` : ''}
          ${md.scrutinizer_name ? `<tr><td class="num">${md.auditor_name ? 2 : 1}.</td><td>${md.scrutinizer_name}</td><td>Scrutinizer / Secretarial Auditor</td><td>${md.scrutinizer_mem_no ? `CP / Mem No: ${md.scrutinizer_mem_no}` : '—'}</td><td></td></tr>` : ''}
          ${!md.auditor_name && !md.scrutinizer_name ? '<tr><td colspan="5">No auditors/special invitees recorded.</td></tr>' : ''}
        </tbody>
      </table>

      <div class="quorum-block">
        <p class="qb-title">Quorum Verification &amp; Chairman Confirmation</p>
        <p><b>Quorum Determination (Section 103):</b></p>
        <p>Total Members Personally Present / Authorised Representatives u/s 113: <b>${quorumCount}</b></p>
        <p>Minimum Quorum Required under Section 103(1): <b>${quorumRequired}</b></p>
        <p>Status: <b>${quorumMet ? 'Quorum Present and Meeting Validly Constituted' : 'Quorum NOT Present'}</b></p>
      </div>

      <div class="sig-block">
        <span class="sig-name">${md.chairperson || '_____________________'}</span><br/>
        Chairperson of the Meeting / Authorised Director<br/>
        DIN: ${chairpersonDin}<br/>
        Date: ${meetingDateFmt}
      </div>
    `;
    downloadAttendanceStatutoryWord(`Attendance Sheet ${type} - ${item.company_name}`, html, headerHtml);
  };

  const downloadMeetingCtcDoc = async (meetingId, type, onlySelected) => {
    const items = getMeetingAgendaItems(meetingId);
    const md = meetingState[meetingId] || {};
    const selection = md.ctc_selection || {};
    const chosen = onlySelected ? items.filter(i => selection[i.itemNo]) : items;
    if (chosen.length === 0) { setAlert({ message: 'Select at least one resolution to include in the CTC.', title: 'Nothing selected' }); return; }
    const signatoryName = md.signatory_name || md.chairperson || '';
    const signatoryRole = md.signatory_role || 'Director';
    const signatoryMeta = md.signatory_din_or_membership
      ? (signatoryRole === 'Company Secretary' ? `Membership No: ${md.signatory_din_or_membership}` : `DIN: ${md.signatory_din_or_membership}`)
      : '';

    const isBM = type === 'BM';
    // Only AGMs are serially numbered (one per financial year) — Board
    // Meetings and EGMs are not counted, per the user's explicit instruction.
    const serial = type === 'AGM' ? await getMeetingSerialNumber('AGM', item.company_id) : null;
    const serialLabel = serial ? `${serial}${ordinal(serial)} ` : '';
    const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
    const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

    // Explicit Ordinary/Special Resolution designation (SS-2) — if every
    // chosen item shares one resolution_type it's stated once in the
    // preamble; a mixed batch instead states it per item heading, since
    // one blanket "ORDINARY/SPECIAL" wouldn't be accurate for all of them.
    const distinctTypes = [...new Set(chosen.map(i => i.resolution.resolution_type))];
    const singleType = distinctTypes.length === 1 ? distinctTypes[0] : null;
    const typeLabel = (t) => t === 'SPECIAL' ? 'Special' : 'Ordinary';

    // The preamble is always rendered in caps, per the user's explicit
    // instruction, for every meeting type (BM/AGM/EGM) — done by upper-
    // casing the assembled sentence rather than the individual fields, so
    // it stays correct regardless of how the underlying data is cased.
    const preamble = (isBM
      ? `Certified true copy of the resolution${chosen.length > 1 ? 's' : ''} passed by the Board of Directors of ${item.company_name} (CIN: ${item.cin_llpin || '—'}) at the ${serialLabel}Meeting of the Board of Directors of the Company held on ${meetingDay}, the ${meetingDateFmt}, commencing at ${formatTime12h(md.meeting_time)}${md.meeting_end_time ? ` and concluding at ${formatTime12h(md.meeting_end_time)}` : ''}, at ${md.venue || '__________'}.`
      : `Certified true copy of the ${singleType ? `<b>${typeLabel(singleType)} Resolution</b>` : 'resolution(s)'} passed at the ${serialLabel}${type === 'AGM' ? 'Annual General Meeting' : 'Extraordinary General Meeting'} of the Members of ${item.company_name} (CIN: ${item.cin_llpin || '—'}) held on ${meetingDay}, the ${meetingDateFmt}, at ${formatTime12h(md.meeting_time)}, at the Registered Office situated at ${md.venue || item.registered_office || '__________'}.`
    ).toUpperCase();

    // CTC of AGM/EGM resolutions must state ORDINARY BUSINESS / SPECIAL
    // BUSINESS (Sec 102 classification) exactly as the Notice does via its
    // section headings — this is independent of the Ordinary/Special
    // Resolution (Sec 114 voting-threshold) label already shown per item.
    // A Board Meeting has no such split (Sec 102 is a general-meeting-only
    // concept), so it keeps a single flat agenda list.
    const renderCtcItem = (i) => `
      <p class="item-title">Item No. ${i.itemNo}: ${cleanLegalTitle(i.resolution.resolution_title)}${!isBM && !singleType ? ` (${typeLabel(i.resolution.resolution_type)} Resolution)` : ''}</p>
      ${i.isFinal
        ? splitResolutionParagraphs(i.text).map(p => `<p class="resolution-quote">"${formatResolutionQuote(p)}"</p>`).join('')
        : '<p class="resolution-quote"><em>Not yet finalized.</em></p>'}
    `;
    const ordinaryChosen = !isBM ? chosen.filter(i => i.resolution.business_classification === 'ORDINARY_BUSINESS') : [];
    const specialChosen = !isBM ? chosen.filter(i => i.resolution.business_classification !== 'ORDINARY_BUSINESS') : [];

    const headerHtml = buildLetterheadHtml(item);
    const html = `
      <p class="doc-title">Certified True Copy of Resolution${chosen.length > 1 ? 's' : ''}</p>
      <p class="preamble">${preamble}</p>

      ${isBM
        ? chosen.map(renderCtcItem).join('')
        : `${ordinaryChosen.length ? `<p class="section-heading">Ordinary Business</p>${ordinaryChosen.map(renderCtcItem).join('')}` : ''}${specialChosen.length ? `<p class="section-heading">Special Business</p>${specialChosen.map(renderCtcItem).join('')}` : ''}`}

      <p class="ctc-banner">CERTIFIED TRUE COPY</p>

      <div class="sig-block">
        <p class="sig-for">For ${item.company_name}</p>
        <div class="sig-name-block">
          _____________________<br/>
          <span class="sig-name">${signatoryName || '__________'}</span><br/>
          ${signatoryRole}
        </div>
        ${signatoryMeta ? `<p class="sig-meta">${signatoryMeta}</p>` : ''}
        <p class="sig-meta">Place: ${md.signatory_place || item.registered_office || '__________'} &nbsp; Date: ${meetingDateFmt}</p>
      </div>
    `;
    downloadCtcStatutoryWord(`CTC ${type} - ${item.company_name}`, html, headerHtml);
  };

  // GM (AGM/EGM) shorter-notice consent is given by MEMBERS, in their
  // capacity as shareholders — not by Directors, and not as a single
  // tabular sheet. Each consenting member's consent is its own signed
  // letter (Sec 101(1) + SS-2), so this generates one letter per
  // consenting member, paginated into a single Word file. BM consent
  // stays a Director-capacity sheet since Sec 173(3) is a different
  // provision with no shareholding-percentage requirement.
  const downloadShorterConsentDoc = async (meetingId, type) => {
    const md = meetingState[meetingId] || {};
    const isBM = type === 'BM';
    const consenters = md.shorter_consent || md.attendees || [];
    // ── Board Meeting shorter-notice consent — Sec 173(3) proviso + SS-1
    // Para 1.3.11. Consent is given by Directors (not Members — that's the
    // GM-only Sec 101(1) provision below), recorded as one certificate the
    // consenting Directors sign together, since the Board is one body
    // acting collectively — unlike GM consent, which the law frames as
    // each Member's individual act and so is drafted as separate letters.
    if (isBM) {
      const majorityRequired = Math.floor(directors.length / 2) + 1;
      const consentedCount = consenters.length;
      const majorityMet = consentedCount >= majorityRequired;
      const ids = directors.filter(d => d.is_independent_director);
      const idPresent = ids.some(d => consenters.includes(d.director_name));
      const statusLine = ids.length === 0
        ? 'The Company has no Independent Directors on its Board; consent to the shorter notice was accorded in the ordinary course by the majority of Directors set out below.'
        : idPresent
          ? `At least one Independent Director (${ids.filter(d => consenters.includes(d.director_name)).map(d => d.director_name).join(', ')}) has consented, in accordance with the proviso to Section 173(3) of the Companies Act, 2013 and SS-1 Para 1.3.11.`
          : 'No Independent Director has consented as of this record. Where the Company is required to have an Independent Director, the resolution(s) passed at a meeting held on shorter notice shall be final only on ratification by at least one Independent Director present at the meeting, or in his/her absence, by at least one Independent Director subsequently, in accordance with SS-1 Para 1.3.11.';
      const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

      const html = `
        <p class="doc-ref">THE COMPANIES ACT, 2013</p>
        <p class="doc-ref">[Pursuant to the proviso to Section 173(3) of the Companies Act, 2013 and Secretarial Standard-1]</p>
        <p class="doc-subtitle">Consent of Directors for Holding Board Meeting at Shorter Notice</p>

        <p class="meta">${item.company_name}</p>
        <p class="meta">CIN: ${item.cin_llpin || '—'}</p>
        <p class="meta">Regd. Office: ${item.registered_office || '—'}</p>

        <p class="body-text">We, the undersigned Directors of ${item.company_name}, being not less than the majority of the total number of Directors of the Company (${consentedCount} of ${directors.length} Directors, against a majority requirement of ${majorityRequired}), do hereby consent to the Meeting of the Board of Directors of the Company scheduled to be held on ${meetingDateFmt} at ${md.venue || '__________'} being convened at shorter notice than the seven days' notice in writing otherwise required under Section 173(3) of the Companies Act, 2013.</p>
        <p class="body-text">${statusLine}</p>
        <p class="body-text">${majorityMet ? 'This consent is accorded by not less than the majority of Directors, as required by law.' : 'This consent does NOT yet meet the majority-of-Directors threshold required by the proviso to Section 173(3) — additional Directors must consent before this Meeting can validly be held at shorter notice.'}</p>

        <table class="sig-table"><tbody>
          <tr><td class="sig-label" style="width:40%;font-weight:bold;">Director</td><td style="font-weight:bold;">Consented</td><td style="font-weight:bold;">Signature</td></tr>
          ${directors.map(d => `<tr><td>${d.director_name}${d.is_independent_director ? ' (Independent Director)' : ''}${d.din ? `<br/><span style="font-weight:normal;font-size:9.5pt;">DIN: ${d.din}</span>` : ''}</td><td>${consenters.includes(d.director_name) ? 'Yes' : '—'}</td><td></td></tr>`).join('') || '<tr><td colspan="3">No directors on record.</td></tr>'}
        </tbody></table>

        <p class="sig-label" style="margin-top:20pt;">Date: ${md.notice_date ? new Date(md.notice_date).toLocaleDateString('en-IN') : '__________'} &nbsp; Place: ${md.signatory_place || item.registered_office || '__________'}</p>
      `;
      downloadStatutoryWord(`Shorter Notice Consent BM - ${item.company_name}`, html);
      return;
    }

    const consentingMembers = shareholders.filter(s => consenters.includes(s.member_name));
    if (consentingMembers.length === 0) {
      setAlert({ message: 'No members are marked as consenting yet — tick them above first.', title: 'Nothing to generate' });
      return;
    }

    const isAGM = type === 'AGM';
    const serial = isAGM ? await getMeetingSerialNumber('AGM', item.company_id) : null;
    const serialLabel = serial ? `${serial}${ordinal(serial)} ` : '';
    const meetingDay = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' }) : '—';
    const meetingDateFmt = md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

    const docTitle = isAGM
      ? 'Consent by Shareholder / Member for Holding Annual General Meeting at Shorter Notice'
      : 'Consent by Shareholder / Member for Holding Extra-Ordinary General Meeting at Shorter Notice';
    const subject = isAGM
      ? `Consent for convening the ${serialLabel}Annual General Meeting of ${item.company_name} at Shorter Notice`
      : `Consent for convening an Extra-Ordinary General Meeting ("EGM") of ${item.company_name} at Shorter Notice`;
    const holdPhrase = isAGM ? `hold the ${serialLabel}Annual General Meeting of the Company` : 'hold the Extra-Ordinary General Meeting of the Company';
    const trailingClause = isAGM ? '' : ', to transact the Special Business as set out in the draft Notice of the EGM';

    const letters = consentingMembers.map(s => {
      const shares = Number(s.no_of_shares) || 0;
      const faceValue = Number(s.face_value) || 10;
      const pct = totalMemberWeight > 0 ? (shares / totalMemberWeight) * 100 : 0;
      const folioOrClientId = s.folio_number || s.dp_id || '—';
      return `
        <div class="letter-page">
          <p class="doc-ref">THE COMPANIES ACT, 2013</p>
          <p class="doc-ref">[Pursuant to Section 101(1) of the Companies Act, 2013 and Secretarial Standard-2]</p>
          <p class="doc-subtitle">${docTitle}</p>

          <p class="meta">To,</p>
          <p class="meta">The Board of Directors,</p>
          <p class="meta">${item.company_name}</p>
          <p class="meta">CIN: ${item.cin_llpin || '—'}</p>
          <p class="meta">Regd. Office: ${item.registered_office || '—'}</p>

          <p class="subject">Subject: ${subject}</p>

          <p class="body-text">Dear Sir(s) / Madam(s),</p>
          <p class="body-text">I / We, <b>${s.member_name}</b>, residing at / having registered office at ${s.address || '__________'}, holding ${shares} (${numberToWordsIndian(shares)}) Equity Shares of INR ${faceValue}/- (Indian Rupees ${numberToWordsIndian(faceValue)} only) each, fully paid-up, representing ${pct.toFixed(2)}% of the total paid-up equity share capital carrying voting rights of ${item.company_name} ("the Company"), registered under Folio No. / DP ID &amp; Client ID: ${folioOrClientId}, do hereby give my / our consent pursuant to the proviso to Section 101(1) of the Companies Act, 2013 read with Secretarial Standard-2 on General Meetings, to ${holdPhrase} on shorter notice on ${meetingDay}, the ${meetingDateFmt} at ${formatTime12h(md.meeting_time)} at ${md.venue || '__________'} or any adjournment thereof${trailingClause}.</p>
          <p class="body-text">I / We hereby confirm that this consent shall be treated as valid consent in writing under the provisions of the Companies Act, 2013.</p>
          <p class="body-text">Thanking you,</p>
          <p class="body-text">Yours faithfully,</p>

          <table class="sig-table"><tbody>
            <tr><td colspan="2">____________________________________________</td></tr>
            <tr><td colspan="2">Signature of the Member / Authorised Signatory</td></tr>
            <tr><td colspan="2" style="height:10pt;"></td></tr>
            <tr><td class="sig-label">Name of Member</td><td>: ${s.member_name}</td></tr>
            <tr><td class="sig-label">Folio No. / DP-Client ID</td><td>: ${folioOrClientId}</td></tr>
            <tr><td class="sig-label">No. of Shares Held</td><td>: ${shares}</td></tr>
            <tr><td class="sig-label">Percentage of Holding</td><td>: ${pct.toFixed(2)}%</td></tr>
            <tr><td colspan="2" style="height:10pt;"></td></tr>
            <tr><td class="sig-label">Date</td><td>: ${md.notice_date ? new Date(md.notice_date).toLocaleDateString('en-IN') : '__________'}</td></tr>
            <tr><td class="sig-label">Place</td><td>: __________</td></tr>
          </tbody></table>
        </div>
      `;
    }).join('');

    downloadStatutoryWord(`Shorter Notice Consent ${type} - ${item.company_name}`, letters);
  };

  const toggleShorterConsent = (meetingId, name) => {
    const current = meetingState[meetingId]?.shorter_consent || meetingState[meetingId]?.attendees || [];
    const next = current.includes(name) ? current.filter(n => n !== name) : [...current, name];
    updateMeeting(meetingId, { shorter_consent: next });
  };
  const toggleCtcSelection = (meetingId, itemNo) => {
    const current = meetingState[meetingId]?.ctc_selection || {};
    updateMeeting(meetingId, { ctc_selection: { ...current, [itemNo]: !current[itemNo] } });
  };
  const setAttendeeCategory = (meetingId, name, category) => {
    const current = meetingState[meetingId]?.attendee_categories || {};
    updateMeeting(meetingId, { attendee_categories: { ...current, [name]: category } });
  };
  const toggleGmDirectorPresent = (meetingId, name) => {
    const current = meetingState[meetingId]?.gm_directors_present || [];
    const next = current.includes(name) ? current.filter(n => n !== name) : [...current, name];
    updateMeeting(meetingId, { gm_directors_present: next });
  };
  const updateProxyDraft = (meetingId, patch) => setProxyDraft(prev => ({ ...prev, [meetingId]: { ...prev[meetingId], ...patch } }));
  const addProxy = (meetingId) => {
    const draft = proxyDraft[meetingId];
    if (!draft?.name?.trim()) return;
    const current = meetingState[meetingId]?.proxies || [];
    updateMeeting(meetingId, { proxies: [...current, { id: `proxy_${Date.now()}`, name: draft.name.trim(), represents_folio: draft.folio || '', shares: draft.shares || 0 }] });
    setProxyDraft(prev => ({ ...prev, [meetingId]: { name: '', folio: '', shares: '' } }));
  };
  const removeProxy = (meetingId, proxyId) => {
    const current = meetingState[meetingId]?.proxies || [];
    updateMeeting(meetingId, { proxies: current.filter(p => p.id !== proxyId) });
  };

  // ═══════════════════════════════════════════════════════════════════════
  // STATUTORY REGISTER SYNC — manual, per meeting (mirrors EventFilings.js's
  // own register sync exactly, so a Board Meeting shared between an Event
  // Filing case and this one lands in the same rich shape either way).
  // Writes the FULL register row (serial number, day, notice date/mode,
  // strength, quorum, chairman) plus, for a Board Meeting, per-director
  // Attendance rows and a full Agenda row per resolution (with vote
  // tallies) — not just the bare date/venue the old auto-sync wrote. If a
  // register row already exists for this company+date, the user is asked
  // whether to update it or record a separate new row.
  //
  // Never syncs at all until every resolution tied to that meeting has
  // actually been Passed/Rejected on the Resolutions tab — logging a
  // meeting as "Held" before its business concluded isn't something Sync
  // can undo once it's in the register.
  // ═══════════════════════════════════════════════════════════════════════
  const requestRegisterSync = (which) => {
    const meetingId = which === 'board' ? item.board_meeting_id : item.agm_meeting_id;
    const type = which === 'board' ? 'BM' : 'AGM';
    if (!meetingId) return;
    const md = meetingState[meetingId] || {};
    const label = which === 'board' ? 'Board Meeting' : 'AGM';
    if (!md.meeting_date) {
      setAlert({ variant: 'error', title: 'Incomplete', message: 'Set the meeting date in Meeting Particulars first.' });
      return;
    }
    const linked = resolutions.filter(r => r.meeting_id === meetingId);
    if (linked.length === 0 || linked.some(r => r.status === 'Pending')) {
      setAlert({ variant: 'error', title: `${label} Not Synced`, message: `Pass (or reject) this meeting's resolution(s) on the Resolutions tab first.` });
      return;
    }
    const attendees = md.attendees || [];
    if (attendees.length === 0) {
      setConfirm({
        title: 'No one marked present',
        message: `Nobody is ticked as present for this ${type === 'BM' ? 'Board Meeting' : 'meeting'} in Meeting Particulars, so it will sync with 0 attendance and quorum not met. Continue anyway, or go back and mark attendance first?`,
        confirmLabel: 'Sync Anyway',
        onCancel: () => setConfirm(null),
        onConfirm: () => { setConfirm(null); checkAndSyncRegister(which, meetingId, type); },
      });
      return;
    }
    checkAndSyncRegister(which, meetingId, type);
  };

  const checkAndSyncRegister = async (which, meetingId, type) => {
    setSyncingWhich(which);
    try {
      const md = meetingState[meetingId] || {};
      const listUrl = type === 'BM'
        ? `/api/mca/companies/${item.company_id}/board-meetings`
        : `/api/mca/companies/${item.company_id}/general-meetings`;
      const list = await fetchJson(listUrl);
      const targetDate = String(md.meeting_date).slice(0, 10);
      const rows = Array.isArray(list) ? list : [];
      const match = rows.find(m => String(m.meeting_date).slice(0, 10) === targetDate && (type === 'BM' || m.meeting_type === type));
      if (match) {
        setSyncingWhich(null);
        setRegisterSyncChoice({ which, meetingId, type, existing: match });
      } else {
        // Serial number continues on from whatever's already in this
        // company's register for this meeting type, so it doesn't need
        // to be typed in by hand.
        await performRegisterSync(which, meetingId, type, null, rows.length + 1);
      }
    } catch (e) {
      setAlert({ variant: 'error', title: 'Sync failed', message: 'Could not check the Statutory Register for an existing entry.' });
      setSyncingWhich(null);
    }
  };

  const performRegisterSync = async (which, meetingId, type, existing, serialHint) => {
    setSyncingWhich(which);
    setRegisterSyncChoice(null);
    try {
      const md = meetingState[meetingId] || {};
      const isBM = type === 'BM';
      const attendees = md.attendees || [];
      const mode = md.venue_mode === 'vc' ? 'Video Conference' : 'Physical';
      const meetingDay = new Date(md.meeting_date).toLocaleDateString('en-IN', { weekday: 'long' });
      const noticeDate = md.notice_date || (item.created_at ? String(item.created_at).slice(0, 10) : null);
      const serialNumber = existing ? existing.serial_number : serialHint;

      const payload = isBM ? {
        serial_number: serialNumber,
        meeting_day: meetingDay,
        status: 'Held',
        meeting_date: md.meeting_date,
        meeting_time: md.meeting_time || null,
        venue: md.venue || null,
        mode,
        notice_date: noticeDate,
        notice_mode: 'Physical',
        total_strength: directors.length,
        quorum_required: boardQuorum(directors.length),
        quorum_present: attendees.length >= boardQuorum(directors.length),
        chairman: md.chairperson || null,
        event_filing_meeting_id: meetingId,
      } : {
        serial_number: serialNumber,
        meeting_day: meetingDay,
        status: 'Held',
        meeting_type: type,
        meeting_date: md.meeting_date,
        meeting_time: md.meeting_time || null,
        venue: md.venue || null,
        mode,
        notice_date: noticeDate,
        notice_mode: 'Physical',
        total_members: shareholders.length,
        members_present: attendees.length,
        quorum_present: attendees.length >= generalMeetingQuorum(companyType, shareholders.length),
        event_filing_meeting_id: meetingId,
        chairman: md.chairperson || null,
        shorter_notice: !!md.shorter_notice,
      };

      const base = isBM
        ? `/api/mca/companies/${item.company_id}/board-meetings`
        : `/api/mca/companies/${item.company_id}/general-meetings`;
      const saved = existing
        ? await fetchJson(`${base}/${existing.id}`, { method: 'PUT', body: JSON.stringify({ ...existing, ...payload }) })
        : await fetchJson(base, { method: 'POST', body: JSON.stringify(payload) });

      if (isBM) {
        const existingAttendance = existing
          ? await fetchJson(`${base}/${saved.id}/attendance`).catch(() => [])
          : [];
        await Promise.all(directors.map(d => {
          const attended = attendees.includes(d.director_name);
          const existingRow = existingAttendance.find(a => a.director_id === d.id || a.director_name === d.director_name);
          return existingRow
            ? fetchJson(`${base}/${saved.id}/attendance/${existingRow.id}`, { method: 'PUT', body: JSON.stringify({ attended, mode_of_attendance: mode, din: d.din || null, designation: d.designation || 'Director' }) }).catch(() => {})
            : fetchJson(`${base}/${saved.id}/attendance`, { method: 'POST', body: JSON.stringify({ director_id: d.id, director_name: d.director_name, din: d.din || null, designation: d.designation || 'Director', mode_of_attendance: mode, attended }) }).catch(() => {});
        }));

        // Agenda — one row per resolution in this meeting's FULL agenda
        // (own case's resolutions plus any sibling case's — see
        // getMeetingAgendaItems above — since the register should reflect
        // everything actually discussed at this one physical meeting).
        const agendaItems = getMeetingAgendaItems(meetingId);
        const mgt14Applicable = filings.some(f => f.form === 'MGT-14');
        const existingAgenda = existing
          ? await fetchJson(`${base}/${saved.id}/agendas`).catch(() => [])
          : [];
        await Promise.all(agendaItems.map(i => {
          const votes = i.resolution.votes || {};
          const { forWeight, againstWeight, abstainWeight } = tallyVotes(votes, attendees, true, shareholders);
          const agendaPayload = {
            serial_number: i.itemNo,
            agenda_title: i.resolution.resolution_title,
            agenda_type: 'Resolution',
            resolution_required: true,
            resolution_type: i.resolution.resolution_type === 'SPECIAL' ? 'Special' : 'Ordinary',
            resolution_text: i.text || null,
            resolution_passed: i.resolution.status === 'Passed' ? true : i.resolution.status === 'Rejected' ? false : null,
            votes_for: forWeight,
            votes_against: againstWeight,
            votes_abstained: abstainWeight,
            mgt14_applicable: mgt14Applicable,
          };
          const existingRow = existingAgenda.find(a => a.serial_number === i.itemNo || a.agenda_title === i.resolution.resolution_title);
          return existingRow
            ? fetchJson(`${base}/${saved.id}/agendas/${existingRow.id}`, { method: 'PUT', body: JSON.stringify(agendaPayload) }).catch(() => {})
            : fetchJson(`${base}/${saved.id}/agendas`, { method: 'POST', body: JSON.stringify(agendaPayload) }).catch(() => {});
        }));
      }

      // Tell the backend to re-check the register and flip this case's
      // own status to Synced once every meeting it links is present there
      // — also picks up board_synced_at/agm_synced_at for the green "✓
      // Synced" badge / Status Panel immediately, no full page refetch.
      try {
        const refreshed = await fetchJson(`/api/mca/annual-filings/${item.id}/sync`, { method: 'POST' });
        setItem(prev => ({ ...prev, status: refreshed.status, synced_at: refreshed.synced_at, board_synced_at: refreshed.board_synced_at, agm_synced_at: refreshed.agm_synced_at }));
      } catch { /* non-fatal — the register write itself already succeeded */ }

      setAlert({
        variant: 'success',
        title: `${which === 'board' ? 'Board Meeting' : 'AGM'} Synced`,
        message: `${meetingLabel(type)} ${existing ? 'updated in' : 'recorded in'} the Statutory Register${isBM ? ', with attendance and agenda' : ''}. Register-only fields (meeting number, SRN, etc.) still need filling in directly there.`,
      });
    } catch (e) {
      setAlert({ variant: 'error', title: `${which === 'board' ? 'Board Meeting' : 'AGM'} Not Synced`, message: e.message || 'Could not sync this meeting to the register.' });
    } finally {
      setSyncingWhich(null);
    }
  };
  const doDelete = async () => {
    try { await fetchJson(`/api/mca/annual-filings/${item.id}`, { method: 'DELETE' }); onBack(); }
    catch (e) { setAlert({ variant: 'error', title: 'Delete failed', message: e.message }); }
  };

  if (loading) return <div className="py-24 text-center text-gray-400 text-sm">Loading case…</div>;

  const meetingList = [
    item.board ? { meetingId: item.board.id, type: 'BM' } : null,
    item.agm ? { meetingId: item.agm.id, type: 'AGM' } : null,
  ].filter(Boolean);

  // ── Status Panel rows (Overview tab) — same shape as Event Filing's ──
  const financialsOk = ['paid_up_capital', 'turnover', 'net_worth'].some(k => companyData[k] && String(companyData[k]).trim());
  const resolutionsPassedCount = resolutions.filter(r => r.status === 'Passed').length;
  const resolutionsOk = resolutions.length > 0 && resolutions.every(r => r.status !== 'Pending');
  const checklistDone = checklist.filter(c => c.status === 'Completed' || c.status === 'Not Applicable').length;
  const attachmentsOk = checklist.length === 0 || checklistDone === checklist.length;
  const filingsApprovedCount = filings.filter(f => f.status === 'Approved' || f.status === 'Not Applicable').length;
  const filingsOk = filings.length > 0 && filingsApprovedCount === filings.length;
  // Synced independently per meeting now — the register itself is the
  // source of truth (board_synced_at/agm_synced_at come from GET,
  // derived by checking mca_board_meetings/mca_general_meetings
  // directly), not a single combined flag. A meeting this case doesn't
  // even link doesn't get its own row at all.
  const boardSyncedOk = !!item.board_synced_at;
  const agmSyncedOk = !!item.agm_synced_at;
  const statusRows = [
    { label: 'Event Details', ok: financialsOk, detail: allPlaceholders.length ? `${filledCount}/${allPlaceholders.length} resolution fields filled` : (financialsOk ? 'Entered' : 'Not entered yet'), tab: 'financials' },
    { label: 'Resolutions', ok: resolutionsOk, detail: `${resolutionsPassedCount}/${resolutions.length} passed`, tab: 'resolutions' },
    { label: 'Attachments', ok: attachmentsOk, detail: checklist.length ? `${checklistDone}/${checklist.length} done` : 'None required', tab: 'checklist' },
    { label: 'Filings', ok: filingsOk, detail: `${filingsApprovedCount}/${filings.length} approved`, tab: 'forms' },
    ...(item.board_meeting_id ? [{ label: 'Board Meeting Register', ok: boardSyncedOk, detail: boardSyncedOk ? `Synced ${new Date(item.board_synced_at).toLocaleDateString('en-IN')}` : 'Pending — pass its resolution(s) then sync', tab: 'documents' }] : []),
    ...(item.agm_meeting_id ? [{ label: 'AGM Register', ok: agmSyncedOk, detail: agmSyncedOk ? `Synced ${new Date(item.agm_synced_at).toLocaleDateString('en-IN')}` : 'Pending — pass its resolution(s) then sync', tab: 'documents' }] : []),
  ];
  const completeCount = statusRows.filter(r => r.ok).length;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'financials', label: 'Event Details' },
    { key: 'resolutions', label: `Resolutions (${resolutions.length})` },
    { key: 'board-report', label: "Board Report" },
    { key: 'documents', label: `Documents (${meetingList.length})` },
    { key: 'checklist', label: `Attachments (${checklist.length})` },
    { key: 'forms', label: `Forms & Filing (${filings.length})` },
  ];

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300";
  const labelCls = "block text-xs font-semibold text-gray-500 mb-1";

  // Every OTHER case (either module) sharing any meeting this case uses —
  // same "Meeting Round" concept as EventFilings.js, surfaced here too so
  // an Event Filing folded into this case's Board Meeting/AGM is visible
  // and one click away, instead of only discoverable via the amber note
  // on the Resolutions tab.
  const allSiblingCases = Object.values(meetingSiblings).flat()
    .filter(c => !((c.module || 'event') === 'annual' && c.id === item.id))
    .filter((c, i, arr) => arr.findIndex(x => (x.module || 'event') === (c.module || 'event') && x.id === c.id) === i);

  return (
    <div className="space-y-5">
      <style>{DARK_STYLE_OVERRIDES}</style>
      {alert && <AlertModal {...alert} onClose={() => setAlert(null)} />}
      {confirm && <ConfirmModal {...confirm} />}
      {confirmDelete && (
        <ConfirmModal title="Delete Annual Filing?" message={`This will permanently remove the FY ${item.financial_year} case for ${item.company_name}.`}
          confirmLabel="Delete" onConfirm={() => { setConfirmDelete(false); doDelete(); }} onCancel={() => setConfirmDelete(false)} />
      )}
      {registerSyncChoice && (
        <RegisterSyncChoiceModal
          title="Existing register entry found"
          message={`${meetingLabel(registerSyncChoice.type)} on this date already has an entry in the Statutory Register. Update it with this meeting's details, or record a separate new row?`}
          onUpdate={() => performRegisterSync(registerSyncChoice.which, registerSyncChoice.meetingId, registerSyncChoice.type, registerSyncChoice.existing)}
          onCreateNew={() => performRegisterSync(registerSyncChoice.which, registerSyncChoice.meetingId, registerSyncChoice.type, null, (registerSyncChoice.existing?.serial_number || 0) + 1)}
          onCancel={() => setRegisterSyncChoice(null)}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <button onClick={onBack} className="group text-xs font-semibold text-gray-400 hover:text-[#0f1f45] mb-1.5 flex items-center gap-1.5 transition-colors">
            <span className="inline-flex transition-transform duration-200 group-hover:-translate-x-0.5">{Icon.back}</span>
            Back to Annual Filings
          </button>
          <h3 className="text-xl font-bold text-[#0f1f45]">{item.company_name} — FY {item.financial_year}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{item.cin_llpin || 'No CIN/LLPIN on file'}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-gray-400">{saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : saveStatus === 'error' ? 'Save failed' : ''}</span>
          {overdue && <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-100 text-red-700">{Icon.alert} Overdue</span>}
          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>
          <button onClick={() => setConfirmDelete(true)} title="Delete case" className="w-8 h-8 rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 flex items-center justify-center">{Icon.trash}</button>
        </div>
      </div>

      {allSiblingCases.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-600 mb-2">🗓 Meeting Round — this meeting covers {allSiblingCases.length + 1} case{allSiblingCases.length + 1 !== 1 ? 's' : ''}</p>
          <div className="flex flex-wrap gap-2">
            <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-700">FY {item.financial_year} Annual Filing (this case)</span>
            {allSiblingCases.map(c => {
              const targetPage = c.module === 'annual' ? 'annual-filings' : 'event-filings';
              return (
                <button key={`${c.module || 'event'}-${c.id}`}
                  onClick={() => onNavigate ? onNavigate(targetPage, { caseId: c.id }) : setAlert({ message: `Open this from ${c.module === 'annual' ? 'Annual' : 'Event'} Filings.`, title: 'Different case' })}
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200">
                  {c.event_name || c.title} · {c.status} ↗
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px ${activeTab === tab.key ? 'border-[#0f1f45] text-[#0f1f45]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-3 gap-5">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden col-span-3">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h4 className="text-base font-bold text-[#0f1f45]">Status Panel</h4>
                <p className="text-xs text-gray-400 mt-0.5">Click any row to jump straight there.</p>
              </div>
              <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${completeCount === statusRows.length ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                {completeCount} / {statusRows.length} complete
              </span>
            </div>
            <div className="divide-y divide-gray-50">
              {statusRows.map(row => (
                <button key={row.label} onClick={() => setActiveTab(row.tab)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 text-left">
                  <div className="flex items-center gap-3">
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${row.ok ? 'bg-green-500' : 'bg-amber-400'}`} />
                    <span className="text-sm font-medium text-gray-700">{row.label}</span>
                  </div>
                  <span className={`text-xs ${row.ok ? 'text-green-600' : 'text-amber-600'}`}>{row.detail}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 col-span-2">
            <h4 className="text-base font-bold text-[#0f1f45] mb-4">Stage Progress</h4>
            <div className="space-y-3">
              {STAGE_ORDER.map((stage, index) => {
                const currentIndex = status === 'Rejected' ? -1 : STAGE_ORDER.indexOf(status);
                const done = index < currentIndex;
                const current = index === currentIndex;
                return (
                  <div key={stage} className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${done ? 'bg-green-100 text-green-700' : current ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                      {done ? '✓' : index + 1}
                    </div>
                    <p className={`text-sm ${current ? 'font-semibold text-gray-800' : 'text-gray-500'}`}>{stage}</p>
                  </div>
                );
              })}
              {status === 'Rejected' && (
                <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-100">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-red-100 text-red-700">✕</div>
                  <p className="text-sm font-semibold text-red-600">One or more filings rejected — see Forms & Filing</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="text-base font-bold text-[#0f1f45] mb-4">Case Summary</h4>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-400">Company</span><span className="text-gray-700 font-medium">{item.company_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Financial Year</span><span className="text-gray-700 font-medium">FY {item.financial_year}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Board Meeting</span><span className="text-gray-700 font-medium">{item.board ? fmtDate(item.board.meeting_date) : 'Not linked'}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">AGM</span><span className="text-gray-700 font-medium">{item.agm ? fmtDate(item.agm.meeting_date) : 'Not linked'}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Resolutions</span><span className="text-gray-700 font-medium">{resolutions.length}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Forms</span><span className="text-gray-700 font-medium">{filings.map(f => f.form).join(', ') || '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Created</span><span className="text-gray-700 font-medium">{item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN') : '—'}</span></div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'financials' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 space-y-5">
          <div className="flex items-center gap-2 text-[11px]">
            <span className={`px-2 py-1 rounded-full font-semibold ${companyData.is_listed ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{companyData.is_listed ? 'Listed Company' : 'Not Listed'}</span>
            <span className={`px-2 py-1 rounded-full font-semibold ${companyData.has_subsidiaries ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{companyData.has_subsidiaries ? 'Has Subsidiaries/JV/Associates' : 'No Subsidiaries on file'}</span>
            <span className="text-gray-400">— set on Company Master, snapshotted when this case was created</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[['paid_up_capital', 'Paid-up Capital (₹)'], ['turnover', 'Turnover (₹)'], ['net_worth', 'Net Worth (₹)'], ['borrowings', 'Borrowings (₹)']].map(([key, label]) => (
              <div key={key}><label className={labelCls}>{label}</label><input type="number" className={inputCls} value={companyData[key] || ''} onChange={e => updateCompanyData({ [key]: e.target.value })} /></div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600 border border-gray-200 rounded-lg px-3 py-2 cursor-pointer">
            <input type="checkbox" checked={!!companyData.has_msme_delayed_payments} onChange={e => updateCompanyData({ has_msme_delayed_payments: e.target.checked })} />
            Payments to Micro/Small enterprise vendors delayed beyond 45 days this FY (MSME-1 trigger)
          </label>

          {resolutionsWithPlaceholders.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-600 mb-1">Resolution Draft Fields</p>
              <p className="text-[11px] text-gray-400 mb-3">Grouped by resolution — only the resolutions actually linked to this case (Resolutions tab) appear here. If a field shouldn't be here, remove that resolution on the Resolutions tab instead.</p>
              <div className="space-y-4">
                {resolutionsWithPlaceholders.map(({ resolution: r, resIdx, instanceLabel, keys }, i) => {
                  const accent = meetingAccent(r.meeting_type);
                  return (
                    <div key={i} className={`rounded-lg border border-gray-100 border-l-4 ${accent.border} p-3`}>
                      <p className={`text-xs font-semibold mb-2 ${accent.text}`}>{r.resolution_title}{instanceLabel} <span className="font-normal text-gray-400">— {meetingLabel(r.meeting_type)}</span></p>
                      <div className="grid grid-cols-2 gap-3">
                        {keys.map(key => (
                          <div key={key} className={NUMBERED_CLAUSE_KEYS.includes(key) ? 'col-span-2' : ''}>
                            <label className={labelCls}>{humanizeKey(key)}</label>
                            {NUMBERED_CLAUSE_KEYS.includes(key) ? (
                              <NumberedClauseEditor
                                clauses={resolutionFieldValue(r, `${key}__clauses`) || ['']}
                                onChange={next => updateResolutionField(resIdx, { [`${key}__clauses`]: next, [key]: formatNumberedClauses(next) })}
                              />
                            ) : (
                              <input type={isDateKey(key) ? 'date' : 'text'} className={inputCls} value={resolutionFieldValue(r, key)} onChange={e => updateResolutionField(resIdx, { [key]: e.target.value })} />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'resolutions' && (
        <div className="space-y-5">
          {meetingList.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-10 text-center text-gray-400 text-sm">No Board Meeting or AGM linked to this case yet.</div>
          )}
          {meetingList.map(({ meetingId, type }) => {
            const accent = meetingAccent(type);
            const md = meetingState[meetingId] || {};
            const isBM = type === 'BM';
            const roster = isBM ? directors : shareholders;
            const rosterKey = isBM ? 'director_name' : 'member_name';
            // A director can't validly be "present" at a meeting held
            // before their appointment or on/after their cessation date —
            // strip any such name out of whatever's stored, so a stale
            // attendee (marked before this check existed, or before their
            // appointment/cessation date was even entered) doesn't quietly
            // count toward quorum, voting, or the generated Minutes/
            // Attendance Sheet. Only enforced once a meeting date is set,
            // and only for directors — shareholder attendance has no
            // equivalent appointment-date concept.
            const rawAttendees = md.attendees || [];
            const attendees = (isBM && md.meeting_date)
              ? rawAttendees.filter(name => {
                  const d = directors.find(x => x.director_name === name);
                  if (!d) return true; // no matching record — nothing to disqualify them on
                  return !(d.appointment_date && d.appointment_date > md.meeting_date) && !(d.cessation_date && d.cessation_date < md.meeting_date);
                })
              : rawAttendees;
            const quorumNeeded = isBM ? boardQuorum(directors.length) : generalMeetingQuorum(companyType, shareholders.length);
            const quorumMet = attendees.length >= quorumNeeded;
            const toggleAttendee = (name) => {
              const next = attendees.includes(name) ? attendees.filter(n => n !== name) : [...attendees, name];
              updateMeeting(meetingId, { attendees: next });
            };
            const items = getMeetingAgendaItems(meetingId);
            // This tab edits THIS case's own resolutions only — a sibling
            // case's resolution (folded in from another Event/Annual
            // Filing case sharing this meeting) has no valid resIdx into
            // local state, so it's shown as a read-only note here instead
            // of a broken edit row; it still appears correctly in the
            // generated Notice/Minutes/CTC (getMeetingAgendaItems above
            // includes it there).
            const ownItems = items.filter(i => i.isOwn);
            const foreignItems = items.filter(i => !i.isOwn);

            return (
              <div key={meetingId} className={`bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden border-l-4 ${accent.border}`}>
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <IconBadge accent={accent}>{Icon.meeting}</IconBadge>
                    <div>
                      <p className={`text-sm font-bold ${accent.text}`}>{meetingLabel(type)}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">{items.length} resolution{items.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <span className="text-[11px] text-gray-400">{meetingSaveStatus[meetingId] === 'saving' ? 'Saving…' : ''}</span>
                </div>

                <div className="p-5">
                  <p className={`text-xs font-semibold mb-2 ${accent.textLight}`}>Meeting Particulars</p>
                  <div className="grid grid-cols-6 gap-2 mb-1">
                    <label className="text-[10px] text-gray-400">Notice Sent</label>
                    <label className="text-[10px] text-gray-400">Meeting Date</label>
                    <span />
                    <span />
                    <span className="col-span-2" />
                  </div>
                  {/* Notice Sent lives here, next to Meeting Date, rather
                      than on the Documents tab — it's meeting particulars,
                      same as the rest of this row, not document-generation
                      config. */}
                  <div className="grid grid-cols-6 gap-2 mb-2.5">
                    <input type="date" max={md.meeting_date || undefined} value={md.notice_date || ''} onChange={e => updateMeeting(meetingId, { notice_date: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-blue-500" />
                    <input type="date" min={md.notice_date || undefined} value={md.meeting_date || ''} onChange={e => updateMeeting(meetingId, { meeting_date: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-blue-500" />
                    <input type="time" value={md.meeting_time || ''} onChange={e => updateMeeting(meetingId, { meeting_time: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-blue-500" placeholder="Started at" title="Started at" />
                    <input type="time" value={md.meeting_end_time || ''} onChange={e => updateMeeting(meetingId, { meeting_end_time: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-blue-500" placeholder="Concluded at" title="Concluded at" />
                    <input value={md.venue || ''} onChange={e => updateMeeting(meetingId, { venue: e.target.value })} placeholder="Venue" className="col-span-2 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-blue-500" />
                  </div>
                  <div className="flex items-center gap-1.5 mb-2.5">
                    <button type="button" onClick={() => updateMeeting(meetingId, { venue_mode: 'physical' })} className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${(md.venue_mode || 'physical') === 'physical' ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>Physical</button>
                    <button type="button" onClick={() => updateMeeting(meetingId, { venue_mode: 'vc' })} className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${md.venue_mode === 'vc' ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>Audio/Video</button>
                    <label className="flex items-center gap-1.5 text-[10px] text-gray-500 ml-2"><input type="checkbox" checked={!!md.shorter_notice} onChange={e => updateMeeting(meetingId, { shorter_notice: e.target.checked })} />Shorter notice</label>
                  </div>
                  <select value={md.chairperson || ''} onChange={e => {
                    const name = e.target.value;
                    const nextAttendees = (isBM && name && !attendees.includes(name)) ? [...attendees, name] : attendees;
                    updateMeeting(meetingId, { chairperson: name, attendees: nextAttendees });
                  }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 mb-3">
                    <option value="">Chairperson…</option>
                    {directors.map(d => <option key={d.id} value={d.director_name}>{d.director_name}{d.din ? ` (DIN: ${d.din})` : ''}</option>)}
                  </select>

                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[11px] font-semibold text-gray-500">{isBM ? 'Directors' : 'Members'} Present</p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${quorumMet ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {attendees.length}/{roster.length} present · quorum {quorumNeeded} · {quorumMet ? '✓ Met' : '✗ Not Met'}
                    </span>
                  </div>
                  {roster.length === 0 ? (
                    <p className="text-[11px] text-gray-400">{isBM ? 'No directors on record for this company.' : 'No shareholders on record for this company.'}</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {roster.map(p => {
                        // A director can't be marked present at a meeting
                        // held before their appointment, or on/after the
                        // date their directorship ceased — checking that
                        // box would be attendance nobody could actually
                        // have taken. Only enforced once a meeting date is
                        // set (nothing to check it against otherwise), and
                        // only for directors (isBM) — shareholder
                        // attendance isn't gated by an appointment date.
                        const ineligible = isBM && !!md.meeting_date && (
                          (p.appointment_date && p.appointment_date > md.meeting_date) ||
                          (p.cessation_date && p.cessation_date < md.meeting_date)
                        );
                        const ineligibleReason = !ineligible ? '' : (p.appointment_date && p.appointment_date > md.meeting_date)
                          ? `Not yet appointed as on this meeting's date — appointed ${formatDateForDoc(p.appointment_date)}`
                          : `Already ceased as on this meeting's date — ceased ${formatDateForDoc(p.cessation_date)}`;
                        return (
                          <label key={p.id} title={ineligibleReason || undefined}
                            className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg ${ineligible ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-50'}`}>
                            <input type="checkbox" disabled={ineligible} checked={!ineligible && attendees.includes(p[rosterKey])} onChange={() => toggleAttendee(p[rosterKey])} />
                            {p[rosterKey]}{isBM && p.din ? ` (DIN: ${p.din})` : ''}{ineligible ? ' ⚠' : ''}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {foreignItems.length > 0 && (
                  <div className="px-5 py-2.5 bg-amber-50 border-t border-amber-100">
                    <p className="text-[11px] text-amber-700">
                      ⚠ {foreignItems.length} more resolution{foreignItems.length !== 1 ? 's' : ''} on this same meeting from {[...new Set(foreignItems.map(i => i.caseTitle))].join(', ')} — included in the generated Notice/Minutes/CTC below, but edited from that case instead.
                    </p>
                  </div>
                )}
                <div className="divide-y divide-gray-50 border-t border-gray-100">
                  {ownItems.length === 0 && <p className="px-5 py-6 text-center text-xs text-gray-400">No resolutions for this meeting yet{foreignItems.length > 0 ? ' from this case' : ''}.</p>}
                  {ownItems.map(({ resolution: r, resIdx }) => {
                    const isPassed = r.status === 'Passed';
                    const isRejected = r.status === 'Rejected';
                    const locked = isPassed || isRejected;
                    return (
                      <div key={resIdx} className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-sm font-bold text-[#0f1f45]">{r.resolution_title}</p>
                            <p className={`text-[11px] mt-0.5 ${accent.textLight}`}>{r.resolution_type} Resolution{r.business_classification ? (r.business_classification === 'ORDINARY_BUSINESS' ? ' · Ordinary Business' : ' · Special Business') : ''}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${isPassed ? 'bg-green-100 text-green-700' : isRejected ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
                            {!locked && <button onClick={() => removeResolution(resIdx)} title="Remove this resolution" className="text-gray-300 hover:text-red-500">{Icon.trash}</button>}
                          </div>
                        </div>
                        {locked && (
                          <div className={`px-3 py-2 mb-2 rounded-lg flex items-center justify-between ${isPassed ? 'bg-green-50' : 'bg-red-50'}`}>
                            <p className={`text-[11px] ${isPassed ? 'text-green-700' : 'text-red-700'}`}>🔒 {isPassed ? 'Passed' : 'Rejected'} and locked</p>
                            <button onClick={() => reviseResolution(resIdx)} className={`text-[11px] font-semibold hover:underline ${isPassed ? 'text-green-700' : 'text-red-700'}`}>↺ Revise</button>
                          </div>
                        )}
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-semibold text-gray-600">Resolution Draft</label>
                          {!locked && <button onClick={() => regenerateResolution(resIdx)} className="text-xs font-semibold text-blue-600 hover:underline">↻ Regenerate from template</button>}
                        </div>
                        <textarea rows={5} value={(r.manually_edited || r.status === 'Passed') ? (r.rendered_resolution || '') : fillTemplate(r.template_body, fieldContextFor(r))}
                          onChange={e => !locked && updateResolution(resIdx, { rendered_resolution: e.target.value, manually_edited: true })}
                          readOnly={locked} style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', lineHeight: 1.7 }}
                          className={`w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 mb-3 ${locked ? 'bg-gray-50 cursor-not-allowed' : ''}`} />

                        {(() => {
                          // Each of these blocks voting until fixed — say
                          // which one explicitly instead of just showing
                          // nothing, which reads as "this feature doesn't
                          // exist" rather than "one thing is missing".
                          // (A locked/Passed resolution skips all of them —
                          // votes are already cast, so the tally below just
                          // shows the final read-only result.)
                          if (!locked && roster.length === 0) return <p className="text-[11px] text-gray-400">Voting opens once {isBM ? 'directors' : 'shareholders'} are on record for this company — add them in Company Master first.</p>;
                          if (!locked && attendees.length === 0) return <p className="text-[11px] text-gray-400">Voting opens once at least one {isBM ? 'director' : 'member'} is marked present above.</p>;
                          if (!locked && !md.chairperson) return <p className="text-[11px] text-gray-400">Voting opens once a Chairperson is selected above.</p>;
                          if (!locked && !quorumMet) return <p className="text-[11px] text-gray-400">Voting opens once quorum is met.</p>;
                          const votes = r.votes || {};
                          const { forWeight, againstWeight, abstainWeight } = tallyVotes(votes, attendees, isBM, shareholders);
                          const cast = forWeight + againstWeight;
                          const forPct = cast > 0 ? Math.round((forWeight / cast) * 100) : 0;
                          const threshold = r.resolution_type === 'SPECIAL' ? 75 : 50;
                          const votedCount = attendees.filter(n => votes[n]).length;
                          return (
                            <div>
                              <div className="flex items-center justify-between mb-1.5">
                                <p className="text-[11px] font-semibold text-gray-500">Voting {isBM ? '' : '(share-weighted)'}</p>
                                <span className="text-[10px] text-gray-400">{votedCount}/{attendees.length} voted</span>
                              </div>
                              <div className="space-y-1.5">
                                {attendees.map(name => (
                                  <div key={name} className="flex items-center justify-between gap-2">
                                    <span className="text-[11px] text-gray-600 truncate">{name}{isBM ? (() => { const din = directors.find(d => d.director_name === name)?.din; return din ? ` (DIN: ${din})` : ''; })() : ''}</span>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      {['for', 'against', 'abstain'].map(choice => (
                                        <button key={choice} type="button" disabled={locked} onClick={() => castVote(resIdx, name, choice)}
                                          className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${votes[name] === choice
                                            ? (choice === 'for' ? 'bg-green-600 text-white border-green-600' : choice === 'against' ? 'bg-red-600 text-white border-red-600' : 'bg-gray-500 text-white border-gray-500')
                                            : 'bg-white text-gray-400 border-gray-200'}`}>
                                          {choice === 'for' ? 'F' : choice === 'against' ? 'A' : 'Ab'}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {cast > 0 && (
                                <div className="mt-2">
                                  <div className="h-1.5 rounded-full bg-red-200 overflow-hidden"><div className="h-full bg-green-500" style={{ width: `${forPct}%` }} /></div>
                                  <p className="text-[10px] text-gray-500 mt-1">{forPct}% in favor{abstainWeight > 0 ? ` · ${abstainWeight} abstained` : ''} — needs ≥{threshold}% of votes cast</p>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>

                <div className="p-5 border-t border-gray-100">
                  {addResOpenFor === meetingId ? (
                    (() => {
                      const options = (isBM ? addResBoardOptions : addResAgmOptions).filter(r => {
                        const q = addResSearch.toLowerCase();
                        return !q || r.resolution_title?.toLowerCase().includes(q) || r.category?.toLowerCase().includes(q);
                      });
                      return (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-gray-600">Add a resolution to this meeting</p>
                            <button onClick={() => { setAddResOpenFor(null); setAddResSearch(''); }} className="text-[11px] font-semibold text-gray-400 hover:text-gray-600">Cancel</button>
                          </div>
                          <input value={addResSearch} onChange={e => setAddResSearch(e.target.value)} placeholder="Search resolutions…" autoFocus
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-blue-500 mb-2" />
                          <div className="space-y-1.5 max-h-56 overflow-y-auto">
                            {options.map(r => (
                              <button key={r.id} onClick={() => addResolutionToMeeting(meetingId, type, r)}
                                className="w-full flex items-start gap-2 px-3 py-2 rounded-lg border border-gray-200 text-left text-xs hover:bg-gray-50">
                                <span><span className="font-semibold text-[#0f1f45]">{r.resolution_code} — {r.resolution_title}</span><span className="block text-gray-400">{r.resolution_type} · {r.category}</span></span>
                              </button>
                            ))}
                            {options.length === 0 && <p className="px-3 py-4 text-center text-xs text-gray-400">No matching resolutions in Resolution Master.</p>}
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <button onClick={() => { setAddResOpenFor(meetingId); setAddResSearch(''); }} className="text-xs font-semibold text-blue-600 hover:underline">+ Add Resolution</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'board-report' && (() => {
        const ctx = buildBoardReportCtx(item, companyData, boardReport, directors, auditors);
        const clauses = boardReportClausesForProfile(ctx.profile);
        const meetingsRows = boardReport.board_meetings || [];
        const updateMeetingRow = (idx, patch) => {
          const next = meetingsRows.map((r, i) => i === idx ? { ...r, ...patch } : r);
          updateBoardReport({ board_meetings: next });
        };
        const prefillMeetings = () => {
          updateBoardReport({ board_meetings: companyBoardMeetings.map(m => ({ date: m.meeting_date, entitled: m.total_strength || directors.length || '', present: '' })) });
        };
        const signatories = boardReport.signatories && boardReport.signatories.length ? boardReport.signatories : [{}, {}];
        const updateSignatory = (idx, directorId) => {
          const d = directors.find(x => String(x.id) === String(directorId));
          const next = [...signatories];
          next[idx] = d ? { director_id: d.id, name: d.director_name, din: d.din } : {};
          updateBoardReport({ signatories: next });
        };
        const handleGenerate = () => {
          const rows = meetingsRows.length ? meetingsRows : companyBoardMeetings.map(m => ({ date: m.meeting_date, entitled: m.total_strength || directors.length || '', present: '' }));
          const html = renderBoardReportHtml(ctx, rows, signatories.filter(s => s.name));
          downloadBoardReportWord(`Boards_Report_${item.company_name}_FY_${item.financial_year}`, html);
        };

        return (
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="text-base font-bold text-[#0f1f45]">Report Profile &amp; Applicability</h4>
                <p className="text-xs text-gray-400 mt-0.5">Routes clause coverage under Sec 134(3)/(5) and Rule 8/8A based on company type and financials.</p>
              </div>
              <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">{BOARD_REPORT_PROFILE_LABELS[ctx.profile]}</span>
            </div>
            <div className="flex items-center gap-1.5 mb-3">
              {['auto', 'abridged', 'standard', 'comprehensive'].map(p => (
                <button key={p} type="button" onClick={() => updateBoardReport({ profile_override: p })}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border ${(boardReport.profile_override || 'auto') === p ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>
                  {p === 'auto' ? `Auto (${companyType || '—'})` : p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="px-2.5 py-1.5 rounded-lg bg-gray-50"><span className="text-gray-400">CSR (Sec 135):</span> <span className="font-semibold">{ctx.csrApplicable ? 'Applicable' : 'Not Applicable'}</span></div>
              <div className="px-2.5 py-1.5 rounded-lg bg-gray-50"><span className="text-gray-400">Secretarial Audit (Sec 204):</span> <span className="font-semibold">{ctx.secretarialAuditApplicable ? 'Applicable' : 'Not Applicable'}</span></div>
              <div className="px-2.5 py-1.5 rounded-lg bg-gray-50"><span className="text-gray-400">Result:</span> <span className="font-semibold">{ctx.isLossMaking ? 'Loss-making (dividend/reserves suppressed)' : 'Profit-making'}</span></div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="text-sm font-bold text-[#0f1f45] mb-3">Report Header</h4>
            <div className="grid grid-cols-4 gap-3 mb-3">
              <div><label className={labelCls}>Report Ordinal (e.g. "4th (Fourth)")</label><input className={inputCls} value={boardReport.report_ordinal || ''} onChange={e => updateBoardReport({ report_ordinal: e.target.value })} /></div>
              <div><label className={labelCls}>Place</label><input className={inputCls} value={boardReport.place || ''} onChange={e => updateBoardReport({ place: e.target.value })} /></div>
              <div><label className={labelCls}>Date</label><input type="date" className={inputCls} value={boardReport.date || ''} onChange={e => updateBoardReport({ date: e.target.value })} /></div>
              <div>
                <label className={labelCls}>Website</label>
                <div className="flex items-center gap-1.5">
                  <button type="button" onClick={() => updateBoardReport({ has_website: !boardReport.has_website })} className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${boardReport.has_website ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>{boardReport.has_website ? 'Has Website' : 'No Website'}</button>
                </div>
              </div>
            </div>
            {boardReport.has_website && (
              <div><label className={labelCls}>Website URL</label><input className={inputCls} value={boardReport.website_url || ''} onChange={e => updateBoardReport({ website_url: e.target.value })} /></div>
            )}
          </div>

          {clauses.map((clause) => {
            if (clause.special === 'financials') {
              const fin = boardReport.financials || {};
              const setFin = (patch) => updateBoardReportNested('financials', patch);
              return (
                <div key={clause.key} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <h4 className="text-sm font-bold text-[#0f1f45] mb-3">1. {clause.heading}</h4>
                  <table className="w-full text-xs mb-3 border-collapse">
                    <thead><tr className="bg-gray-50"><th className="text-left px-2 py-1.5 font-semibold text-gray-500">Particulars (₹)</th><th className="text-right px-2 py-1.5 font-semibold text-gray-500">FY {ctx.fyCy}</th><th className="text-right px-2 py-1.5 font-semibold text-gray-500">FY {ctx.fyPy}</th></tr></thead>
                    <tbody>
                      {[['revenue', 'Revenue from operations'], ['other_income', 'Other income'], ['total_expenditure', 'Total expenditure'], ['tax', 'Current and deferred tax']].map(([f, label]) => (
                        <tr key={f} className="border-t border-gray-50">
                          <td className="px-2 py-1.5 text-gray-600">{label}</td>
                          <td className="px-2 py-1"><input type="number" className={`${inputCls} text-right`} value={fin[`${f}_cy`] || ''} onChange={e => setFin({ [`${f}_cy`]: e.target.value })} /></td>
                          <td className="px-2 py-1"><input type="number" className={`${inputCls} text-right`} value={fin[`${f}_py`] || ''} onChange={e => setFin({ [`${f}_py`]: e.target.value })} /></td>
                        </tr>
                      ))}
                      <tr className="border-t border-gray-100 font-semibold"><td className="px-2 py-1.5">Total income</td><td className="px-2 py-1.5 text-right">{formatINR(ctx.totalIncCy)}</td><td className="px-2 py-1.5 text-right">{formatINR(ctx.totalIncPy)}</td></tr>
                      <tr><td className="px-2 py-1.5">Profit / (Loss) before tax</td><td className="px-2 py-1.5 text-right">{formatINR(ctx.pbtCy)}</td><td className="px-2 py-1.5 text-right">{formatINR(ctx.pbtPy)}</td></tr>
                      <tr className="font-semibold"><td className="px-2 py-1.5">Profit / (Loss) after tax</td><td className="px-2 py-1.5 text-right">{formatINR(ctx.patCy)}</td><td className="px-2 py-1.5 text-right">{formatINR(ctx.patPy)}</td></tr>
                    </tbody>
                  </table>
                  <textarea rows={3} className={`${inputCls} font-serif`} value={boardReportClauseText(clause, ctx)} onChange={e => updateBoardReportClause(clause.key, e.target.value)} />
                </div>
              );
            }
            if (clause.special === 'meetings') {
              return (
                <div key={clause.key} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-[#0f1f45]">{clauses.indexOf(clause) + 1}. {clause.heading}</h4>
                    <div className="flex items-center gap-2">
                      {companyBoardMeetings.length > 0 && <button type="button" onClick={prefillMeetings} className="text-[11px] font-semibold text-blue-600 hover:underline">↻ Prefill from Register ({companyBoardMeetings.length})</button>}
                      <button type="button" onClick={() => updateBoardReport({ board_meetings: [...meetingsRows, { date: '', entitled: directors.length || '', present: '' }] })} className="text-[11px] font-semibold text-blue-600 hover:underline">+ Add Meeting</button>
                    </div>
                  </div>
                  {meetingsRows.length === 0 ? (
                    <p className="text-xs text-gray-400">No board meetings added yet.</p>
                  ) : (
                    <table className="w-full text-xs border-collapse">
                      <thead><tr className="bg-gray-50"><th className="text-left px-2 py-1.5 font-semibold text-gray-500">S.No.</th><th className="text-left px-2 py-1.5 font-semibold text-gray-500">Date</th><th className="text-left px-2 py-1.5 font-semibold text-gray-500">Directors Entitled</th><th className="text-left px-2 py-1.5 font-semibold text-gray-500">Directors Present</th><th></th></tr></thead>
                      <tbody>
                        {meetingsRows.map((r, i) => (
                          <tr key={i} className="border-t border-gray-50">
                            <td className="px-2 py-1.5">{i + 1}</td>
                            <td className="px-2 py-1"><input type="date" className={inputCls} value={r.date || ''} onChange={e => updateMeetingRow(i, { date: e.target.value })} /></td>
                            <td className="px-2 py-1"><input type="number" className={inputCls} value={r.entitled || ''} onChange={e => updateMeetingRow(i, { entitled: e.target.value })} /></td>
                            <td className="px-2 py-1"><input type="number" className={inputCls} value={r.present || ''} onChange={e => updateMeetingRow(i, { present: e.target.value })} /></td>
                            <td><button type="button" onClick={() => updateBoardReport({ board_meetings: meetingsRows.filter((_, j) => j !== i) })} className="text-gray-300 hover:text-red-500">{Icon.trash}</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            }
            if (clause.special === 'directors') {
              // Filtered/annotated by ctx (see relevantBoardReportDirectors)
              // — not the raw Company Master list — so this preview always
              // matches what actually gets exported.
              const reportDirectors = ctx.directors || [];
              return (
                <div key={clause.key} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <h4 className="text-sm font-bold text-[#0f1f45] mb-3">{clauses.indexOf(clause) + 1}. {clause.heading}</h4>
                  <p className="text-[11px] text-gray-400 mb-2">Directors who served any part of FY {item.financial_year}, plus anyone appointed since who's still sitting as on {ctx.reportDate ? formatDateForDoc(ctx.reportDate) : 'the Report date'} (set below under Report Header).</p>
                  {directors.length === 0 ? (
                    <p className="text-xs text-gray-400 mb-3">No directors on record for this company — add them via Company Master.</p>
                  ) : reportDirectors.length === 0 ? (
                    <p className="text-xs text-gray-400 mb-3">No directors on record fall within this Report's period — check appointment/cessation dates on Company Master, or the Report date under Report Header.</p>
                  ) : (
                    <table className="w-full text-xs border-collapse mb-3">
                      <thead><tr className="bg-gray-50"><th className="text-left px-2 py-1.5 font-semibold text-gray-500">S.No.</th><th className="text-left px-2 py-1.5 font-semibold text-gray-500">Name</th><th className="text-left px-2 py-1.5 font-semibold text-gray-500">DIN</th><th className="text-left px-2 py-1.5 font-semibold text-gray-500">Designation</th><th className="text-left px-2 py-1.5 font-semibold text-gray-500">Date of Appointment or Resignation</th><th className="text-left px-2 py-1.5 font-semibold text-gray-500">Status</th></tr></thead>
                      <tbody>{reportDirectors.map((d, i) => (
                        <tr key={d.id} className="border-t border-gray-50">
                          <td className="px-2 py-1.5">{i + 1}</td><td className="px-2 py-1.5">{d.director_name}</td><td className="px-2 py-1.5">{d.din}</td><td className="px-2 py-1.5">{d.designation || 'Director'}</td>
                          <td className="px-2 py-1.5">{formatDateForDoc(d.board_report_status_date) || '—'}</td>
                          <td className={`px-2 py-1.5 ${d.board_report_status === 'Ceased' ? 'text-amber-600 font-semibold' : ''}`}>{d.board_report_status}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  )}
                  <label className={labelCls}>Changes / KMP note (editable — auto-drafted from the table above until you type your own)</label>
                  <textarea rows={2} className={`${inputCls} font-serif`} value={boardReport.clauses?.directors_kmp_note ?? ctx.directorsKmpNoteDefault}
                    onChange={e => updateBoardReportClause('directors_kmp_note', e.target.value)} />
                </div>
              );
            }
            if (clause.special === 'drs') {
              const items = ctx.profile === 'comprehensive' ? [...DRS_ITEMS.slice(0, 3), DRS_ITEM_PUBLIC] : DRS_ITEMS;
              return (
                <div key={clause.key} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <h4 className="text-sm font-bold text-[#0f1f45] mb-2">{clauses.indexOf(clause) + 1}. {clause.heading}</h4>
                  <p className="text-xs text-gray-400 mb-2">Fixed statutory wording under Sec 134(5) — not editable.</p>
                  <ol className="list-[lower-alpha] pl-5 space-y-1.5">
                    {items.map((t, i) => <li key={i} className="text-xs text-gray-600 leading-relaxed">{t}</li>)}
                  </ol>
                </div>
              );
            }
            if (clause.special === 'acknowledgements') {
              return (
                <div key={clause.key} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <h4 className="text-sm font-bold text-[#0f1f45] mb-3">{clauses.indexOf(clause) + 1}. {clause.heading}</h4>
                  <textarea rows={2} className={`${inputCls} font-serif mb-4`} value={boardReportClauseText(clause, ctx)} onChange={e => updateBoardReportClause(clause.key, e.target.value)} />
                  <label className={labelCls}>Signatories (for the Word export)</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[0, 1].map(idx => (
                      <select key={idx} className={inputCls} value={signatories[idx]?.director_id || ''} onChange={e => updateSignatory(idx, e.target.value)}>
                        <option value="">Select director…</option>
                        {directors.map(d => <option key={d.id} value={d.id}>{d.director_name} (DIN {d.din})</option>)}
                      </select>
                    ))}
                  </div>
                </div>
              );
            }
            // Generic narrative clauses — plain editable textarea seeded with
            // the routed default text; a few carry extra structured inputs
            // just above the textarea to drive that default.
            const extra = (() => {
              if (clause.key === 'dividend') return (
                <div className="flex items-center gap-2 mb-2">
                  <button type="button" onClick={() => updateBoardReportNested('dividend', { recommended: !boardReport.dividend?.recommended })} className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${boardReport.dividend?.recommended ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>{boardReport.dividend?.recommended ? 'Dividend Recommended' : 'No Dividend'}</button>
                  {boardReport.dividend?.recommended && <input className={inputCls} style={{ maxWidth: 160 }} placeholder="Rate (e.g. 10%)" value={boardReport.dividend?.rate || ''} onChange={e => updateBoardReportNested('dividend', { rate: e.target.value })} />}
                </div>
              );
              if (clause.key === 'reserves') return (
                <div className="flex items-center gap-2 mb-2">
                  <button type="button" onClick={() => updateBoardReportNested('reserves', { transferred: !boardReport.reserves?.transferred })} className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${boardReport.reserves?.transferred ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>{boardReport.reserves?.transferred ? 'Amount Transferred' : 'No Transfer'}</button>
                  {boardReport.reserves?.transferred && <input type="number" className={inputCls} style={{ maxWidth: 160 }} placeholder="Amount (₹)" value={boardReport.reserves?.amount || ''} onChange={e => updateBoardReportNested('reserves', { amount: e.target.value })} />}
                </div>
              );
              if (clause.key === 'statutory_auditors') {
                const legacy = boardReport.auditor || {};
                const setLegacy = (patch) => updateBoardReportNested('auditor', patch);
                if (ctx.activeAuditor) {
                  const m = ctx.activeAuditor;
                  return (
                    <div className="mb-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-between text-xs">
                      <span className="text-gray-600">
                        <span className="font-semibold text-[#0f1f45]">{m.firm_name}</span>{m.frn ? ` (FRN: ${m.frn})` : ''} — {m.appointment_category}{m.confirmation_status === 'Pending EGM Confirmation' ? ' · pending EGM confirmation' : ''} · from Statutory Auditor Master
                      </span>
                      <span className="text-gray-400">Edit via the appointment/removal resolution in this case, or Company Master</span>
                    </div>
                  );
                }
                return (
                  <div className="mb-2">
                    <p className="text-[11px] text-amber-600 mb-1.5">No Statutory Auditor Master record found for this company yet — pass a Passed First Auditor / AGM Appointment / Casual Vacancy resolution to auto-fill this, or enter details manually below as a one-off fallback.</p>
                    <div className="grid grid-cols-4 gap-2">
                      <input className={inputCls} placeholder="Firm name" value={legacy.firm_name || ''} onChange={e => setLegacy({ firm_name: e.target.value })} />
                      <input className={inputCls} placeholder="FRN" value={legacy.frn || ''} onChange={e => setLegacy({ frn: e.target.value })} />
                      <input type="date" className={inputCls} value={legacy.appointed_agm_date || ''} onChange={e => setLegacy({ appointed_agm_date: e.target.value })} />
                      <input className={inputCls} placeholder="Term ends AGM year" value={legacy.term_to_year || ''} onChange={e => setLegacy({ term_to_year: e.target.value })} />
                    </div>
                  </div>
                );
              }
              if (clause.key === 'csr' && ctx.csrApplicable) return (
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input className={inputCls} placeholder="CSR Committee composition (optional)" value={boardReport.csr?.committee_details || ''} onChange={e => updateBoardReportNested('csr', { committee_details: e.target.value })} />
                  <input className={inputCls} placeholder="CSR spend details (optional)" value={boardReport.csr?.spend_details || ''} onChange={e => updateBoardReportNested('csr', { spend_details: e.target.value })} />
                </div>
              );
              if (clause.key === 'significant_orders') return (
                <div className="flex items-center gap-2 mb-2">
                  <button type="button" onClick={() => updateBoardReportNested('significant_orders', { has_orders: !boardReport.significant_orders?.has_orders })} className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${boardReport.significant_orders?.has_orders ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>{boardReport.significant_orders?.has_orders ? 'Orders Exist' : 'No Orders'}</button>
                </div>
              );
              if (clause.key === 'ibc_proceedings') return (
                <div className="flex items-center gap-2 mb-2">
                  <button type="button" onClick={() => updateBoardReportNested('ibc', { has_proceedings: !boardReport.ibc?.has_proceedings })} className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${boardReport.ibc?.has_proceedings ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>{boardReport.ibc?.has_proceedings ? 'Proceedings Exist' : 'No Proceedings'}</button>
                </div>
              );
              if (clause.key === 'sexual_harassment') return (
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-[11px] text-gray-500">Complaints received:</label>
                  <input type="number" className={inputCls} style={{ maxWidth: 100 }} value={boardReport.sexual_harassment?.complaints || ''} onChange={e => updateBoardReportNested('sexual_harassment', { complaints: e.target.value })} />
                </div>
              );
              return null;
            })();
            return (
              <div key={clause.key} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <h4 className="text-sm font-bold text-[#0f1f45] mb-3">{clauses.indexOf(clause) + 1}. {clause.heading}</h4>
                {extra}
                <textarea rows={clause.key === 'energy_tech_forex' ? 6 : 3} className={`${inputCls} font-serif`} value={boardReportClauseText(clause, ctx)} onChange={e => updateBoardReportClause(clause.key, e.target.value)} />
              </div>
            );
          })}

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center justify-between">
            <div>
              <h4 className="text-sm font-bold text-[#0f1f45]">Generate Board's Report</h4>
              <p className="text-xs text-gray-400 mt-0.5">{clauses.length} clauses routed under {BOARD_REPORT_PROFILE_LABELS[ctx.profile]}.</p>
            </div>
            <button onClick={handleGenerate} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#0f1f45]">📄 Download Board's Report (Word)</button>
          </div>
        </div>
        );
      })()}

      {activeTab === 'documents' && (
        <div className="space-y-5">
          {meetingList.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-10 text-center text-gray-400 text-sm">No meeting linked to this case yet — link one from the Resolutions tab or when creating the case.</div>
          ) : meetingList.map(({ meetingId, type }) => {
            const accent = meetingAccent(type);
            const md = meetingState[meetingId] || {};
            const isBM = type === 'BM';
            const items = getMeetingAgendaItems(meetingId);
            const selection = md.ctc_selection || {};
            const selectedCount = items.filter(i => selection[i.itemNo]).length;
            const allSelected = items.length > 0 && selectedCount === items.length;
            const gapDays = clearDaysBetween(md.notice_date, md.meeting_date, type);
            const requiredDays = requiredNoticeDays(type);
            const isShort = gapDays !== null && gapDays < requiredDays;
            const consentRoster = isBM ? directors.map(d => d.director_name) : shareholders.map(s => s.member_name);
            const consenters = md.shorter_consent || md.attendees || [];
            return (
              <div key={meetingId} className={`bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden border-l-4 ${accent.border}`}>
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-bold ${accent.text}`}>{meetingLabel(type)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{items.length} agenda item{items.length !== 1 ? 's' : ''}</p>
                  </div>
                  {(() => {
                    const which = isBM ? 'board' : 'agm';
                    const syncedAt = isBM ? item.board_synced_at : item.agm_synced_at;
                    if (syncedAt) {
                      return <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-green-100 text-green-700">✓ Synced {new Date(syncedAt).toLocaleDateString('en-IN')}</span>;
                    }
                    return (
                      <button onClick={() => requestRegisterSync(which)} disabled={syncingWhich === which}
                        title="Pushes date/time/venue/mode/chairman/quorum into the Statutory Register — only once this meeting's resolution(s) are Passed/Rejected."
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-[11px] font-semibold disabled:opacity-50">
                        {syncingWhich === which ? 'Syncing…' : <>{Icon.sync} Sync to Register</>}
                      </button>
                    );
                  })()}
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2">Notice & Minutes</p>
                    {/* Concluded At and Notice Sent now live on the
                        Resolution tab's Meeting Particulars block, next to
                        Date/Time/Venue — they're meeting-level data, set
                        once where the rest of the meeting particulars are
                        already edited, not document-generation config. */}
                    {!isBM && (
                      <div className="mb-2.5">
                        <input value={md.chairman_remarks || ''} onChange={e => updateMeeting(meetingId, { chairman_remarks: e.target.value })} placeholder="Chairman's remarks (optional)" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                      </div>
                    )}
                    {/* A negative gap means notice was purportedly sent
                        AFTER the meeting date — impossible, and not what
                        "shorter notice consent" covers (that's for notice
                        given late but still before the meeting). Flag it
                        as a data error instead of offering the consent
                        workflow below. */}
                    {gapDays !== null && gapDays < 0 && (
                      <p className="text-[11px] mb-2 text-red-600 font-semibold">⚠ Notice Sent date is after the Meeting Date — fix the dates before generating documents (see the Resolutions tab).</p>
                    )}
                    {!isBM && (
                      <input value={md.scrutinizer_name || ''} onChange={e => updateMeeting(meetingId, { scrutinizer_name: e.target.value })} placeholder="Scrutinizer name (if polled)" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500 mb-2.5" />
                    )}
                    {isBM && (
                      <input value={md.interest_disclosure || ''} onChange={e => updateMeeting(meetingId, { interest_disclosure: e.target.value })} placeholder="Directors' interest disclosure (Sec 184) — leave blank for the standard 'none interested' line" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500 mb-2.5" />
                    )}
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => downloadMeetingNoticeDoc(meetingId, type)} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Notice of Meeting (Word)</button>
                      <button onClick={() => downloadMeetingMinutesDoc(meetingId, type)} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Minutes (Word)</button>
                      <button onClick={() => downloadAttendanceSheetDoc(meetingId, type)} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Attendance Sheet (Word)</button>
                    </div>
                  </div>

                  {!isBM && (
                    <div className="pt-4 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Attendance Sheet Details</p>
                      <p className="text-[11px] text-gray-400 mb-3">Classify each present member (Sec 103/112/113 all count toward quorum), add any proxies (Sec 105 — excluded from quorum), and record which directors/KMP/auditors attended.</p>

                      {(md.attendees || []).length > 0 && (
                        <div className="mb-3">
                          <p className="text-[11px] font-semibold text-gray-500 mb-1">Member Category</p>
                          <div className="space-y-1">
                            {(md.attendees || []).map(name => (
                              <div key={name} className="flex items-center justify-between gap-2 text-xs">
                                <span className="text-gray-700">{name}</span>
                                <select value={md.attendee_categories?.[name] || 'self'} onChange={e => setAttendeeCategory(meetingId, name, e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-[11px]">
                                  <option value="self">Self</option>
                                  <option value="corp_rep">Corporate Rep. (Sec 113)</option>
                                  <option value="nominee">President/Governor Nominee (Sec 112)</option>
                                </select>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mb-3">
                        <p className="text-[11px] font-semibold text-gray-500 mb-1">Proxies (Form MGT-11) — excluded from quorum</p>
                        {(md.proxies || []).length > 0 && (
                          <div className="space-y-1 mb-2">
                            {md.proxies.map(p => (
                              <div key={p.id} className="flex items-center justify-between gap-2 text-xs bg-gray-50 rounded-lg px-2 py-1">
                                <span>{p.name} — represents {p.represents_folio || 'folio ?'} ({p.shares || 0} shares)</span>
                                <button onClick={() => removeProxy(meetingId, p.id)} className="text-red-500 text-[11px] font-semibold">Remove</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="grid grid-cols-4 gap-1.5">
                          <input value={proxyDraft[meetingId]?.name || ''} onChange={e => updateProxyDraft(meetingId, { name: e.target.value })} placeholder="Proxy name" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs col-span-2" />
                          <input value={proxyDraft[meetingId]?.folio || ''} onChange={e => updateProxyDraft(meetingId, { folio: e.target.value })} placeholder="Represents Folio" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                          <input type="number" value={proxyDraft[meetingId]?.shares || ''} onChange={e => updateProxyDraft(meetingId, { shares: e.target.value })} placeholder="Shares" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                        </div>
                        <button onClick={() => addProxy(meetingId)} className="mt-1.5 text-[11px] font-semibold text-blue-600">+ Add Proxy</button>
                      </div>

                      <div className="mb-3">
                        <p className="text-[11px] font-semibold text-gray-500 mb-1">Directors / KMP Present</p>
                        <div className="flex flex-wrap gap-1.5">
                          {directors.map(d => (
                            <label key={d.id} className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg bg-gray-50">
                              <input type="checkbox" checked={(md.gm_directors_present || []).includes(d.director_name)} onChange={() => toggleGmDirectorPresent(meetingId, d.director_name)} />{d.director_name}{d.din ? ` (DIN: ${d.din})` : ''}
                            </label>
                          ))}
                          {directors.length === 0 && <p className="text-[11px] text-gray-400">No directors on record.</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <input value={md.cs_name || ''} onChange={e => updateMeeting(meetingId, { cs_name: e.target.value })} placeholder="Company Secretary name" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                        <input value={md.cs_membership_no || ''} onChange={e => updateMeeting(meetingId, { cs_membership_no: e.target.value })} placeholder="CS Membership No." className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                      </div>
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <input value={md.auditor_name || ''} onChange={e => updateMeeting(meetingId, { auditor_name: e.target.value })} placeholder="Statutory Auditor name" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                        <input value={md.auditor_firm_name || ''} onChange={e => updateMeeting(meetingId, { auditor_firm_name: e.target.value })} placeholder="Auditor firm name" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                        <input value={md.auditor_frn || ''} onChange={e => updateMeeting(meetingId, { auditor_frn: e.target.value })} placeholder="FRN" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                      </div>
                      <input value={md.scrutinizer_mem_no || ''} onChange={e => updateMeeting(meetingId, { scrutinizer_mem_no: e.target.value })} placeholder="Scrutinizer membership/CP no." className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                    </div>
                  )}

                  {isShort && gapDays >= 0 && (
                    <div className="pt-4 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Shorter Notice Consent <span className="font-normal text-amber-600">— required, only {gapDays} of {requiredDays} day{requiredDays !== 1 ? 's' : ''}{isBM ? '' : ' clear'} given</span></p>
                      <p className="text-[11px] text-gray-400 mb-2">{isBM ? 'Tick which directors consented.' : 'Tick which members consented — the document totals their shareholding against the 95% threshold.'}</p>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {consentRoster.map(name => (
                          <label key={name} className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg bg-gray-50">
                            <input type="checkbox" checked={consenters.includes(name)} onChange={() => toggleShorterConsent(meetingId, name)} />
                            {name}{isBM ? (() => { const din = directors.find(d => d.director_name === name)?.din; return din ? ` (DIN: ${din})` : ''; })() : ''}
                          </label>
                        ))}
                        {consentRoster.length === 0 && <p className="text-[11px] text-gray-400">No roster on record.</p>}
                      </div>
                      <button onClick={() => downloadShorterConsentDoc(meetingId, type)} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold">📄 Shorter Notice Consent (Word)</button>
                    </div>
                  )}

                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-600">Certified True Copy (CTC)</p>
                      {items.length > 1 && (
                        <button onClick={() => updateMeeting(meetingId, { ctc_selection: Object.fromEntries(items.map(i => [i.itemNo, !allSelected])) })} className="text-[11px] font-semibold text-blue-600">{allSelected ? 'Clear all' : 'Select all'}</button>
                      )}
                    </div>
                    {items.length > 1 && (
                      <div className="space-y-1 mb-3 border border-gray-100 rounded-lg divide-y divide-gray-50">
                        {items.map(i => (
                          <label key={i.itemNo} className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50">
                            <input type="checkbox" checked={!!selection[i.itemNo]} onChange={() => toggleCtcSelection(meetingId, i.itemNo)} />
                            <span className="font-semibold text-gray-400">#{i.itemNo}</span> {i.resolution.resolution_title}
                          </label>
                        ))}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 mb-2.5">
                      <input value={md.signatory_name || ''} onChange={e => updateMeeting(meetingId, { signatory_name: e.target.value })} placeholder="Signatory name (defaults to chairperson)" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                      <select value={md.signatory_role || 'Director'} onChange={e => updateMeeting(meetingId, { signatory_role: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500">
                        <option>Director</option><option>Managing Director</option><option>Whole-time Director</option>
                        {!isBM && <option>Chairperson of the Meeting</option>}
                        <option>Company Secretary</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2.5">
                      <input value={md.signatory_din_or_membership || ''} onChange={e => updateMeeting(meetingId, { signatory_din_or_membership: e.target.value })}
                        placeholder={(md.signatory_role || 'Director') === 'Company Secretary' ? 'Membership Number' : 'DIN'}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                      {/* Feeds "Place: __________" on the CTC/Minutes/Shorter
                          Consent sign-off lines below instead of leaving it
                          blank for the CS to fill by hand afterward. */}
                      <input value={md.signatory_place || ''} onChange={e => updateMeeting(meetingId, { signatory_place: e.target.value })}
                        placeholder="Place (defaults to registered office)"
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => downloadMeetingCtcDoc(meetingId, type, false)} className="px-3 py-2 rounded-lg bg-[#0f1f45] text-white text-xs font-semibold">📄 Full CTC — All Resolutions</button>
                      {items.length > 1 && <button onClick={() => downloadMeetingCtcDoc(meetingId, type, true)} disabled={selectedCount === 0} className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold disabled:opacity-40">📄 CTC — Selected ({selectedCount})</button>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'checklist' && (
        <div className="space-y-4">
          {CHECKLIST_GROUP_ORDER.map(group => {
            const items = checklist.filter(c => (c.group || 'Other') === group);
            if (items.length === 0) return null;
            return (
              <div key={group} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                  <p className="text-[11px] font-bold text-gray-500 uppercase">{group}</p>
                  {group === 'First Board Meeting of FY' && <p className="text-[11px] text-gray-400 mt-0.5">Internal disclosures kept on record — not filed with the ROC, and not tied to the AGM at all.</p>}
                </div>
                <div className="divide-y divide-gray-50">
                  {items.map(c => (
                    <div key={c.id} className="flex items-center gap-3 px-5 py-3">
                      <IconBadge accent={ACCENTS.slate}>{Icon.checklist}</IconBadge>
                      <p className="flex-1 text-sm text-[#0f1f45]">{c.task}</p>
                      <select className="border border-gray-200 rounded-lg px-2 py-1 text-xs" value={c.status} onChange={e => updateChecklistItem(c.id, { status: e.target.value })}>
                        {Object.keys(CHECKLIST_STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <input className="border border-gray-200 rounded-lg px-2 py-1 text-xs w-48" value={c.remarks || ''} onChange={e => updateChecklistItem(c.id, { remarks: e.target.value })} placeholder="Remarks" />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {checklist.length === 0 && <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-8 text-center text-xs text-gray-400">No attachment checklist for this case.</div>}
        </div>
      )}

      {activeTab === 'forms' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500 uppercase">
                <th className="px-4 py-3">Form</th>
                <th className="px-4 py-3">Basis</th>
                <th className="px-4 py-3">Due Date</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">SRN</th>
                <th className="px-4 py-3">Filing Date</th>
                <th className="px-4 py-3">Remarks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filings.map((f, i) => {
                const late = f.due_date && f.due_date < new Date().toISOString().slice(0, 10) && !['Filed', 'Approved'].includes(f.status);
                const accent = filingAccent(f.status);
                const def = FORM_DEFINITIONS.find(d => d.key === f.form);
                return (
                  <tr key={i} className={`border-l-4 ${accent.border}`}>
                    <td className="px-4 py-3 font-medium text-[#0f1f45]">{f.label || f.form}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${def?.basis === 'fy' ? 'bg-cyan-50 text-cyan-700' : 'bg-amber-50 text-amber-700'}`}>{def?.basis === 'fy' ? 'FY-based' : 'AGM-linked'}</span></td>
                    <td className={`px-4 py-3 ${late ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>{fmtDate(f.due_date)}</td>
                    <td className="px-4 py-3">
                      <select className="border border-gray-200 rounded-lg px-2 py-1 text-xs" value={f.status} onChange={e => updateFiling(i, { status: e.target.value })}>
                        {FILING_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        <option value="Not Applicable">Not Applicable</option>
                      </select>
                    </td>
                    <td className="px-4 py-3"><input className="border border-gray-200 rounded-lg px-2 py-1 text-xs w-28" value={f.srn || ''} onChange={e => updateFiling(i, { srn: e.target.value })} /></td>
                    <td className="px-4 py-3"><input type="date" className="border border-gray-200 rounded-lg px-2 py-1 text-xs" value={f.filing_date || ''} onChange={e => updateFiling(i, { filing_date: e.target.value })} /></td>
                    <td className="px-4 py-3"><input className="border border-gray-200 rounded-lg px-2 py-1 text-xs w-40" value={f.rejection_reason || ''} onChange={e => updateFiling(i, { rejection_reason: e.target.value })} placeholder="Remarks / rejection reason" /></td>
                  </tr>
                );
              })}
              {filings.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-xs text-gray-400">No forms selected for this case.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// TOP-LEVEL — list + router into case detail
// ═══════════════════════════════════════════════════════════════════════
export default function AnnualFilings({ initialCaseId, onNavigate, entityScope }) {
  const llpScoped = entityScope === 'LLP';
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [alert, setAlert] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const list = await fetchJson('/api/mca/annual-filings' + (llpScoped ? '?entity_regime=LLP' : ''));
      setCases(list);
      if (initialCaseId) {
        const found = list.find(c => String(c.id) === String(initialCaseId));
        if (found) setSelected(found);
      }
    } catch (e) {
      setAlert({ variant: 'error', title: 'Failed to load', message: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doDelete = async (id) => {
    try {
      await fetchJson(`/api/mca/annual-filings/${id}`, { method: 'DELETE' });
      setCases(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      setAlert({ variant: 'error', title: 'Delete failed', message: e.message });
    }
  };

  if (selected) {
    return (
      <div>
        <style>{DARK_STYLE_OVERRIDES}</style>
        <AnnualFilingCaseDetail item={selected} onBack={() => { setSelected(null); load(); }} onNavigate={onNavigate} />
      </div>
    );
  }

  const withStatus = cases.map(c => ({ ...c, _status: computeCaseStatus(c.filings || [], c.synced_at), _overdue: isOverdue(c.filings || []) }));
  const stats = STAGE_ORDER.reduce((acc, s) => { acc[s] = withStatus.filter(c => c._status === s).length; return acc; }, {});
  const overdueCount = withStatus.filter(c => c._overdue).length;
  const filtered = statusFilter === 'all' ? withStatus : withStatus.filter(c => c._status === statusFilter);

  return (
    <div>
      <style>{DARK_STYLE_OVERRIDES}</style>
      {alert && <AlertModal {...alert} onClose={() => setAlert(null)} />}
      {confirmDeleteId && (
        <ConfirmModal title="Delete Annual Filing?" message="This will permanently remove this case."
          confirmLabel="Delete" onConfirm={() => { const id = confirmDeleteId; setConfirmDeleteId(null); doDelete(id); }} onCancel={() => setConfirmDeleteId(null)} />
      )}
      {showWizard && (
        <NewAnnualFilingWizard entityScope={entityScope} onClose={() => setShowWizard(false)} onCreated={(created) => { setShowWizard(false); load(); setSelected(created); }} />
      )}

      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="font-bold text-lg text-[#0f1f45]">{llpScoped ? 'LLP Annual Filing' : 'Annual Filings'}</p>
          <p className="text-xs text-gray-400">{llpScoped
            ? 'Form 11 (Annual Return, due 30 May) & Form 8 (Statement of Account & Solvency, due 30 October) — LLP Act, 2008. No AGM. Accounts audit if turnover > ₹40 L or contribution > ₹25 L (Rule 24(8)).'
            : 'AOC-4, MGT-7/7A, ADT-1, DPT-3, MSME-1 — company-wise, financial-year-wise compliance tracking, with the pre-AGM Board Meeting and AGM both covered.'}</p>
        </div>
        <button onClick={() => setShowWizard(true)} className="px-4 py-2.5 rounded-lg text-xs font-semibold text-white bg-[#0f1f45]">{llpScoped ? '+ New LLP Filing' : '+ New Annual Filing'}</button>
      </div>

      <div className="grid grid-cols-6 gap-3 mb-5">
        {STAGE_ORDER.map(s => (
          <button key={s} onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
            className={`bg-white rounded-xl border p-3 text-left ${statusFilter === s ? 'border-[#0f1f45]' : 'border-gray-100'}`}>
            <p className="text-[11px] font-semibold text-gray-400">{s}</p>
            <p className="text-xl font-bold text-[#0f1f45]">{stats[s] || 0}</p>
          </button>
        ))}
        <button onClick={() => setStatusFilter(statusFilter === 'overdue' ? 'all' : 'overdue')}
          className={`bg-white rounded-xl border p-3 text-left ${statusFilter === 'overdue' ? 'border-red-400' : 'border-gray-100'}`}>
          <p className="text-[11px] font-semibold text-red-500">Overdue</p>
          <p className="text-xl font-bold text-red-600">{overdueCount}</p>
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500 uppercase">
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Financial Year</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading && <tr><td colSpan={4} className="px-4 py-8 text-center text-xs text-gray-400">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-xs text-gray-400">No annual filing cases yet — click "+ New Annual Filing" to start one.</td></tr>}
            {(statusFilter === 'overdue' ? filtered.filter(c => c._overdue) : filtered).map(c => {
              const accent = filingAccent(c._status);
              return (
                <tr key={c.id} onClick={() => setSelected(c)} className={`cursor-pointer hover:bg-gray-50/60 border-l-4 ${accent.border}`}>
                  <td className="px-4 py-3 font-medium text-[#0f1f45]">{c.company_name}</td>
                  <td className="px-4 py-3 text-gray-500">FY {c.financial_year}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${STATUS_COLORS[c._status] || STATUS_COLORS.Draft}`}>{c._status}</span>
                    {c._overdue && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">Overdue</span>}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <KebabMenu actions={[
                      { label: 'View', icon: Icon.eye, onClick: () => setSelected(c) },
                      { label: 'Delete', icon: Icon.trash, danger: true, onClick: () => setConfirmDeleteId(c.id) },
                    ]} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
